/**
 * 素材图层数据管理工具函数
 * 提供创建和管理素材图层数据结构的工具函数
 * 数据结构定义参考：doc/editPaster/data.md
 */
import * as constant from './constant.js';
import slog from './slog.js';
import * as mwx from './common.js';
import { Cache } from './Cache.js';
import * as canvasUtil from './canvasUtil.js';
import {
  convertGrayToAlphaForBackground,
  mirrorImageHorizontal
} from './canvasUtil.js';
import { shouldSkipBackgroundGrayToAlpha } from './sku/featureConfig.js';
import { imageWithWhiteInkDefaultOpacity } from './config.js';
import {
  Control,
  controlsUtils,
  Image as FabricImage,
  loadSVGFromURL,
  Point,
  StaticCanvas,
  Pattern,
  Rect,
  util
} from 'fabric';
import iconMoveUrl from '@/assets/image/icon_rotate.png';
import iconDeleteUrl from '@/assets/image/sticker/icon_delete.png';
import iconCopyUrl from '@/assets/image/sticker/icon_copy.png';
import iconScaleUrl from '@/assets/image/sticker/icon_scale.png';
import {
  exportFinalWhiteInk,
  getImageDataRenderKey,
  hasLayerImageSource,
  parseMaterialImageData,
  resolveImageDataRenderResult,
  resolveImageUrlFromImageData,
  resolveLayerImageUrl,
  shouldHideMaterial
} from './layerUtil/index.js';
import {
  clearImageDataRenderUrlCache,
  imageDataRenderUrlCache
} from './editImage/imageDataRenderCache.js';
import { getImageDataLayerRenderSize } from './editImage/imageDataRenderSizeUtil.js';
import {
  setActiveObjectPinchGestureEnabled,
  enableActiveObjectPinchGesture,
  disableActiveObjectPinchGesture
} from './enableActiveObjectPinchGesture.js';
import { convertAcrylicToComponentData } from './serviceComponentHole/presetConverter.js';

export {
  clearImageDataRenderUrlCache,
  imageDataRenderUrlCache,
  getImageDataRenderKey,
  hasLayerImageSource,
  parseMaterialImageData,
  resolveImageDataRenderResult,
  resolveImageUrlFromImageData,
  resolveLayerImageUrl,
  exportFinalWhiteInk,
  shouldHideMaterial,
  setActiveObjectPinchGestureEnabled,
  enableActiveObjectPinchGesture,
  disableActiveObjectPinchGesture
};

const backgroundMaxZIndex = constant.backgroundMaxZIndex;
const maxLayerZIndex = constant.maxLayerZIndex;
const emptyWhiteInk = constant.emptyWhiteInk;
// 透明网格底图：固定放到最底层
const transparentGridZIndex = constant.transparentGridZIndex;
const transparentGridPatternSize = 400;
const maskClipMaxDpr = 2;
const maskClipMaxPixels = 2000000;
let skuTemplateMaskClipBlobUrlCache = null;

// 创建图片缓存实例，最多缓存10张图片
const imageCache = new Cache(10);

// 透明 PNG 的 blobUrl（按尺寸缓存复用）
const transparentBlobUrlBySizeCache = new Cache(20);
const transparentBlobUrlPromiseBySize = {};

/**
 * 将 Blob 转成 blobUrl。
 * @param {Blob} blob - 需要转换的 Blob。
 * @returns {string} 生成的 blobUrl。
 */
function createObjectUrlFromBlob(blob) {
  // 当前环境缺少 URL.createObjectURL 时无法生成可复用透明图地址。
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    throw new Error('getTransparentBlobUrlBySize: URL.createObjectURL 不可用');
  }
  return URL.createObjectURL(blob);
}

/**
 * 用同尺寸 canvas 的 dataURL 生成透明 PNG Blob。
 * @param {HTMLCanvasElement} canvas - 已按目标尺寸创建的 canvas。
 * @returns {Blob} 透明 PNG Blob。
 */
function createTransparentBlobFromCanvasDataUrl(canvas) {
  const dataUrl = canvas.toDataURL('image/png');
  // 浏览器返回空 dataURL 时不能继续生成空 Blob。
  if (!dataUrl || dataUrl === 'data:,') {
    throw new Error('getTransparentBlobUrlBySize: dataURL 为空');
  }
  const parts = dataUrl.split(',');
  // dataURL 缺少 base64 内容时说明导出结果不可用。
  if (!parts || parts.length < 2 || !parts[1]) {
    throw new Error('getTransparentBlobUrlBySize: dataURL 格式异常');
  }
  const header = parts[0];
  const rawBase64 = parts[1];
  const match = header.match(/:(.*?);/);
  const contentType = match && match[1] ? match[1] : 'image/png';
  const byteCharacters = atob(rawBase64);
  const byteNumbers = new Array(byteCharacters.length)
    .fill(null)
    .map((_, i) => byteCharacters.charCodeAt(i));
  const byteArray = new Uint8Array(byteNumbers);
  return new Blob([byteArray], { type: contentType });
}

/**
 * 获取文字 imageData 图层声明的画布尺寸。
 * @param {Object} layer - 待读取的图层。
 * @returns {{width:number,height:number}|null} imageData 声明的有效尺寸。
 */
export function getTextImageDataCanvasSize(layer) {
  // 非文字 imageData 图层不需要改写图层基础宽高。
  if (!layer || layer.canvasType !== 'textbox' || !layer.imageData) {
    return null;
  }
  try {
    const renderSize = getImageDataLayerRenderSize(layer.imageData);
    // imageData 尺寸无效时保持原 Fabric 提取结果。
    if (!renderSize) {
      return null;
    }
    return {
      width: renderSize.width,
      height: renderSize.height
    };
  } catch (error) {
    console.error('getTextImageDataCanvasSize: imageData 解析失败', error);
    return null;
  }
}

/**
 * 判断字段是否为 imageData 运行时渲染缓存字段。
 * @param {string} key - 字段名。
 * @returns {boolean} 是否为运行时字段。
 */
function isImageDataRenderRuntimeField(key) {
  return key === 'imageDataRenderBlobUrl' || key === 'imageDataRenderKey';
}

/**
 * 释放图层上正式保存的 imageData 渲染 blobUrl。
 * @param {Object} layer - 图层对象。
 * @returns {void}
 */
export function revokeLayerImageDataRenderBlobUrl(layer) {
  if (!layer || !layer.imageDataRenderBlobUrl) {
    return;
  }
  const renderBlobUrl = layer.imageDataRenderBlobUrl;
  const renderKey = layer.imageDataRenderKey;
  if (renderKey && imageDataRenderUrlCache.get(renderKey) === renderBlobUrl) {
    imageDataRenderUrlCache.delete(renderKey);
    return;
  }
  mwx.revokeBlobUrl(renderBlobUrl);
}

/**
 * 递归克隆数据并移除 imageData 渲染运行时字段。
 * @param {*} value - 需要处理的数据。
 * @returns {*} 移除运行时字段后的新数据。
 */
export function removeImageDataRenderRuntimeFields(value) {
  if (Array.isArray(value)) {
    return value.map((item) => removeImageDataRenderRuntimeFields(item));
  }
  if (!value || typeof value !== 'object') {
    return value;
  }
  return Object.keys(value).reduce((result, key) => {
    // 运行时 blobUrl/key 不进入后端，也不在跨页面返回数据中保留。
    if (isImageDataRenderRuntimeField(key)) {
      return result;
    }
    result[key] = removeImageDataRenderRuntimeFields(value[key]);
    return result;
  }, {});
}

/**
 * 功能说明：释放已从画布摘除的 Fabric 对象，帮助浏览器回收图片和内部缓存。
 * @param {Object} fabricObj - 需要释放的 Fabric 对象
 * @returns {void}
 */
export function disposeFabricObject(fabricObj) {
  // 空对象无需释放。
  if (!fabricObj) {
    return;
  }
  try {
    // Fabric 对象支持 dispose 时优先调用其释放逻辑。
    if (typeof fabricObj.dispose === 'function') {
      fabricObj.dispose();
    }
  } catch (error) {
    console.error('disposeFabricObject: 释放 Fabric 对象失败', error);
  }
}

/**
 * 功能说明：清空 Fabric 画布 clipPath，并释放旧裁剪对象。
 * @param {Object} fabricCanvas - Fabric 画布实例
 * @returns {void}
 */
export function clearFabricCanvasClipPath(fabricCanvas) {
  // 没有画布实例时无需处理。
  if (!fabricCanvas) {
    return;
  }
  const oldClipPath = fabricCanvas.clipPath;
  fabricCanvas.clipPath = null;
  disposeFabricObject(oldClipPath);
}

/**
 * 功能说明：计算 mask 裁剪用 DPR，限制高 DPR 或大画布下的像素峰值。
 * @param {number} canvasWidth - 画布逻辑宽度
 * @param {number} canvasHeight - 画布逻辑高度
 * @returns {number} 受限后的 mask DPR
 */
function getMaskClipDpr(canvasWidth, canvasHeight) {
  const rawDpr =
    typeof window !== 'undefined' && window.devicePixelRatio
      ? window.devicePixelRatio
      : 1;
  let dpr = Math.min(rawDpr, maskClipMaxDpr);
  const pixelCount = canvasWidth * canvasHeight * dpr * dpr;
  // 像素量超过上限时，按面积比例降低 DPR。
  if (pixelCount > maskClipMaxPixels) {
    dpr = Math.sqrt(maskClipMaxPixels / (canvasWidth * canvasHeight));
  }
  return dpr;
}

/**
 * 功能说明：生成 skuTemplate mask 裁剪结果缓存 key。
 * @param {Object} options - 参数对象
 * @param {string} options.maskUrl - mask 图片地址
 * @param {number} options.canvasWidth - 画布逻辑宽度
 * @param {number} options.canvasHeight - 画布逻辑高度
 * @param {number} options.dpr - mask 处理 DPR
 * @param {number} options.threshold - 黑色亮度阈值
 * @returns {string} 缓存 key
 */
function createSkuTemplateMaskClipCacheKey(options) {
  const opts = options || {};
  return JSON.stringify({
    maskUrl: opts.maskUrl,
    canvasWidth: opts.canvasWidth,
    canvasHeight: opts.canvasHeight,
    dpr: opts.dpr,
    threshold: opts.threshold
  });
}

/**
 * 功能说明：释放 skuTemplate mask 裁剪结果单槽缓存。
 * @returns {void}
 */
function revokeSkuTemplateMaskClipBlobUrlCache() {
  // 缓存不存在时无需释放。
  if (!skuTemplateMaskClipBlobUrlCache) {
    return;
  }
  const blobUrl = skuTemplateMaskClipBlobUrlCache.blobUrl;
  skuTemplateMaskClipBlobUrlCache = null;
  mwx.revokeBlobUrl(blobUrl);
}

/**
 * 功能说明：读取并校验 skuTemplate mask 裁剪结果缓存。
 * @param {string} cacheKey - 当前裁剪参数生成的缓存 key
 * @returns {Promise<string>} 有效缓存 blobUrl，无有效缓存时返回空字符串
 */
async function getValidSkuTemplateMaskClipBlobUrlCache(cacheKey) {
  // 缓存 key 不一致时不能复用裁剪结果。
  if (
    !skuTemplateMaskClipBlobUrlCache ||
    skuTemplateMaskClipBlobUrlCache.key !== cacheKey ||
    !skuTemplateMaskClipBlobUrlCache.blobUrl
  ) {
    return '';
  }

  try {
    await canvasUtil.loadImage(skuTemplateMaskClipBlobUrlCache.blobUrl);
    console.log(
      'applySkuTemplateMaskClipPath: 命中 maskDataUrl 缓存，cacheKey 相同则当前 maskDataUrl 内容可复用',
      {
        cacheKey
      }
    );
    return skuTemplateMaskClipBlobUrlCache.blobUrl;
  } catch (error) {
    console.warn(
      'applySkuTemplateMaskClipPath: maskDataUrl 缓存 blobUrl 已失效，重新生成',
      error
    );
    revokeSkuTemplateMaskClipBlobUrlCache();
    return '';
  }
}

/**
 * 功能说明：写入 skuTemplate mask 裁剪结果单槽缓存。
 * @param {string} cacheKey - 当前裁剪参数生成的缓存 key
 * @param {string} blobUrl - 处理后的 mask blobUrl
 * @returns {void}
 */
function setSkuTemplateMaskClipBlobUrlCache(cacheKey, blobUrl) {
  const oldBlobUrl =
    skuTemplateMaskClipBlobUrlCache && skuTemplateMaskClipBlobUrlCache.blobUrl;
  // 单槽缓存写入新 URL 时，释放不再用于后续命中的旧 URL。
  if (oldBlobUrl && oldBlobUrl !== blobUrl) {
    mwx.revokeBlobUrl(oldBlobUrl);
  }
  skuTemplateMaskClipBlobUrlCache = {
    key: cacheKey,
    blobUrl: blobUrl
  };
}

/**
 * 创建离屏 StaticCanvas（用于导出/合成中间结果，避免污染主画布 UI）
 * @param {Object} options
 * @param {number} options.width
 * @param {number} options.height
 * @returns {fabric.StaticCanvas}
 */
function createOffscreenStaticCanvas(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('createOffscreenStaticCanvas: width/height 无效');
  }

  if (typeof document === 'undefined') {
    throw new Error('createOffscreenStaticCanvas: document 不存在');
  }

  const canvasEl = document.createElement('canvas');
  canvasEl.width = width;
  canvasEl.height = height;

  const offscreenCanvas = new StaticCanvas(canvasEl, {
    width: width,
    height: height
  });

  // 避免 add/remove 时频繁重绘；最终由调用方 toDataURL / renderAll 控制
  offscreenCanvas.renderOnAddRemove = false;

  return offscreenCanvas;
}

/**
 * 获取指定尺寸的全透明 PNG 的 blobUrl（生成并缓存）
 * - 用于“清空模板”时设置 templateLayer.imageUrl
 * - 缓存 key：`${width}x${height}`
 * - 注意：不 revoke，避免后续引用失效（生命周期跟随页面）
 * @param {Object} options
 * @param {number} options.width
 * @param {number} options.height
 * @returns {Promise<string>}
 */
export async function getTransparentBlobUrlBySize(options) {
  const opts = options || {};
  const width = typeof opts.width === 'number' ? opts.width : 0;
  const height = typeof opts.height === 'number' ? opts.height : 0;
  if (!(width > 0 && height > 0)) {
    throw new Error('getTransparentBlobUrlBySize: width/height 无效');
  }

  const cacheKey = `${width}x${height}`;
  const cached = transparentBlobUrlBySizeCache.get(cacheKey);
  if (cached) {
    return cached;
  }

  if (transparentBlobUrlPromiseBySize[cacheKey]) {
    return await transparentBlobUrlPromiseBySize[cacheKey];
  }

  transparentBlobUrlPromiseBySize[cacheKey] = new Promise((resolve, reject) => {
    try {
      if (typeof document === 'undefined') {
        reject(new Error('getTransparentBlobUrlBySize: document 不存在'));
        return;
      }

      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, width, height);
      }

      if (typeof canvas.toBlob === 'function') {
        canvas.toBlob(
          (blob) => {
            try {
              if (!blob) {
                mwx.log.error('getTransparentBlobUrlBySize retry dataURL', {
                  scene: 'transparent-placeholder-export',
                  attempt: 'toDataURL-after-empty-toBlob',
                  width,
                  height,
                  reason: 'empty_toBlob'
                });
                const fallbackBlob = createTransparentBlobFromCanvasDataUrl(
                  canvas
                );
                const fallbackBlobUrl = createObjectUrlFromBlob(fallbackBlob);
                transparentBlobUrlBySizeCache.set(cacheKey, fallbackBlobUrl);
                resolve(fallbackBlobUrl);
                return;
              }
              const blobUrl = createObjectUrlFromBlob(blob);
              transparentBlobUrlBySizeCache.set(cacheKey, blobUrl);
              resolve(blobUrl);
            } catch (error) {
              reject(error);
            }
          },
          'image/png',
          1
        );
        return;
      }

      // 兼容：没有 toBlob 时，退化为 dataURL -> Blob
      const blob = createTransparentBlobFromCanvasDataUrl(canvas);
      const blobUrl = createObjectUrlFromBlob(blob);
      transparentBlobUrlBySizeCache.set(cacheKey, blobUrl);
      resolve(blobUrl);
    } catch (error) {
      reject(error);
    }
  }).finally(() => {
    delete transparentBlobUrlPromiseBySize[cacheKey];
  });

  return await transparentBlobUrlPromiseBySize[cacheKey];
}

/**
 * 兼容旧调用：1x1 透明 blobUrl
 * @returns {Promise<string>}
 */
export async function getTransparent1x1BlobUrl() {
  return await getTransparentBlobUrlBySize({ width: 1, height: 1 });
}

const noWhiteLayerType = [
  constant.layer_background,
  constant.layer_transparentGrid,
  constant.layer_stampingTemplate,
  constant.layer_stampingMaterial
];

/**
 * 判断是否为“透明网格底图”图层
 * @param {Object} layer - layer 对象
 * @returns {boolean} 是否为透明网格图层
 */
function isTransparentGridLayer(layer) {
  return layer && layer.type === constant.layer_transparentGrid;
}

/**
 * 判断是否为烫色模板图层。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否为烫色模板图层。
 */
export function isStampingTemplateLayer(layer) {
  return !!(layer && layer.type === constant.layer_stampingTemplate);
}

/**
 * 判断是否为烫色素材图层。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否为烫色素材图层。
 */
export function isStampingMaterialLayer(layer) {
  return !!(layer && layer.type === constant.layer_stampingMaterial);
}

/**
 * 判断是否为烫色专用图层。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否为烫色专用图层。
 */
export function isStampingLayer(layer) {
  return isStampingTemplateLayer(layer) || isStampingMaterialLayer(layer);
}

/**
 * 判断是否为上传烫色派生出的覆盖渲染层。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否为上传烫色覆盖渲染层。
 */
function isUploadStampingOverlayRenderLayer(layer) {
  return !!(
    layer &&
    layer.type === constant.layer_userImage &&
    layer.foilUrl &&
    layer.stampingEffectImageUrl &&
    layer.imageUrl === layer.stampingEffectImageUrl
  );
}

/**
 * 判断是否为白墨合成阶段需要后置覆盖的烫色渲染层。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否为烫色覆盖渲染层。
 */
function isStampingOverlayRenderLayer(layer) {
  return !!(
    isStampingLayer(layer) || isUploadStampingOverlayRenderLayer(layer)
  );
}

/**
 * 判断图层是否允许进入普通设计页通用绘制链路。
 * @param {Object} layer - 待判断的 layer 对象。
 * @param {Object} [options] - 配置项。
 * @param {boolean} [options.includeStampingLayers=false] - 是否显式允许烫色图层。
 * @returns {boolean} 是否允许绘制。
 */
export function shouldRenderLayerInCommonDesign(layer, options = {}) {
  // 空图层不允许进入通用绘制链路。
  if (!layer) {
    return false;
  }
  // 烫色图层默认只在烫色编辑页专用入口绘制。
  if (isStampingLayer(layer) && options.includeStampingLayers !== true) {
    return false;
  }
  return true;
}

/**
 * 判断图层是否允许进入后端提交转换。
 * @param {Object} layer - 待判断的 layer 对象。
 * @returns {boolean} 是否允许提交转换。
 */
export function shouldIncludeLayerInBackendPayload(layer) {
  // 空图层不允许进入提交转换。
  if (!layer) {
    return false;
  }
  return true;
}

/**
 * 获取透明网格底图资源信息
 * - imageUrl 使用 require 让 webpack 正确处理静态资源
 * - width/height 为透明网格图片的像素尺寸（用于计算缩放）
 *
 * @returns {{imageUrl: string, width: number, height: number}} 透明网格图片信息
 */
function getTransparentGridImageInfo() {
  // 使用 require 保持与现有代码一致（webpack 会处理静态资源）
  return {
    imageUrl: require('@/assets/image/transparent.png'),
    width: 376, // 图片的宽度
    height: 376
  };
}

/**
 * 创建透明网格底图 layer（仅用于编辑 UI 展示，不参与导出合成）
 * - zIndex 固定为最底层（transparentGridZIndex）
 * - layerInfo 仅用于标记资源信息，实际平铺尺寸在 Pattern 绘制时统一控制
 *
 * @param {Object} canvasInfo - 画布信息（至少包含 displayWidth/displayHeight）
 * @param {number} canvasInfo.displayWidth - 显示宽度
 * @param {number} canvasInfo.displayHeight - 显示高度
 * @returns {Object} 透明网格 layer 对象
 */
export function createTransparentGridLayer(canvasInfo) {
  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error('createTransparentGridLayer: canvasInfo 参数不完整');
  }

  const transparentGridImageInfo = getTransparentGridImageInfo();
  let scale = canvasInfo.displayWidth / transparentGridImageInfo.width;

  return createLayer({
    type: constant.layer_transparentGrid,
    imageUrl: transparentGridImageInfo.imageUrl,
    layerInfo: createLayerInfo({
      left: 0,
      top: 0,
      width: transparentGridImageInfo.width,
      height: transparentGridImageInfo.height,
      scaleX: scale,
      scaleY: scale,
      angle: 0,
      originX: 'left',
      originY: 'top'
    }),
    opacity: 1,
    canEditImage: false,
    canAdjustLayer: false,
    zIndex: transparentGridZIndex
  });
}

/**
 * 确保 layers 中包含“透明网格底图”layer
 * - 若已存在则原样返回
 * - 若不存在则在数组最前插入（最终仍按 zIndex 排序渲染）
 * - 当 canvasInfo 不完整时不注入，避免影响调用方
 *
 * @param {Array} layers - layer 数组
 * @param {Object} canvasInfo - 画布信息（至少包含 displayWidth/displayHeight）
 * @returns {Array} 可能被注入过的 layer 数组
 */
function ensureTransparentGridLayer(layers, canvasInfo) {
  if (!layers || !Array.isArray(layers)) {
    return layers;
  }
  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    return layers;
  }
  const existed = layers.some((l) => isTransparentGridLayer(l));
  if (existed) {
    return layers;
  }
  const gridLayer = createTransparentGridLayer(canvasInfo);
  return [gridLayer].concat(layers);
}

/**
 * 在 Fabric canvas 中加载“透明网格底图”layer（Pattern + Rect 平铺）
 * - 图片先缩放到统一贴图尺寸，再做 pattern 平铺
 * - 设置 excludeFromExport=true，确保 Fabric 导出不带入网格
 *
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Object} options.layer - 透明网格 layer 对象
 * @param {number} [options.scale=1] - 缩放比例（displayWidth -> 当前 canvas 尺寸）
 * @returns {Promise<fabric.Rect>} 返回 Rect 对象（不在此处添加到 canvas，由调用方统一 add/render）
 */
async function loadTransparentGridLayerToFabricCanvas(options) {
  const {
    fabricCanvas,
    layer,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight
  } = options || {};

  if (!fabricCanvas) {
    throw new Error(
      'loadTransparentGridLayerToFabricCanvas: fabricCanvas 参数不能为空'
    );
  }
  if (!layer || !layer.imageUrl) {
    throw new Error('loadTransparentGridLayerToFabricCanvas: layer 参数无效');
  }

  // 读取透明度（从 layer.opacity 读取，默认为 1）
  const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;

  // 获取透明网格需要覆盖的宽高；默认覆盖整个 canvas
  const canvasWidth =
    typeof renderWidth === 'number' && renderWidth > 0
      ? renderWidth
      : typeof fabricCanvas.getWidth === 'function'
      ? fabricCanvas.getWidth()
      : fabricCanvas.width;
  const canvasHeight =
    typeof renderHeight === 'number' && renderHeight > 0
      ? renderHeight
      : typeof fabricCanvas.getHeight === 'function'
      ? fabricCanvas.getHeight()
      : fabricCanvas.height;

  if (
    typeof canvasWidth !== 'number' ||
    canvasWidth <= 0 ||
    typeof canvasHeight !== 'number' ||
    canvasHeight <= 0
  ) {
    throw new Error(
      'loadTransparentGridLayerToFabricCanvas: 无法获取有效的 canvas 尺寸'
    );
  }

  const patternImg = await FabricImage.fromURL(layer.imageUrl, {
    crossOrigin: 'anonymous'
  });
  const imgElement = patternImg.getElement();
  let patternSource = imgElement;
  if (typeof document !== 'undefined') {
    const patternCanvas = document.createElement('canvas');
    patternCanvas.width = transparentGridPatternSize;
    patternCanvas.height = transparentGridPatternSize;
    const patternCtx = patternCanvas.getContext('2d');
    if (patternCtx) {
      patternCtx.drawImage(
        imgElement,
        0,
        0,
        transparentGridPatternSize,
        transparentGridPatternSize
      );
      patternSource = patternCanvas;
    }
  }
  const pattern = new Pattern({
    source: patternSource,
    repeat: 'repeat'
  });

  const rect = new Rect({
    left: offsetX,
    top: offsetY,
    width: canvasWidth,
    height: canvasHeight,
    fill: pattern,
    opacity: opacity,
    selectable: false,
    hasControls: false,
    hasBorders: false,
    evented: false,
    lockMovementX: true,
    lockMovementY: true,
    lockRotation: true,
    lockScalingX: true,
    lockScalingY: true,
    excludeFromExport: true
  });

  // 存储 layerId
  rect.set('layerId', layer.id);
  rect.setCoords();
  return rect;
}

// ==============================
// layerInfo 图片尺寸校验（开发辅助，非阻塞）
// ==============================
// 是否开启 layerInfo.width/height 与 imageUrl 对应图片真实宽高一致性的校验
// - 默认：开发环境 mwx.isDev 时开启
// - 可覆盖：window.__enableLayerInfoImageSizeValidate__ = true/false
const ENABLE_LAYERINFO_IMAGE_SIZE_VALIDATE = function () {
  return process.env.NODE_ENV === 'development';
};

// 用于去重错误日志，避免拖拽/频繁更新时刷屏
const layerInfoImageSizeValidateLogCache = new Cache(200);

function validateLayerInfoImageSizeNonBlocking(layer, source) {
  try {
    if (!ENABLE_LAYERINFO_IMAGE_SIZE_VALIDATE()) {
      return;
    }

    if (!layer || typeof layer !== 'object') {
      return;
    }

    if ([constant.layer_transparentGrid].indexOf(layer.type) !== -1) {
      return;
    }

    // 文字图层不使用原始 imageUrl 宽高校验 layerInfo，避免还原图与原图尺寸差异误报。
    if (layer.canvasType === 'textbox') {
      return;
    }

    const imageUrl = layer.imageUrl;
    if (!imageUrl || typeof imageUrl !== 'string') {
      return;
    }

    const layerInfo = layer.layerInfo;
    const layerId = layer.id || '';
    const layerType = layer.type || '';
    const src = source || '';

    if (!layerInfo || typeof layerInfo !== 'object') {
      const key = `layerInfoImageSizeValidate:missingLayerInfo:${layerId}:${layerType}:${imageUrl}:${src}`;
      if (!layerInfoImageSizeValidateLogCache.get(key)) {
        layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
        console.error('[layerInfoImageSizeValidate] layerInfo 缺失', {
          id: layerId,
          type: layerType,
          imageUrl: imageUrl,
          source: src
        });
      }
      return;
    }

    const w = layerInfo.width;
    const h = layerInfo.height;

    if (typeof w !== 'number' || typeof h !== 'number' || w <= 0 || h <= 0) {
      const key = `layerInfoImageSizeValidate:invalidLayerInfoWH:${layerId}:${layerType}:${imageUrl}:${src}`;
      if (!layerInfoImageSizeValidateLogCache.get(key)) {
        layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
        console.error(
          '[layerInfoImageSizeValidate] layerInfo.width/height 非法',
          {
            id: layerId,
            type: layerType,
            imageUrl: imageUrl,
            layerInfoWidth: w,
            layerInfoHeight: h,
            source: src
          }
        );
      }
      return;
    }

    // 异步校验，不阻塞主流程
    (async function () {
      try {
        const img = await mwx.getImageInfo(imageUrl);
        const imgW =
          img && typeof img.naturalWidth === 'number' && img.naturalWidth > 0
            ? img.naturalWidth
            : img && typeof img.width === 'number'
            ? img.width
            : 0;
        const imgH =
          img && typeof img.naturalHeight === 'number' && img.naturalHeight > 0
            ? img.naturalHeight
            : img && typeof img.height === 'number'
            ? img.height
            : 0;

        let isSkip = imgW == 1 && imgH == 1; // 清空背景时会生成1*1px的图片作为占位图(getTransparent1x1BlobUrl)
        if (isSkip) {
          return;
        }

        if (!(imgW > 0 && imgH > 0)) {
          const key = `layerInfoImageSizeValidate:invalidImageWH:${layerId}:${layerType}:${imageUrl}:${src}`;
          if (!layerInfoImageSizeValidateLogCache.get(key)) {
            layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
            console.error('[layerInfoImageSizeValidate] 获取图片宽高失败', {
              id: layerId,
              type: layerType,
              imageUrl: imageUrl,
              imageWidth: imgW,
              imageHeight: imgH,
              source: src
            });
          }
          return;
        }

        if (w !== imgW || h !== imgH) {
          const key = `layerInfoImageSizeValidate:mismatch:${layerId}:${layerType}:${imageUrl}:${w}x${h}:${imgW}x${imgH}:${src}`;
          if (!layerInfoImageSizeValidateLogCache.get(key)) {
            layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
            console.error(
              '[layerInfoImageSizeValidate] layerInfo.width/height 与 imageUrl 图片真实宽高不一致',
              {
                id: layerId,
                type: layerType,
                imageUrl: imageUrl,
                layerInfoWidth: w,
                layerInfoHeight: h,
                imageWidth: imgW,
                imageHeight: imgH,
                source: src
              }
            );
          }
        }
      } catch (error) {
        const key = `layerInfoImageSizeValidate:error:${layerId}:${layerType}:${imageUrl}:${src}`;
        if (!layerInfoImageSizeValidateLogCache.get(key)) {
          layerInfoImageSizeValidateLogCache.set(key, true, 30 * 1000);
          console.error('[layerInfoImageSizeValidate] 校验异常', {
            id: layerId,
            type: layerType,
            imageUrl: imageUrl,
            source: src,
            error: error
          });
        }
      }
    })();
  } catch (e) {
    // ignore
  }
}

// ==============================
// Fabric 控件定制（editImageNew 用）
// ==============================
const EDIT_IMAGE_CONTROL_COLOR = '#E60012';
const EDIT_IMAGE_BORDER_DASH_ARRAY = [6, 6];
const EDIT_IMAGE_BORDER_SCALE_FACTOR = 2;
// 缩放角点（tl/bl/br）矩形大小（px）
const EDIT_IMAGE_SCALE_CORNER_SIZE = 6;
// 旋转图标大小（px）
const EDIT_IMAGE_ROTATE_ICON_SIZE = 36; // 参考 0.2rem（1rem=100px）
// 旋转图标默认所在角（可选：tl / tr / bl / br）
let editImageRotateCorner = 'tr';

function normalizeEditImageRotateCorner(corner) {
  const c = typeof corner === 'string' ? corner.toLowerCase() : '';
  if (c === 'tl' || c === 'tr' || c === 'bl' || c === 'br') {
    return c;
  }
  return 'tr';
}

function getEditImageCornerXY(corner) {
  const c = normalizeEditImageRotateCorner(corner);
  if (c === 'tl') {
    return { x: -0.5, y: -0.5 };
  }
  if (c === 'tr') {
    return { x: 0.5, y: -0.5 };
  }
  if (c === 'bl') {
    return { x: -0.5, y: 0.5 };
  }
  return { x: 0.5, y: 0.5 };
}

/**
 * 设置 editImageNew 的旋转图标所在角（默认 tr）
 * @param {'tl'|'tr'|'bl'|'br'} corner
 */
export function setEditImageRotateCorner(corner) {
  editImageRotateCorner = normalizeEditImageRotateCorner(corner);
}

let cachedMoveIconImage = null;
let cachedMoveIconBinded = false;

function ensureMoveIconImageForCanvas(fabricObject) {
  // 在非浏览器环境（如 node）避免报错
  if (typeof Image === 'undefined') {
    return null;
  }

  if (!cachedMoveIconImage) {
    cachedMoveIconImage = new Image();
    cachedMoveIconImage.src = iconMoveUrl;
  }

  // 绑定一次 onload，加载完后触发一次重绘（尽量保证旋转图标能及时出现）
  if (!cachedMoveIconBinded) {
    cachedMoveIconBinded = true;
    cachedMoveIconImage.onload = function () {
      try {
        if (
          fabricObject &&
          fabricObject.canvas &&
          typeof fabricObject.canvas.requestRenderAll === 'function'
        ) {
          fabricObject.canvas.requestRenderAll();
        } else if (
          fabricObject &&
          fabricObject.canvas &&
          typeof fabricObject.canvas.renderAll === 'function'
        ) {
          fabricObject.canvas.renderAll();
        }
      } catch (e) {
        // ignore
      }
    };
  }

  return cachedMoveIconImage;
}

function renderMoveIconControl(ctx, left, top, styleOverride, fabricObject) {
  const img = ensureMoveIconImageForCanvas(fabricObject);
  if (!img || !img.complete) {
    return;
  }
  // 旋转图标大小独立于缩放角点大小（cornerSize）
  const size = EDIT_IMAGE_ROTATE_ICON_SIZE;
  const x = left - size / 2;
  const y = top - size / 2;

  ctx.save();
  try {
    ctx.drawImage(img, x, y, size, size);
  } catch (e) {
    // ignore
  }
  ctx.restore();
}

/**
 * editImageNew：仅保留四角控件（tl/bl/br 缩放，tr 旋转），并设置红色虚线外框
 * 注意：仅对可编辑对象使用（selectable/hasControls=true）
 * @param {fabric.Object} fabricObject - Fabric 对象
 * @param {'tl'|'tr'|'bl'|'br'} [rotateCorner] - 旋转图标所在角（不传则使用全局默认）
 */
export function applyEditImageCornerControls(fabricObject, rotateCorner) {
  if (!fabricObject) {
    return;
  }

  // 1) 外框样式（红色虚线）
  try {
    fabricObject.set({
      borderColor: EDIT_IMAGE_CONTROL_COLOR,
      borderDashArray: EDIT_IMAGE_BORDER_DASH_ARRAY,
      borderScaleFactor: EDIT_IMAGE_BORDER_SCALE_FACTOR,
      cornerColor: EDIT_IMAGE_CONTROL_COLOR,
      cornerStrokeColor: EDIT_IMAGE_CONTROL_COLOR,
      cornerSize: EDIT_IMAGE_SCALE_CORNER_SIZE,
      transparentCorners: false
    });
  } catch (e) {
    // ignore
  }

  // 2) 仅保留四角（隐藏边中点 + mtr）
  if (typeof fabricObject.setControlsVisibility === 'function') {
    fabricObject.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      mtr: false,
      tl: true,
      tr: true,
      bl: true,
      br: true
    });
  }

  // 3) 指定角替换为旋转控件（默认右上角 tr；使用内置旋转 handler，render 用 icon_move）
  if (fabricObject.controls) {
    const corner = normalizeEditImageRotateCorner(
      rotateCorner ? rotateCorner : editImageRotateCorner
    );
    const cornerXY = getEditImageCornerXY(corner);

    fabricObject.controls[corner] = new Control({
      x: cornerXY.x,
      y: cornerXY.y,
      // 旋转控件的交互区域大小（与图标大小一致）
      sizeX: EDIT_IMAGE_ROTATE_ICON_SIZE,
      sizeY: EDIT_IMAGE_ROTATE_ICON_SIZE,
      // 使用内置旋转逻辑（带吸附的版本），与 editImageNew 的 object:modified 90°吸附兼容
      actionHandler: controlsUtils.rotationWithSnapping,
      cursorStyleHandler: controlsUtils.rotationStyleHandler,
      actionName: 'rotate',
      render: renderMoveIconControl
    });
  }
}

// ==============================
// Fabric 自定义图层控件（edit 页面用）
// ==============================
const LAYER_CONTROL_ICON_SIZE = 28;
const LAYER_CONTROL_HIT_SIZE = 30;
const LAYER_CONTROL_COLOR = '#E60012';
const LAYER_CONTROL_BORDER_DASH_ARRAY = [6, 6];
const LAYER_CONTROL_BORDER_SCALE_FACTOR = 2;
const LAYER_CONTROL_SCALE_CORNER_SIZE = 6;
const LAYER_EDIT_TEXT_CONTROL_WIDTH = 76;
const LAYER_EDIT_TEXT_CONTROL_HEIGHT = 28;
const LAYER_EDIT_TEXT_CONTROL_RADIUS = 8;
const LAYER_EDIT_TEXT_CONTROL_BG = '#FF6D8C';

function getLayerControlPoint(fabricObject, corner) {
  if (!fabricObject) return new Point(0, 0);
  const width = fabricObject.width || 0;
  const height = fabricObject.height || 0;
  const scaleX = fabricObject.scaleX || 1;
  const scaleY = fabricObject.scaleY || 1;
  const paddingX =
    fabricObject._subjectControlPaddingX ||
    fabricObject._subjectControlPadding ||
    0;
  const paddingY =
    fabricObject._subjectControlPaddingY ||
    fabricObject._subjectControlPadding ||
    0;
  const padX = paddingX / scaleX;
  const padY = paddingY / scaleY;
  const left = -padX;
  const top = -padY;
  const right = width + padX;
  const bottom = height + padY;
  let localX = left;
  let localY = top;
  if (corner === 'tr') {
    localX = right;
    localY = top;
  } else if (corner === 'br') {
    localX = right;
    localY = bottom;
  } else if (corner === 'bl') {
    localX = left;
    localY = bottom;
  }
  const angle = ((fabricObject.angle || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const center = fabricObject.getCenterPoint();
  const px = (localX - width / 2) * scaleX;
  const py = (localY - height / 2) * scaleY;
  return new Point(
    px * cos - py * sin + center.x,
    px * sin + py * cos + center.y
  );
}

function createLayerControlPositionHandler(corner) {
  return function layerControlPositionHandler(dim, finalMatrix, fabricObject) {
    return getLayerControlPoint(fabricObject, corner);
  };
}

/**
 * 绘制图层自定义操作框红色虚线边框
 * @param {CanvasRenderingContext2D} ctx - Fabric 控件渲染上下文
 * @param {fabric.Object} fabricObject - 当前选中的 Fabric 图层对象
 * @returns {void}
 */
function renderLayerControlBorder(ctx, fabricObject) {
  if (!ctx || !fabricObject) return;
  const tl = getLayerControlPoint(fabricObject, 'tl');
  const tr = getLayerControlPoint(fabricObject, 'tr');
  const br = getLayerControlPoint(fabricObject, 'br');
  const bl = getLayerControlPoint(fabricObject, 'bl');
  ctx.save();
  try {
    ctx.strokeStyle = LAYER_CONTROL_COLOR;
    ctx.lineWidth = LAYER_CONTROL_BORDER_SCALE_FACTOR;
    ctx.setLineDash(LAYER_CONTROL_BORDER_DASH_ARRAY);
    ctx.beginPath();
    ctx.moveTo(tl.x, tl.y);
    ctx.lineTo(tr.x, tr.y);
    ctx.lineTo(br.x, br.y);
    ctx.lineTo(bl.x, bl.y);
    ctx.closePath();
    ctx.stroke();
  } catch (e) {
    /* ignore */
  }
  ctx.restore();
}

/**
 * 包装 Fabric 对象的控件绘制流程，让虚线框先于所有操作 icon 绘制。
 * @param {fabric.Object} fabricObject - 当前选中的 Fabric 图层对象
 * @returns {void}
 */
function applyLayerControlDrawOrder(fabricObject) {
  // 对象不存在、drawControls 不可用或已包装时，不重复处理。
  if (
    !fabricObject ||
    typeof fabricObject.drawControls !== 'function' ||
    fabricObject.__layerControlDrawOrderApplied
  ) {
    return;
  }

  const originalDrawControls = fabricObject.drawControls;
  fabricObject.__layerControlDrawOrderApplied = true;
  fabricObject.drawControls = function drawControlsWithLayerBorder(
    ctx,
    styleOverride
  ) {
    ctx.save();
    try {
      const retinaScaling =
        typeof this.getCanvasRetinaScaling === 'function'
          ? this.getCanvasRetinaScaling()
          : 1;
      ctx.setTransform(retinaScaling, 0, 0, retinaScaling, 0, 0);
      renderLayerControlBorder(ctx, this);
    } catch (e) {
      /* ignore */
    }
    ctx.restore();

    return originalDrawControls.call(this, ctx, styleOverride);
  };
}

// 缓存图标 Image 对象
let cachedDeleteIcon = null;
let cachedDeleteIconBinded = false;
let cachedCopyIcon = null;
let cachedCopyIconBinded = false;
let cachedScaleIcon = null;
let cachedScaleIconBinded = false;
let cachedRotateIcon = null;
let cachedRotateIconBinded = false;

function ensureIconImage(iconUrl, cachedRef, bindFlagRef, fabricObject) {
  if (typeof Image === 'undefined') return null;
  if (!cachedRef) {
    cachedRef = new Image();
    cachedRef.src = iconUrl;
  }
  if (!bindFlagRef) {
    bindFlagRef = true;
    cachedRef.onload = function () {
      try {
        if (fabricObject && fabricObject.canvas) {
          if (typeof fabricObject.canvas.requestRenderAll === 'function') {
            fabricObject.canvas.requestRenderAll();
          } else if (typeof fabricObject.canvas.renderAll === 'function') {
            fabricObject.canvas.renderAll();
          }
        }
      } catch (e) {
        /* ignore */
      }
    };
  }
  return cachedRef;
}

function createIconRenderer(iconUrl, getCached, setCached) {
  return function renderIconControl(
    ctx,
    left,
    top,
    styleOverride,
    fabricObject
  ) {
    let img = getCached();
    if (!img) {
      img = new Image();
      img.src = iconUrl;
      setCached(img);
      img.onload = function () {
        try {
          if (fabricObject && fabricObject.canvas) {
            if (typeof fabricObject.canvas.requestRenderAll === 'function') {
              fabricObject.canvas.requestRenderAll();
            } else if (typeof fabricObject.canvas.renderAll === 'function') {
              fabricObject.canvas.renderAll();
            }
          }
        } catch (e) {
          /* ignore */
        }
      };
    }
    if (!img.complete) return;
    const size = LAYER_CONTROL_ICON_SIZE;
    const x = left - size / 2;
    const y = top - size / 2;
    ctx.save();
    try {
      ctx.drawImage(img, x, y, size, size);
    } catch (e) {
      /* ignore */
    }
    ctx.restore();
  };
}

const renderDeleteControl = createIconRenderer(
  iconDeleteUrl,
  () => cachedDeleteIcon,
  (img) => {
    cachedDeleteIcon = img;
  }
);

const renderCopyControl = createIconRenderer(
  iconCopyUrl,
  () => cachedCopyIcon,
  (img) => {
    cachedCopyIcon = img;
  }
);

const renderScaleControl = createIconRenderer(
  iconScaleUrl,
  () => cachedScaleIcon,
  (img) => {
    cachedScaleIcon = img;
  }
);

const renderRotateControl = createIconRenderer(
  iconMoveUrl,
  () => cachedRotateIcon,
  (img) => {
    cachedRotateIcon = img;
  }
);

/**
 * 绘制文字素材编辑按钮。
 * @param {CanvasRenderingContext2D} ctx - Fabric 控件渲染上下文。
 * @param {number} left - 控件中心点 X 坐标。
 * @param {number} top - 控件中心点 Y 坐标。
 * @returns {void}
 */
function renderEditTextControl(ctx, left, top) {
  const width = LAYER_EDIT_TEXT_CONTROL_WIDTH;
  const height = LAYER_EDIT_TEXT_CONTROL_HEIGHT;
  const radius = LAYER_EDIT_TEXT_CONTROL_RADIUS;
  const x = left - width / 2;
  const y = top - height / 2;
  ctx.save();
  try {
    ctx.fillStyle = LAYER_EDIT_TEXT_CONTROL_BG;
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.font = '14px sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('编辑文字', left, top);
  } catch (e) {
    /* ignore */
  }
  ctx.restore();
}

const LAYER_ROTATE_SNAP_RANGE = constant.SNAP_ANGLE_CORRECTION_RANGE;
const LAYER_ROTATE_SNAP_INTERVAL = 90;

function snapLayerControlAngle(angle) {
  if (typeof angle !== 'number') return angle;
  const nearest =
    Math.round(angle / LAYER_ROTATE_SNAP_INTERVAL) * LAYER_ROTATE_SNAP_INTERVAL;
  return Math.abs(angle - nearest) <= LAYER_ROTATE_SNAP_RANGE // 小于6度旋转吸附
    ? nearest
    : angle;
}

/**
 * 计算图层覆盖画布后的最大等比缩放值。
 * @param {Object} options - 计算参数。
 * @param {number} options.targetWidth - 图层原始宽度。
 * @param {number} options.targetHeight - 图层原始高度。
 * @param {number} options.canvasWidth - 画布宽度。
 * @param {number} options.canvasHeight - 画布高度。
 * @param {number} [options.angle=0] - 图层旋转角度。
 * @param {number} [options.multiplier=2] - 覆盖画布后的放大倍数。
 * @returns {number} 最大等比缩放值，参数无效时返回 0。
 */
export function calcCoverCanvasMaxScale(options = {}) {
  const opts = options || {};
  const targetWidth = Number(opts.targetWidth) || 0;
  const targetHeight = Number(opts.targetHeight) || 0;
  const canvasWidth = Number(opts.canvasWidth) || 0;
  const canvasHeight = Number(opts.canvasHeight) || 0;
  const angle = Number(opts.angle) || 0;
  const multiplier = Number(opts.multiplier) || 2;

  // 参数不合法时不返回缩放上限，调用方保持原有计算结果。
  if (
    !(targetWidth > 0) ||
    !(targetHeight > 0) ||
    !(canvasWidth > 0) ||
    !(canvasHeight > 0) ||
    !(multiplier > 0)
  ) {
    return 0;
  }

  const angleRad = (angle * Math.PI) / 180;
  const absCos = Math.abs(Math.cos(angleRad));
  const absSin = Math.abs(Math.sin(angleRad));
  const unitAabbWidth = targetWidth * absCos + targetHeight * absSin;
  const unitAabbHeight = targetWidth * absSin + targetHeight * absCos;

  // 单位缩放包围盒无效时不返回缩放上限。
  if (!(unitAabbWidth > 0) || !(unitAabbHeight > 0)) {
    return 0;
  }

  return (
    Math.max(canvasWidth / unitAabbWidth, canvasHeight / unitAabbHeight) *
    multiplier
  );
}

/**
 * 右下角组合 handler：等比缩放 + 旋转
 * 拖拽右下角时，以对象中心为锚点同时缩放和旋转
 * - 旋转：鼠标相对中心的角度变化
 * - 缩放：鼠标相对中心的距离变化
 * - 最大尺寸：按旋转后的实际渲染包围盒限制，不能超出画布可见区域
 * - 最小尺寸：最长物理边不得小于 15mm（通过纸张宽高和画布像素换算）
 * @param {Object} options - 缩放限制参数
 * @param {number} options.paperHeight - 纸张物理高度，单位 mm
 * @param {number} options.paperWidth - 纸张物理宽度，单位 mm
 * @param {number} options.canvansWidth - 纸张宽度对应的画布像素值
 * @param {number} options.canvansHeight - 纸张高度对应的画布像素值
 * @param {number} [options.minLongSideMm=15] - 最小长边物理尺寸（mm）
 * @param {number} [options.minCanvasWidthRatio=0] - 最小宽度占画布宽度比例。
 * @param {number} [options.strokeWidth=0] - 当前图层描边宽度（mm）
 * @returns {Function} Fabric 控件 actionHandler
 */
const scalingAndRotatingHandler = (options = {}) => {
  function scaleAndRotateObject(eventData, transform, x, y) {
    const target = transform.target;
    const { ex, ey, theta } = transform;

    // 保存变换前的中心点（旋转/缩放都围绕中心进行）
    const center = target.getCenterPoint();

    // --- 等比缩放计算：鼠标相对中心的距离变化 ---
    const lastDist = Math.sqrt((ex - center.x) ** 2 + (ey - center.y) ** 2);
    const curDist = Math.sqrt((x - center.x) ** 2 + (y - center.y) ** 2);
    const ratio = curDist / Math.max(lastDist, 0.001);
    const { original } = transform;
    let newScaleX = (original.scaleX || 1) * ratio;
    let newScaleY = (original.scaleY || 1) * ratio;

    const paperWidth = Number(options.paperWidth) || 0;
    const paperHeight = Number(options.paperHeight) || 0;
    const canvansWidth = Number(options.canvansWidth) || 0;
    const canvansHeight = Number(options.canvansHeight) || 0;
    const minLongSideMm = Number(options.minLongSideMm) || 15;
    const ignoreStrokeInScaleLimit = options.ignoreStrokeInScaleLimit === true;
    const strokeWidthMm = ignoreStrokeInScaleLimit
      ? 0
      : Number(options.strokeWidth) || 0;
    const effectiveMinLongSideMm = Math.max(1, minLongSideMm - strokeWidthMm);
    const minCanvasLongSideRatio = Number(options.minCanvasLongSideRatio) || 0;
    const minCanvasWidthRatio = Number(options.minCanvasWidthRatio) || 0;
    const targetWidth = target.width || 0;
    const targetHeight = target.height || 0;

    // --- 旋转计算：鼠标相对中心的角度变化 ---
    const lastAngle = Math.atan2(ey - center.y, ex - center.x);
    const curAngle = Math.atan2(y - center.y, x - center.x);
    let newAngle = (curAngle - lastAngle + theta) * (180 / Math.PI);
    if (newAngle < 0) newAngle = 360 + newAngle;
    newAngle %= 360;
    newAngle = snapLayerControlAngle(newAngle);

    /**
     * 功能：按旋转后的实际渲染包围盒约束最大缩放，保证不超出画布区域
     * @returns {void}
     */
    const clampScaleByRenderedAabb = () => {
      // 缩放阶段显示的是不带描边图：
      // 先在物理尺寸上扣掉双边描边，再换算成像素作为可显示最大尺寸
      // 例：102mm 纸张，描边 2mm -> 最大内容 98mm -> 98/102*canvasPx
      const maxCanvasWidth =
        paperWidth > 0
          ? (Math.max(paperWidth - strokeWidthMm * 2, 1) / paperWidth) *
            canvansWidth
          : canvansWidth;
      const maxCanvasHeight =
        paperHeight > 0
          ? (Math.max(paperHeight - strokeWidthMm * 2, 1) / paperHeight) *
            canvansHeight
          : canvansHeight;

      const maxScale = calcCoverCanvasMaxScale({
        targetWidth,
        targetHeight,
        canvasWidth: maxCanvasWidth,
        canvasHeight: maxCanvasHeight,
        angle: newAngle,
        multiplier: 2
      });

      // 缩放上限无效时保持当前计算结果。
      if (!(maxScale > 0)) return;

      const nextMaxScale = Math.max(newScaleX, newScaleY);
      const clampRatio = Math.min(maxScale / Math.max(nextMaxScale, 0.001), 1);
      newScaleX *= clampRatio;
      newScaleY *= clampRatio;
    };
    clampScaleByRenderedAabb();

    const calcLongSideMmByScale = (scaleX, scaleY) => {
      if (
        !(paperWidth > 0) ||
        !(paperHeight > 0) ||
        !(canvansWidth > 0) ||
        !(canvansHeight > 0)
      ) {
        return 0;
      }
      const widthMm = ((targetWidth * scaleX) / canvansWidth) * paperWidth;
      const heightMm = ((targetHeight * scaleY) / canvansHeight) * paperHeight;
      return Math.max(widthMm, heightMm);
    };
    /**
     * 计算指定缩放下图层的像素长边。
     * @param {number} scaleX - 横向缩放比例。
     * @param {number} scaleY - 纵向缩放比例。
     * @returns {number} 像素长边。
     */
    const calcLongSidePxByScale = (scaleX, scaleY) => {
      const widthPx = targetWidth * scaleX;
      const heightPx = targetHeight * scaleY;
      return Math.max(widthPx, heightPx);
    };

    // 素材图层按画布宽度比例限制最小宽度，不使用物理尺寸和描边扣减。
    if (minCanvasWidthRatio > 0 && canvansWidth > 0) {
      const minWidthPx = canvansWidth * minCanvasWidthRatio;
      const nextWidthPx = targetWidth * newScaleX;
      const currentWidthPx = targetWidth * (target.scaleX || 1);
      // 仅在继续缩小时才钳制，允许当前已小于下限的素材被放大恢复。
      if (nextWidthPx < minWidthPx && nextWidthPx < currentWidthPx) {
        const clampRatio = minWidthPx / Math.max(nextWidthPx, 0.001);
        newScaleX *= clampRatio;
        newScaleY *= clampRatio;
      }
    } else if (
      minCanvasLongSideRatio > 0 &&
      canvansWidth > 0 &&
      canvansHeight > 0
    ) {
      const minLongSidePx =
        Math.max(canvansWidth, canvansHeight) * minCanvasLongSideRatio;
      const nextLongSidePx = calcLongSidePxByScale(newScaleX, newScaleY);
      const currentLongSidePx = calcLongSidePxByScale(
        target.scaleX || 1,
        target.scaleY || 1
      );
      if (
        nextLongSidePx < minLongSidePx &&
        nextLongSidePx < currentLongSidePx
      ) {
        const clampRatio = minLongSidePx / Math.max(nextLongSidePx, 0.001);
        newScaleX *= clampRatio;
        newScaleY *= clampRatio;
      }
    } else if (
      paperWidth > 0 &&
      paperHeight > 0 &&
      canvansWidth > 0 &&
      canvansHeight > 0
    ) {
      const nextLongSideMm = calcLongSideMmByScale(newScaleX, newScaleY);
      const currentLongSideMm = calcLongSideMmByScale(
        target.scaleX || 1,
        target.scaleY || 1
      );
      if (
        nextLongSideMm < effectiveMinLongSideMm &&
        nextLongSideMm < currentLongSideMm
      ) {
        const clampRatio =
          effectiveMinLongSideMm / Math.max(nextLongSideMm, 0.001);
        newScaleX *= clampRatio;
        newScaleY *= clampRatio;
      }
    }

    // 如果最小尺寸兜底后又超过最大尺寸，以渲染包围盒约束为最终结果
    clampScaleByRenderedAabb();
    // 素材图层最终兜底使用画布宽度比例最小值，避免最大尺寸约束后跌破下限。
    if (minCanvasWidthRatio > 0 && canvansWidth > 0) {
      const minWidthPx = canvansWidth * minCanvasWidthRatio;
      const finalWidthPx = targetWidth * newScaleX;
      // 最大尺寸约束后若宽度跌破素材下限，按最小宽度再次兜底。
      if (finalWidthPx > 0 && finalWidthPx < minWidthPx) {
        const finalMinRatio = minWidthPx / Math.max(finalWidthPx, 0.001);
        newScaleX *= finalMinRatio;
        newScaleY *= finalMinRatio;
      }
    } else if (
      minCanvasLongSideRatio > 0 &&
      canvansWidth > 0 &&
      canvansHeight > 0
    ) {
      const minLongSidePx =
        Math.max(canvansWidth, canvansHeight) * minCanvasLongSideRatio;
      const finalLongSidePx = calcLongSidePxByScale(newScaleX, newScaleY);
      if (finalLongSidePx > 0 && finalLongSidePx < minLongSidePx) {
        const finalMinRatio = minLongSidePx / Math.max(finalLongSidePx, 0.001);
        newScaleX *= finalMinRatio;
        newScaleY *= finalMinRatio;
      }
    } else {
      const finalLongSideMm = calcLongSideMmByScale(newScaleX, newScaleY);
      if (finalLongSideMm > 0 && finalLongSideMm < effectiveMinLongSideMm) {
        const finalMinRatio =
          effectiveMinLongSideMm / Math.max(finalLongSideMm, 0.001);
        newScaleX *= finalMinRatio;
        newScaleY *= finalMinRatio;
      }
    }

    // 应用缩放和旋转
    target.angle = newAngle;
    target.scaleX = newScaleX;
    target.scaleY = newScaleY;
    target.setPositionByOrigin(center, 'center', 'center');

    return true;
  }

  return controlsUtils.wrapWithFireEvent('modifying', scaleAndRotateObject);
};

/**
 * edit 页面：自定义图层控件
 * - 左上角(tl)：删除
 * - 右上角(tr)：复制
 * - 右下角(br)：等比缩放 + 旋转（单指拖动同时缩放和旋转）
 * - 隐藏其他默认控件（边中点、旋转）
 * @param {fabric.Object} fabricObject - Fabric 对象
 * @param {Object} callbacks - 图层控件回调和缩放限制参数
 * @param {Object} [options] - 控件显示配置
 * @param {boolean} [options.showDeleteControl=true] - 是否显示左上角删除控件
 * @param {boolean} [options.showCopyControl=true] - 是否显示右上角复制控件
 * @param {boolean} [options.showScaleControl=true] - 是否显示右下角缩放控件
 * @param {boolean} [options.showEditTextControl=false] - 是否显示文字编辑控件
 */
export function applyLayerCustomControls(fabricObject, callbacks, options) {
  if (!fabricObject) return;
  applyLayerControlDrawOrder(fabricObject);
  const controlCallbacks = callbacks || {};
  const controlOptions = options || {};
  const showDeleteControl = controlOptions.showDeleteControl !== false;
  const showCopyControl = controlOptions.showCopyControl !== false;
  const showScaleControl = controlOptions.showScaleControl !== false;
  const showEditTextControl = controlOptions.showEditTextControl === true;
  const hasCopyCallback = typeof controlCallbacks.onLayerCopy === 'function';
  const hasEditTextCallback =
    typeof controlCallbacks.onLayerEditText === 'function';

  // 1) 外框样式（红色虚线）
  try {
    fabricObject.set({
      borderColor: LAYER_CONTROL_COLOR,
      borderDashArray: LAYER_CONTROL_BORDER_DASH_ARRAY,
      borderScaleFactor: LAYER_CONTROL_BORDER_SCALE_FACTOR,
      hasBorders: false,
      cornerColor: LAYER_CONTROL_COLOR,
      cornerStrokeColor: LAYER_CONTROL_COLOR,
      cornerSize: LAYER_CONTROL_SCALE_CORNER_SIZE,
      transparentCorners: false
    });
  } catch (e) {
    /* ignore */
  }

  // 2) 隐藏边中点 + 旋转控件
  if (typeof fabricObject.setControlsVisibility === 'function') {
    fabricObject.setControlsVisibility({
      mt: false,
      mb: false,
      ml: false,
      mr: false,
      mtr: false,
      tl: showDeleteControl,
      tr: showCopyControl && hasCopyCallback,
      bl: false,
      br: showScaleControl,
      editText: showEditTextControl && hasEditTextCallback
    });
  }

  // 3) 替换控件
  if (fabricObject.controls) {
    // 仅需要删除入口的图层才挂载左上角删除控件。
    if (showDeleteControl) {
      // 左上角 → 删除
      fabricObject.controls.tl = new Control({
        x: -0.5,
        y: -0.5,
        sizeX: LAYER_CONTROL_HIT_SIZE,
        sizeY: LAYER_CONTROL_HIT_SIZE,
        touchSizeX: LAYER_CONTROL_HIT_SIZE,
        touchSizeY: LAYER_CONTROL_HIT_SIZE,
        cursorStyle: 'pointer',
        positionHandler: createLayerControlPositionHandler('tl'),
        mouseDownHandler: (a, b) => {
          // console.log('删除图层事件对象', b);
          const id = b.target.layerId;
          // 删除回调存在时才通知业务页处理图层数据。
          if (typeof controlCallbacks.onLayerDelete === 'function') {
            controlCallbacks.onLayerDelete(id);
          }
        },
        render: renderDeleteControl
      });
    }

    // 复制回调存在时才显示右上角复制控件。
    if (showCopyControl && hasCopyCallback) {
      fabricObject.controls.tr = new Control({
        x: 0.5,
        y: -0.5,
        sizeX: LAYER_CONTROL_ICON_SIZE,
        sizeY: LAYER_CONTROL_ICON_SIZE,
        cursorStyle: 'pointer',
        positionHandler: createLayerControlPositionHandler('tr'),
        mouseDownHandler: (a, b) => {
          // console.log('复制图层事件对象', b);
          const target = b && b.target;
          const id = target && target.layerId;
          controlCallbacks.onLayerCopy(id, target);
        },
        render: renderCopyControl
      });
    } else {
      fabricObject.setControlsVisibility({
        tr: false
      });
    }

    // 文字素材才显示顶部编辑文字入口。
    if (showEditTextControl && hasEditTextCallback) {
      fabricObject.controls.editText = new Control({
        x: 0,
        y: -0.5,
        offsetY: -26,
        sizeX: LAYER_EDIT_TEXT_CONTROL_WIDTH,
        sizeY: LAYER_EDIT_TEXT_CONTROL_HEIGHT,
        touchSizeX: LAYER_EDIT_TEXT_CONTROL_WIDTH,
        touchSizeY: LAYER_EDIT_TEXT_CONTROL_HEIGHT,
        cursorStyle: 'pointer',
        mouseDownHandler: (a, b) => {
          const target = b && b.target;
          const id = target && target.layerId;
          controlCallbacks.onLayerEditText(id, target);
        },
        render: renderEditTextControl
      });
    }

    // 右下角 → 等比缩放 + 旋转（组合 handler）
    if (showScaleControl) {
      fabricObject.controls.br = new Control({
        x: 0.5,
        y: 0.5,
        sizeX: LAYER_CONTROL_HIT_SIZE,
        sizeY: LAYER_CONTROL_HIT_SIZE,
        touchSizeX: LAYER_CONTROL_HIT_SIZE,
        touchSizeY: LAYER_CONTROL_HIT_SIZE,
        cursorStyle: 'se-resize',
        actionHandler: scalingAndRotatingHandler({
          paperHeight: controlCallbacks.paperHeight,
          paperWidth: controlCallbacks.paperWidth,
          canvansWidth: controlCallbacks.canvansWidth,
          canvansHeight: controlCallbacks.canvansHeight,
          minLongSideMm: controlCallbacks.minLongSideMm,
          strokeWidth: Number(fabricObject.strokeWidth) || 0,
          ignoreStrokeInScaleLimit: controlCallbacks.ignoreStrokeInScaleLimit,
          minCanvasLongSideRatio: controlCallbacks.minCanvasLongSideRatio,
          minCanvasWidthRatio: controlCallbacks.minCanvasWidthRatio
        }),
        actionName: 'scale',
        positionHandler: createLayerControlPositionHandler('br'),
        render: renderScaleControl
      });
    } else {
      fabricObject.setControlsVisibility({
        br: false
      });
    }
  }
}

/**
 * 生成图层唯一ID
 * @returns {string} 格式为 layer_${timestamp}_${random} 的唯一ID
 */
export function generateLayerId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `layer_${timestamp}_${random}`;
}

/**
 * 创建画布信息
 * @param {Object} options - 画布配置选项
 * @param {number} options.mmWidth - 物理宽度（单位：mm）
 * @param {number} options.mmHeight - 物理高度（单位：mm）
 * @param {number} [options.dpi=300] - 分辨率，默认300
 * @param {number} [options.displayWidth] - 显示像素宽度（单位：px），用于预览画布
 * @param {number} [options.displayHeight] - 显示像素高度（单位：px），用于预览画布
 * @returns {Object} 完整的 canvas 对象
 */
export function initCanvasInfo(options) {
  if (!options) {
    throw new Error('initCanvasInfo: options 参数不能为空');
  }

  const { mmWidth, mmHeight, dpi = 300, displayWidth, displayHeight } = options;

  if (typeof mmWidth !== 'number' || mmWidth <= 0) {
    throw new Error('initCanvasInfo: mmWidth 必须是大于0的数字');
  }

  if (typeof mmHeight !== 'number' || mmHeight <= 0) {
    throw new Error('initCanvasInfo: mmHeight 必须是大于0的数字');
  }

  // 计算生产像素尺寸：mmWidth * dpi / 25.4
  const productionWidth = Math.round((mmWidth * dpi) / 25.4);
  const productionHeight = Math.round((mmHeight * dpi) / 25.4);

  // 如果没有指定显示尺寸，使用生产尺寸
  const finalDisplayWidth =
    typeof displayWidth === 'number' && displayWidth > 0
      ? displayWidth
      : productionWidth;
  const finalDisplayHeight =
    typeof displayHeight === 'number' && displayHeight > 0
      ? displayHeight
      : productionHeight;

  return {
    mmWidth,
    mmHeight,
    dpi,
    productionWidth,
    productionHeight,
    displayWidth: finalDisplayWidth,
    displayHeight: finalDisplayHeight
  };
}

/**
 * 创建图层画布信息（Fabric.js 格式）
 * @param {Object} options - 图层画布信息配置选项
 * @param {number} [options.left=0] - 图层在画布中的 left 坐标（单位：px），对齐 Fabric.js
 * @param {number} [options.top=0] - 图层在画布中的 top 坐标（单位：px），对齐 Fabric.js
 * @param {number} options.width - 图层原始宽度（单位：px），与 Fabric.js 对齐
 * @param {number} options.height - 图层原始高度（单位：px），与 Fabric.js 对齐
 * @param {number} [options.angle=0] - 图层在画布中的旋转角度（单位：度），与 Fabric.js 对齐
 * @param {number} [options.scaleX] - X 轴缩放比例，对齐 Fabric.js
 * @param {number} [options.scaleY] - Y 轴缩放比例，对齐 Fabric.js
 * @param {string} [options.originX='left'] - X 轴原点，对齐 Fabric.js
 * @param {string} [options.originY='top'] - Y 轴原点，对齐 Fabric.js
 * @returns {Object} 完整的 layerInfo 对象
 */
export function createLayerInfo(options) {
  if (!options) {
    throw new Error('createLayerInfo: options 参数不能为空');
  }

  const {
    left = 0,
    top = 0,
    width,
    height,
    angle = 0,
    scaleX,
    scaleY,
    originX = 'left',
    originY = 'top'
  } = options;

  if (typeof width !== 'number' || width <= 0) {
    throw new Error('createLayerInfo: width 必须是大于0的数字');
  }

  if (typeof height !== 'number' || height <= 0) {
    throw new Error('createLayerInfo: height 必须是大于0的数字');
  }

  // 如果没有提供 scaleX/scaleY，默认为 1
  const finalScaleX = typeof scaleX === 'number' && scaleX > 0 ? scaleX : 1;
  const finalScaleY = typeof scaleY === 'number' && scaleY > 0 ? scaleY : 1;

  return {
    left,
    top,
    width,
    height,
    scaleX: finalScaleX,
    scaleY: finalScaleY,
    angle,
    originX,
    originY
  };
}

/**
 * 批量设置字段（仅复制已定义字段）
 * @param {Object} target - 目标对象
 * @param {Object} source - 源对象
 * @param {Array} fields - 需要复制的字段名列表
 */
function assignDefinedFields(target, source, fields) {
  if (!target || !source || !Array.isArray(fields)) {
    return;
  }
  for (let i = 0; i < fields.length; i++) {
    const field = fields[i];
    if (source[field] !== undefined) {
      target[field] = source[field];
    }
  }
}

/**
 * 创建图层对象
 * @param {Object} options - 图层配置选项
 * @param {string} options.type - 图层类型：userImage、skuTemplate、skuMaterial、background
 * @param {string} options.imageUrl - 图片地址
 * @param {Object} options.layerInfo - 图层在画布中的位置、大小和变换信息
 * @param {boolean} [options.canEditImage] - 是否可以进行图片编辑操作，根据类型自动设置
 * @param {boolean} [options.canAdjustLayer] - 是否可以调整图层上下层级，根据类型自动设置
 * @param {string} [options.id] - 图层唯一标识，如果不提供则自动生成
 * @param {number} [options.zIndex] - 图层层级，如果不提供则自动计算
 * @param {Object} [options.typeSpecificFields] - 类型特有字段
 * @returns {Object} 完整的 layer 对象
 */
export function createLayer(options) {
  if (!options) {
    throw new Error('createLayer: options 参数不能为空');
  }

  const {
    type,
    imageUrl,
    layerInfo,
    canEditImage,
    canAdjustLayer,
    id,
    zIndex,
    ...typeSpecificFields
  } = options;

  // 验证必需字段
  if (!type) {
    throw new Error('createLayer: type 参数不能为空');
  }

  const validTypes = [
    constant.layer_userImage,
    constant.layer_template,
    constant.layer_skuMaterial,
    constant.layer_background,
    constant.layer_transparentGrid,
    constant.layer_stampingTemplate,
    constant.layer_stampingMaterial
  ];
  if (validTypes.indexOf(type) === -1) {
    throw new Error(
      `createLayer: type 必须是以下值之一：${validTypes.join(', ')}`
    );
  }

  if (!imageUrl) {
    throw new Error('createLayer: imageUrl 参数不能为空');
  }

  // userImage 类型需要验证 originImageUrl 和 originImageFileName
  if (type === constant.layer_userImage) {
    if (!typeSpecificFields.originImageUrl) {
      throw new Error(
        'createLayer: type=userImage 时，originImageUrl 参数不能为空'
      );
    }
    // if (!typeSpecificFields.originImageFileName) {
    //   throw new Error(
    //     'createLayer: type=userImage 时，originImageFileName 参数不能为空'
    //   );
    // }
    // userImage 的 zIndex 必须 >= 0
    // if (typeof zIndex === 'number' && zIndex < 0) {
    //   throw new Error('createLayer: type=userImage 时，zIndex 必须 >= 0');
    // }
  }

  // background 类型的 zIndex 必须 <= -100
  if (type === constant.layer_background) {
    if (typeof zIndex === 'number' && zIndex > backgroundMaxZIndex) {
      throw new Error(
        `createLayer: type=background 时，zIndex 必须 <= ${backgroundMaxZIndex}`
      );
    }
  }

  // transparentGrid 类型的 zIndex 必须 <= backgroundMaxZIndex（确保在所有普通图层之下）
  if (type === constant.layer_transparentGrid) {
    if (typeof zIndex === 'number' && zIndex > backgroundMaxZIndex) {
      throw new Error(
        `createLayer: type=transparentGrid 时，zIndex 必须 <= ${backgroundMaxZIndex}`
      );
    }
  }

  // if (!layerInfo) {
  //   throw new Error('createLayer: layerInfo 参数不能为空');
  // }

  // 根据类型设置默认值
  let defaultCanEditImage = true;
  let defaultCanAdjustLayer = true;

  if (type === constant.layer_background) {
    defaultCanAdjustLayer = false;
  } else if (type === constant.layer_template) {
    // skuTemplate 的 canEditImage 由模板配置决定，这里设为 false 作为默认值
    defaultCanEditImage = false;
  } else if (
    type === constant.layer_stampingTemplate ||
    type === constant.layer_stampingMaterial
  ) {
    // 烫色图层只在烫色编辑页专用入口处理，默认不参与普通编辑。
    defaultCanEditImage = false;
    defaultCanAdjustLayer = false;
  }

  // 根据类型设置默认 zIndex
  let defaultZIndex = 0;
  if (type === constant.layer_background) {
    defaultZIndex = backgroundMaxZIndex; // background 默认 zIndex 为 -100
  } else if (type === constant.layer_transparentGrid) {
    defaultZIndex = transparentGridZIndex;
  } else if (type === constant.layer_userImage) {
    defaultZIndex = 0; // userImage 默认 zIndex 为 0（必须 >= 0）
  } else if (type === constant.layer_stampingTemplate) {
    defaultZIndex = maxLayerZIndex + 20;
  } else if (type === constant.layer_stampingMaterial) {
    defaultZIndex = maxLayerZIndex + 10;
  }

  // 创建基础图层对象
  const layer = {
    id: id || generateLayerId(),
    type,
    zIndex: typeof zIndex === 'number' ? zIndex : defaultZIndex,
    imageUrl,
    layerInfo,
    canEditImage:
      typeof canEditImage === 'boolean' ? canEditImage : defaultCanEditImage,
    canAdjustLayer:
      typeof canAdjustLayer === 'boolean'
        ? canAdjustLayer
        : defaultCanAdjustLayer
  };

  // 添加类型特有字段
  if (type === constant.layer_userImage) {
    // 用户原图特有字段
    layer.originImageUrl = typeSpecificFields.originImageUrl;
    layer.originImageFileName = typeSpecificFields.originImageFileName;
    // layer.cutoutState = typeSpecificFields.cutoutState || 'none';
    // layer.cutoutStates = typeSpecificFields.cutoutStates || {
    //   currentStatus: 'none',
    //   fullBody: null,
    //   head: null,
    //   manual: null
    // };
    // layer.operationHistory = typeSpecificFields.operationHistory || [];
    layer.imageStatus = typeSpecificFields.imageStatus || {
      uploadRes: null,
      afterClipUploadRes: null,
      beforeClipRetouchRes: null,
      retouchRes: null,
      shapeClipRes: null,
      customOutlineClipOriginImageRes: null
    };
    // 抠图相关字段
    layer.retouchAction = typeSpecificFields.retouchAction || '';
    layer.retouchType = typeSpecificFields.retouchType || '';
    layer.excuteRetouch = typeSpecificFields.excuteRetouch || false;
    // 白墨相关字段
    layer.whiteInkOriginImageSrc = typeSpecificFields.whiteInkOriginImageSrc;
    layer.whiteAction = typeSpecificFields.whiteAction || '';
    // 白墨收缩（默认开启）
    layer.whiteShrink =
      typeSpecificFields.whiteShrink !== undefined
        ? typeSpecificFields.whiteShrink
        : true;
    // 描边宽度字段
    layer.strokeWidth = typeSpecificFields.strokeWidth;
    if (typeSpecificFields.strokeDisplayInfo) {
      layer.strokeDisplayInfo = typeSpecificFields.strokeDisplayInfo;
    }
    // 反面图(不是正反不同图)
    if (typeSpecificFields.isReverseLayerSku) {
      layer.reversePrimerLayerImageUrl =
        typeSpecificFields.reversePrimerLayerImageUrl || '';
      layer.reversePrimerLayerAction =
        typeSpecificFields.reversePrimerLayerAction ||
        constant.ReversePrimerLayerActionMap.AUTO;
      layer.scopeUv = typeSpecificFields.scopeUv || '';
      layer.scopeUvAction =
        typeSpecificFields.scopeUvAction || constant.ScopeUvActionMap.EMPTY;
    }
    // 原图信息字段（可选，只有当原图发生尺寸缩放或裁剪时才有此字段）
    assignDefinedFields(layer, typeSpecificFields, [
      'originImageInfo',
      'isUploadedUserImage',
      'whiteInk',
      'foilUrl'
    ]);
  } else if (type === constant.layer_template) {
    // sku 模板特有字段
    assignDefinedFields(layer, typeSpecificFields, [
      'whiteInk',
      'isClearTemplate',
      'maskUrl',
      'backUrl',
      'spotUv',
      'frostUrl',
      'foilUrl',
      'canvasType',
      'imageData'
    ]);
  } else if (type === constant.layer_skuMaterial) {
    // sku 素材特有字段
    if (typeSpecificFields.materialId) {
      layer.materialId = typeSpecificFields.materialId;
    }
    layer.source = typeSpecificFields.source || 'system';
    assignDefinedFields(layer, typeSpecificFields, [
      'originImageUrl',
      'originImageFileName',
      'imageStatus',
      'originImageInfo',
      'strokeWidth',
      'strokeColor',
      'initOutLineInfo',
      'strokeDisplayInfo',
      'couldRetouch',
      'whiteInk',
      'canvasType',
      'imageData'
    ]);
  } else if (type === constant.layer_stampingTemplate) {
    // 烫色模板特有字段。
    assignDefinedFields(layer, typeSpecificFields, [
      'stampingTemplateId',
      'stampingTemplateName',
      'source',
      'canvasType',
      'imageData'
    ]);
  } else if (type === constant.layer_stampingMaterial) {
    // 烫色素材特有字段。
    assignDefinedFields(layer, typeSpecificFields, [
      'stampingMaterialId',
      'stampingMaterialName',
      'source',
      'foilUrl',
      'canvasType',
      'imageData'
    ]);
  }

  // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
  validateLayerInfoImageSizeNonBlocking(layer, 'createLayer');
  return layer;
}

/**
 * 获取图层数组中的最大 zIndex
 * @param {Array} layers - 图层数组
 * @returns {number} 最大 zIndex，如果没有图层则返回 0
 */
export function getMaxZIndex(layers) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return 0;
  }

  let maxZIndex = 0;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && typeof layer.zIndex === 'number' && layer.zIndex > maxZIndex) {
      maxZIndex = layer.zIndex;
    }
  }

  return maxZIndex;
}

/**
 * 获取图层数组中的最小 zIndex
 * @param {Array} layers - 图层数组
 * @returns {number} 最小 zIndex，如果没有图层则返回 0
 */
export function getMinZIndex(layers) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return 0;
  }

  let minZIndex = Infinity;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && typeof layer.zIndex === 'number' && layer.zIndex < minZIndex) {
      minZIndex = layer.zIndex;
    }
  }

  return minZIndex === Infinity ? 0 : minZIndex;
}

/**
 * 确保烫色图层的 zIndex 高于所有非烫色图层。
 * @param {Array} layers - 图层数组。
 * @returns {Array} 处理后的图层数组。
 */
function normalizeStampingLayerTopZIndex(layers) {
  let maxNormalZIndex = -Infinity;
  let nextTopZIndex = -Infinity;
  let hasStampingLayer = false;

  layers.forEach((layer) => {
    const zIndex = layer && typeof layer.zIndex === 'number' ? layer.zIndex : 0;
    nextTopZIndex = Math.max(nextTopZIndex, zIndex);
    // 烫色图层需要统一保持在普通图层上方。
    if (
      layer &&
      (layer.type === constant.layer_stampingTemplate ||
        layer.type === constant.layer_stampingMaterial)
    ) {
      hasStampingLayer = true;
      return;
    }
    maxNormalZIndex = Math.max(maxNormalZIndex, zIndex);
  });

  // 没有烫色图层或没有普通图层时无需调整。
  if (!hasStampingLayer || maxNormalZIndex === -Infinity) {
    return layers;
  }

  const shouldAdjust = layers.some((layer) => {
    const zIndex = layer && typeof layer.zIndex === 'number' ? layer.zIndex : 0;
    // 非烫色图层不参与烫色最高层校验。
    if (
      !layer ||
      (layer.type !== constant.layer_stampingTemplate &&
        layer.type !== constant.layer_stampingMaterial)
    ) {
      return false;
    }
    return zIndex <= maxNormalZIndex;
  });

  // 烫色图层已经在最高层时保持原对象引用。
  if (!shouldAdjust) {
    return layers;
  }

  let nextStampingZIndex = nextTopZIndex + 1;
  return layers.map((layer) => {
    const zIndex = layer && typeof layer.zIndex === 'number' ? layer.zIndex : 0;
    // 只抬高不满足最高层规则的烫色图层，其他图层不做额外改动。
    if (
      layer &&
      (layer.type === constant.layer_stampingTemplate ||
        layer.type === constant.layer_stampingMaterial) &&
      zIndex <= maxNormalZIndex
    ) {
      return {
        ...layer,
        zIndex: nextStampingZIndex++
      };
    }
    return layer;
  });
}

/**
 * 按 zIndex 排序图层数组
 * @param {Array} layers - 图层数组
 * @returns {Array} 排序后的图层数组（不修改原数组）
 */
export function sortLayersByZIndex(layers) {
  if (!layers || !Array.isArray(layers)) {
    return [];
  }

  const normalizedLayers = normalizeStampingLayerTopZIndex(layers);

  // 创建副本并排序
  return normalizedLayers.slice().sort((a, b) => {
    const zIndexA = a && typeof a.zIndex === 'number' ? a.zIndex : 0;
    const zIndexB = b && typeof b.zIndex === 'number' ? b.zIndex : 0;
    return zIndexA - zIndexB;
  });
}

/**
 * 添加图层到 layerData
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {Object} layer - 要添加的图层对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function addLayer(layerData, layer) {
  if (!layerData) {
    throw new Error('addLayer: layerData 参数不能为空');
  }

  if (!layer) {
    throw new Error('addLayer: layer 参数不能为空');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 如果图层没有 zIndex，自动计算
  if (typeof layer.zIndex !== 'number') {
    const maxZIndex = getMaxZIndex(newLayerData.layers);
    layer.zIndex = maxZIndex + 1;
  }

  // 添加图层
  newLayerData.layers.push(layer);

  // 按 zIndex 排序
  newLayerData.layers = sortLayersByZIndex(newLayerData.layers);

  // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
  validateLayerInfoImageSizeNonBlocking(layer, 'addLayer');
  return newLayerData;
}

/**
 * 获取指定ID的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 图层ID
 * @returns {Object|null} 找到的图层对象，如果不存在则返回 null
 */
export function getLayer(layerData, layerId) {
  if (!layerData) {
    throw new Error('getLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('getLayer: layerId 参数不能为空');
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    return null;
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.id === layerId) {
      return layer;
    }
  }

  return null;
}

/**
 * 更新图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 要更新的图层ID
 * @param {Object} updates - 要更新的字段对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function updateLayer(layerData, layerId, updates) {
  if (!layerData) {
    throw new Error('updateLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('updateLayer: layerId 参数不能为空');
  }

  if (!updates || typeof updates !== 'object') {
    throw new Error('updateLayer: updates 参数必须是一个对象');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 查找并更新图层
  let found = false;
  for (let i = 0; i < newLayerData.layers.length; i++) {
    const layer = newLayerData.layers[i];
    if (layer && layer.id === layerId) {
      newLayerData.layers[i] = {
        ...layer,
        ...updates
      };
      // 非阻塞校验：layerInfo.width/height 是否与 imageUrl 对应图片真实宽高一致
      validateLayerInfoImageSizeNonBlocking(
        newLayerData.layers[i],
        'updateLayer'
      );
      found = true;
      break;
    }
  }

  if (!found) {
    throw new Error(`updateLayer: 未找到ID为 ${layerId} 的图层`);
  }

  // 如果更新了 zIndex，需要重新排序
  if (typeof updates.zIndex === 'number') {
    newLayerData.layers = sortLayersByZIndex(newLayerData.layers);
  }

  return newLayerData;
}

/**
 * 删除图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {string} layerId - 要删除的图层ID
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function removeLayer(layerData, layerId) {
  if (!layerData) {
    throw new Error('removeLayer: layerData 参数不能为空');
  }

  if (!layerId) {
    throw new Error('removeLayer: layerId 参数不能为空');
  }

  // 创建 layerData 的副本
  const newLayerData = {
    ...layerData,
    layers: layerData.layers ? [...layerData.layers] : []
  };

  // 查找并删除图层
  let found = false;
  for (let i = 0; i < newLayerData.layers.length; i++) {
    const layer = newLayerData.layers[i];
    if (layer && layer.id === layerId) {
      newLayerData.layers.splice(i, 1);
      found = true;
      break;
    }
  }

  if (!found) {
    throw new Error(`removeLayer: 未找到ID为 ${layerId} 的图层`);
  }

  return newLayerData;
}

/**
 * 初始化 layerData 数据结构
 * @param {Object} options - 初始化配置选项
 * @param {Object} options.canvas - 画布信息对象，如果提供则使用，否则需要后续设置
 * @param {Array} [options.layers=[]] - 初始图层数组
 * @param {Array} [options.reverseLayer=[]] - 反面图层数组
 * @param {number} [options.strokeWidth] - 当前画布描边宽度，0 表示不描边，未传则默认 0
 * @param {string} [options.reversePrimerLayerImageUrl] - 逆向层图片 URL，默认 ''
 * @param {string} [options.reversePrimerLayerAction] - 逆向层最近一次工具动作（见 ReversePrimerLayerActionMap），默认 ''
 * @param {string} [options.scopeUv] - 局部 UV 图片 URL，默认 ''
 * @param {string} [options.scopeUvAction] - 局部 UV 最近一次工具动作（见 ScopeUvActionMap），默认 ''
 * @returns {Object} 完整的 layerData 对象
 */
export function initLayerData(options) {
  const opts = options || {};

  const layerData = {
    canvas: opts.canvas || null,
    layers: Array.isArray(opts.layers) ? opts.layers : [],
    reverseLayer: Array.isArray(opts.reverseLayer) ? opts.reverseLayer : [],
    reverseLayerIsMirror:
      opts.reverseLayerIsMirror === undefined
        ? true
        : !!opts.reverseLayerIsMirror,
    outlineImageSrc: opts.outlineImageSrc || '',
    outlineOriginImageSrc: opts.outlineOriginImageSrc || '',
    outlineOriginImageFileName: opts.outlineOriginImageFileName || '',
    pureOutlineLineImageSrc: opts.pureOutlineLineImageSrc || '',
    pureOutlineLineCropBounds: opts.pureOutlineLineCropBounds || null,
    outlineSourceCanvasInfo: opts.outlineSourceCanvasInfo || null,
    reverseOutlineImageSrc: opts.reverseOutlineImageSrc || '',
    reverseOutlineOriginImageSrc: opts.reverseOutlineOriginImageSrc || '',
    reverseOutlineOriginImageFileName:
      opts.reverseOutlineOriginImageFileName || '',
    reversePureOutlineLineImageSrc: opts.reversePureOutlineLineImageSrc || '',
    reversePureOutlineLineCropBounds:
      opts.reversePureOutlineLineCropBounds || null,
    reverseOutlineSourceCanvasInfo: opts.reverseOutlineSourceCanvasInfo || null,
    previewCompositeImage: opts.previewCompositeImage || null,
    outlineImage: opts.outlineImage || null,
    accessoryAndOutlineImage: opts.accessoryAndOutlineImage || null,
    strokeWidth: opts.strokeWidth != null ? opts.strokeWidth : 0,
    reversePrimerLayerImageUrl: opts.reversePrimerLayerImageUrl || '',
    reversePrimerLayerAction: opts.reversePrimerLayerAction || '',
    scopeUv: opts.scopeUv || '',
    scopeUvAction: opts.scopeUvAction || ''
  };

  // 如果提供了图层，确保它们按 zIndex 排序
  if (layerData.layers.length > 0) {
    layerData.layers = sortLayersByZIndex(layerData.layers);
  }
  if (layerData.reverseLayer.length > 0) {
    layerData.reverseLayer = sortLayersByZIndex(layerData.reverseLayer);
  }

  return layerData;
}

/**
 * 判断画布显示尺寸是否为有效数字。
 * @param {*} value - 待检查的尺寸值
 * @returns {boolean} 是否为有效显示尺寸
 */
function isValidCanvasDisplaySize(value) {
  return typeof value === 'number' && isFinite(value) && value > 0;
}

/**
 * 读取 layerData.canvas 或当前 canvasInfo 的显示尺寸。
 * @param {Object} canvas - 画布信息
 * @returns {{displayWidth:number,displayHeight:number}|null} 显示尺寸
 */
function getValidCanvasDisplaySize(canvas) {
  // 缺少合法宽高时，调用方不能进行坐标归一化。
  if (
    !canvas ||
    !isValidCanvasDisplaySize(canvas.displayWidth) ||
    !isValidCanvasDisplaySize(canvas.displayHeight)
  ) {
    return null;
  }
  return {
    displayWidth: canvas.displayWidth,
    displayHeight: canvas.displayHeight
  };
}

/**
 * 按画布缩放比例缩放数值字段。
 * @param {*} value - 待缩放的字段值
 * @param {number} scale - 缩放比例
 * @returns {*} 缩放后的字段值
 */
function scaleCanvasNumber(value, scale) {
  return typeof value === 'number' ? value * scale : value;
}

/**
 * 按目标画布尺寸归一化单个 layerInfo。
 * @param {Object} layerInfo - 图层坐标信息
 * @param {{scaleX:number,scaleY:number}} scaleInfo - 画布缩放信息
 * @returns {Object} 归一化后的 layerInfo
 */
function normalizeLayerInfoCanvasSize(layerInfo, scaleInfo) {
  // 缺少 layerInfo 时保持原值，避免破坏异常旧数据的兜底流程。
  if (!layerInfo || typeof layerInfo !== 'object') {
    return layerInfo;
  }

  var nextLayerInfo = {
    ...layerInfo,
    left: scaleCanvasNumber(layerInfo.left, scaleInfo.scaleX),
    top: scaleCanvasNumber(layerInfo.top, scaleInfo.scaleY)
  };
  var originScaleX =
    typeof layerInfo.scaleX === 'number' && layerInfo.scaleX > 0
      ? layerInfo.scaleX
      : typeof layerInfo.scale === 'number' && layerInfo.scale > 0
      ? layerInfo.scale
      : 1;
  var originScaleY =
    typeof layerInfo.scaleY === 'number' && layerInfo.scaleY > 0
      ? layerInfo.scaleY
      : typeof layerInfo.scale === 'number' && layerInfo.scale > 0
      ? layerInfo.scale
      : 1;

  nextLayerInfo.scaleX = originScaleX * scaleInfo.scaleX;
  nextLayerInfo.scaleY = originScaleY * scaleInfo.scaleY;

  // 仅在等比迁移时同步旧 scale 字段，避免非等比场景把两个方向压成一个值。
  if (
    typeof layerInfo.scale === 'number' &&
    layerInfo.scale > 0 &&
    Math.abs(scaleInfo.scaleX - scaleInfo.scaleY) < 0.001
  ) {
    nextLayerInfo.scale = layerInfo.scale * scaleInfo.scaleX;
  }

  return nextLayerInfo;
}

/**
 * 按目标画布尺寸归一化图层集合。
 * @param {Array} layers - 图层集合
 * @param {{scaleX:number,scaleY:number}} scaleInfo - 画布缩放信息
 * @returns {Array} 归一化后的图层集合
 */
function normalizeLayerCollectionCanvasSize(layers, scaleInfo) {
  // 非数组图层集合保持原值，兼容历史异常数据。
  if (!Array.isArray(layers)) {
    return layers;
  }
  return layers.map(function (layer) {
    // 空图层项保持原值，避免改变数组占位语义。
    if (!layer || typeof layer !== 'object') {
      return layer;
    }
    return {
      ...layer,
      layerInfo: normalizeLayerInfoCanvasSize(layer.layerInfo, scaleInfo)
    };
  });
}

/**
 * 将已有 layerData 的画布坐标基准迁移到当前 edit.vue 画布尺寸。
 * @param {Object} layerData - 待归一化的 layerData
 * @param {Object} targetCanvasInfo - 当前 edit.vue 的 canvasInfo
 * @returns {{layerData:Object,changed:boolean}} 归一化结果
 */
export function normalizeLayerDataCanvasSize(layerData, targetCanvasInfo) {
  var sourceSize = getValidCanvasDisplaySize(layerData && layerData.canvas);
  var targetSize = getValidCanvasDisplaySize(targetCanvasInfo);
  // 源或目标画布尺寸缺失时不迁移，保留原始数据作为兜底。
  if (!layerData || !sourceSize || !targetSize) {
    return {
      layerData: layerData,
      changed: false
    };
  }

  var widthChanged =
    Math.abs(sourceSize.displayWidth - targetSize.displayWidth) >= 1;
  var heightChanged =
    Math.abs(sourceSize.displayHeight - targetSize.displayHeight) >= 1;
  // 画布尺寸没有实际变化时不迁移，避免重复缩放。
  if (!widthChanged && !heightChanged) {
    return {
      layerData: layerData,
      changed: false
    };
  }

  var scaleInfo = {
    scaleX: targetSize.displayWidth / sourceSize.displayWidth,
    scaleY: targetSize.displayHeight / sourceSize.displayHeight
  };
  var nextLayerData = {
    ...layerData,
    canvas: {
      ...layerData.canvas,
      displayWidth: targetSize.displayWidth,
      displayHeight: targetSize.displayHeight
    },
    layers: normalizeLayerCollectionCanvasSize(layerData.layers, scaleInfo),
    reverseLayer: normalizeLayerCollectionCanvasSize(
      layerData.reverseLayer,
      scaleInfo
    )
  };

  return {
    layerData: nextLayerData,
    changed: true
  };
}

/**
 * 计算图片在画布中的位置和尺寸信息
 * @param {Object} options - 计算选项
 * @param {number} options.imageWidth - 图片原始宽度（单位：px）
 * @param {number} options.imageHeight - 图片原始高度（单位：px）
 * @param {number} options.canvasWidth - 画布宽度（单位：px）
 * @param {number} options.canvasHeight - 画布高度（单位：px）
 * @param {string} [options.type] - 布局类型：ImageLayoutTypeMap.fit / fill
 * @returns {Object} drawCanvasInfo 对象
 */
export function calcImageInCanvasPosition({
  imageWidth,
  imageHeight,
  canvasWidth,
  canvasHeight,
  type = constant.ImageLayoutTypeMap.fit
}) {
  if (typeof imageWidth !== 'number' || imageWidth <= 0) {
    throw new Error('calculateDrawCanvasInfo: imageWidth 必须是大于0的数字');
  }

  if (typeof imageHeight !== 'number' || imageHeight <= 0) {
    throw new Error('calculateDrawCanvasInfo: imageHeight 必须是大于0的数字');
  }

  if (typeof canvasWidth !== 'number' || canvasWidth <= 0) {
    throw new Error('calculateDrawCanvasInfo: canvasWidth 必须是大于0的数字');
  }

  if (typeof canvasHeight !== 'number' || canvasHeight <= 0) {
    throw new Error('calculateDrawCanvasInfo: canvasHeight 必须是大于0的数字');
  }

  const imageRatio = imageWidth / imageHeight;
  const canvasRatio = canvasWidth / canvasHeight;
  let inCanvasImageWidth;
  let inCanvasImageHeight;
  let baseType;

  if (type === constant.ImageLayoutTypeMap.fit) {
    // 适应模式：短边撑满，长边居中
    if (imageRatio < canvasRatio) {
      // 图片更高，高度撑满
      inCanvasImageHeight = canvasHeight;
      inCanvasImageWidth = inCanvasImageHeight * imageRatio;
      baseType = 'height';
    } else {
      // 图片更宽，宽度撑满
      inCanvasImageWidth = canvasWidth;
      inCanvasImageHeight = inCanvasImageWidth / imageRatio;
      baseType = 'width';
    }
  } else if (type === constant.ImageLayoutTypeMap.fill) {
    // 填充模式：长边撑满，短边居中
    if (imageRatio > canvasRatio) {
      // 图片更宽，高度撑满（长边）
      inCanvasImageHeight = canvasHeight;
      inCanvasImageWidth = inCanvasImageHeight * imageRatio;
      baseType = 'height';
    } else {
      // 图片更高，宽度撑满（长边）
      inCanvasImageWidth = canvasWidth;
      inCanvasImageHeight = inCanvasImageWidth / imageRatio;
      baseType = 'width';
    }
  } else {
    throw new Error(
      'calculateDrawCanvasInfo: type 必须是 fit 或 fill（见 ImageLayoutTypeMap）'
    );
  }

  // 居中显示
  const x = (canvasWidth - inCanvasImageWidth) / 2;
  const y = (canvasHeight - inCanvasImageHeight) / 2;

  return {
    originImageWidth: imageWidth,
    originImageHeight: imageHeight,
    inCanvasImageWidth: inCanvasImageWidth,
    inCanvasImageHeight: inCanvasImageHeight,
    initImageInCanvasWidth: inCanvasImageWidth,
    prevTouchWidth: inCanvasImageWidth,
    prevTouchHeight: inCanvasImageHeight,
    currStatus: {
      x: x,
      y: y,
      scale: 1
    },
    prevStatus: {
      x: x,
      y: y,
      scale: 1
    },
    baseType: baseType
  };
}

/**
 * 验证图片 URL
 * @param {any} imageUrl - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateImageUrl(imageUrl, paramName = 'imageUrl') {
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error(`${paramName} 必须是字符串且不为空`);
  }
}

/**
 * 验证画布信息
 * @param {any} canvasInfo - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateCanvasInfo(canvasInfo, paramName = 'canvasInfo') {
  if (!canvasInfo || typeof canvasInfo !== 'object') {
    throw new Error(`${paramName} 必须是对象`);
  }
  if (
    typeof canvasInfo.displayWidth !== 'number' ||
    canvasInfo.displayWidth <= 0
  ) {
    throw new Error(`${paramName}.displayWidth 必须是大于0的数字`);
  }
  if (
    typeof canvasInfo.displayHeight !== 'number' ||
    canvasInfo.displayHeight <= 0
  ) {
    throw new Error(`${paramName}.displayHeight 必须是大于0的数字`);
  }
}

/**
 * 验证布局类型
 * @param {any} type - 要验证的值
 * @param {string} paramName - 参数名称，用于错误信息
 * @throws {Error} 如果验证失败
 */
export function validateLayoutType(type, paramName = 'type') {
  var L = constant.ImageLayoutTypeMap;
  if (type !== L.fit && type !== L.fill && type !== L.fitRemoveTransparent) {
    throw new Error(
      paramName + ' 必须是 ImageLayoutTypeMap.fit、fill 或 fitRemoveTransparent'
    );
  }
}

/**
 * 获取 skuTemplate 类型的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {Object|null} 找到的第一个 skuTemplate layer，如果不存在则返回 null
 */
export function getSkuTemplateLayer(layerData) {
  if (!layerData) {
    return null;
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    return null;
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.type === constant.layer_template) {
      return layer;
    }
  }

  return null;
}

/**
 * 处理 skuTemplate maskUrl，生成处理后的遮罩层图片 URL
 * @param {Object} options - 参数对象
 * @param {Object} options.layerData - 完整的 layerData 对象
 * @param {string} options.hexColor - 替换黑色像素的颜色，默认 '#fdeeed'
 * @param {number} options.maxWidth - 图片的最大宽度
 * @param {number} options.maxHeight - 图片的最大高度
 * @param {boolean} [options.cache=true] - 是否启用缓存，默认 true
 * @returns {Promise<string>} 返回处理后的图片 URL，如果不存在 maskUrl 则返回空字符串
 */
export async function processSkuTemplateMaskUrl(options) {
  const { layerData, hexColor = '#fdeeed', maxWidth, maxHeight, cache = true } =
    options || {};

  if (!layerData || !layerData.layers) {
    return '';
  }

  try {
    // 获取 skuTemplate layer
    const skuTemplateLayer = getSkuTemplateLayer(layerData);
    if (!skuTemplateLayer) {
      return '';
    }

    // 获取 maskUrl
    const maskUrl =
      skuTemplateLayer &&
      typeof skuTemplateLayer.maskUrl === 'string' &&
      skuTemplateLayer.maskUrl
        ? skuTemplateLayer.maskUrl
        : '';

    if (!maskUrl) {
      return '';
    }

    // 调用 fillBlackPixelsWithHexColorByComposite 处理 maskUrl
    const processedMaskUrl = await canvasUtil.fillBlackPixelsWithHexColorByComposite(
      {
        imageSrc: maskUrl,
        hexColor: hexColor,
        maxWidth: maxWidth,
        maxHeight: maxHeight,
        cache: cache
      }
    );
    // window._debugLog({
    //   title: 'processSkuTemplateMaskUrl',
    //   maskUrl: maskUrl
    // });

    return processedMaskUrl;
  } catch (error) {
    console.error('processSkuTemplateMaskUrl: 处理失败', error);
    return '';
  }
}

/**
 * 获取第一个 type=userImage 的 layer 在 layers 数组中的 index
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {number} layer index，如果不存在则返回 -1
 */
export function getActiveUserImageLayerIndex(layerData) {
  if (!layerData) {
    slog.error('getActiveUserImageLayerIndex: layerData 参数不能为空');
    throw new Error('getActiveUserImageLayerIndex: layerData 参数不能为空');
  }

  if (!layerData.layers || !Array.isArray(layerData.layers)) {
    slog.error('getActiveUserImageLayerIndex: layerData.layers 参数不能为空');
    throw new Error(
      'getActiveUserImageLayerIndex: layerData.layers 参数不能为空'
    );
  }

  for (let i = 0; i < layerData.layers.length; i++) {
    const layer = layerData.layers[i];
    if (layer && layer.type === constant.layer_userImage) {
      return i;
    }
  }

  return -1;
}

/**
 * 获取轮廓图路径
 * 找到第一个 type=userImage 的 layer，返回其 imageUrl
 * @param {Array} layers - layer 数组
 * @returns {string} 轮廓图路径（imageUrl），如果不存在则返回 null
 */

/**
 * 判断 skuTemplate layer 的层级模式（置顶或置底）
 * @param {Array} layers - 图层数组
 * @param {Object} templateLayer - skuTemplate layer 对象
 * @returns {string|null} 'top'（置顶）、'bottom'（置底）或 null（无法判断）
 */
export function getSkuTemplateLayerMode(layers, templateLayer) {
  if (!layers || !Array.isArray(layers) || layers.length === 0) {
    return null;
  }

  if (!templateLayer || typeof templateLayer.zIndex !== 'number') {
    return null;
  }

  const maxZIndex = getMaxZIndex(layers);
  const minZIndex = getMinZIndex(layers);

  // 如果模板的 zIndex 等于最大 zIndex，则为置顶
  if (templateLayer.zIndex === maxZIndex) {
    return 'top';
  }

  // 如果模板的 zIndex 等于最小 zIndex，则为置底
  if (templateLayer.zIndex === minZIndex) {
    return 'bottom';
  }

  // 如果既不是最大也不是最小，返回 null
  return null;
}

/**
 * 加载图片
 * @param {string} imageUrl - 图片地址
 * @returns {Promise<HTMLImageElement>} 返回加载完成的 Image 对象
 */
export function loadImage(imageUrl) {
  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error('loadImage: imageUrl 参数不能为空且必须是字符串');
  }

  // 先从缓存中查找
  const cachedImage = imageCache.get(imageUrl);
  if (cachedImage) {
    return Promise.resolve(cachedImage);
  }

  return new Promise((resolve, reject) => {
    const img = new Image();
    // 如果是 http/https 协议的图片，设置跨域属性
    if (/^http/.test(imageUrl)) {
      img.crossOrigin = 'anonymous';
    }

    img.onload = () => {
      if (img.naturalWidth) {
        img.width = img.naturalWidth;
        img.height = img.naturalHeight;
      }
      // 将加载成功的图片存入缓存
      imageCache.set(imageUrl, img);
      resolve(img);
    };

    img.onerror = (err) => {
      reject(new Error(`图片加载失败: ${imageUrl}`));
    };

    img.src = imageUrl;
  });
}

/**
 * 验证绘制 canvas 的通用参数
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {string} methodName - 方法名称，用于错误提示
 * @param {Object} [options.layerInfo] - layerInfo 对象（可选）
 */
function validateDrawCanvasOptions(options, methodName) {
  if (!options || typeof options !== 'object') {
    throw new Error(`${methodName}: options 参数必须是一个对象`);
  }

  const { ctx, canvasWidth, canvasHeight, layerInfo } = options;

  if (!ctx || typeof ctx.drawImage !== 'function') {
    throw new Error(
      `${methodName}: ctx 参数必须是一个有效的 CanvasRenderingContext2D 对象`
    );
  }

  if (typeof canvasWidth !== 'number' || canvasWidth <= 0) {
    throw new Error(`${methodName}: canvasWidth 必须是大于0的数字`);
  }

  if (typeof canvasHeight !== 'number' || canvasHeight <= 0) {
    throw new Error(`${methodName}: canvasHeight 必须是大于0的数字`);
  }

  // 如果提供了 layerInfo，验证其字段
  if (layerInfo !== undefined) {
    if (!layerInfo || typeof layerInfo !== 'object') {
      throw new Error(`${methodName}: layerInfo 必须是一个对象`);
    }

    // 验证必要字段是否存在
    if (typeof layerInfo.left !== 'number') {
      throw new Error(`${methodName}: layerInfo.left 字段必须存在且为数字`);
    }

    if (typeof layerInfo.top !== 'number') {
      throw new Error(`${methodName}: layerInfo.top 字段必须存在且为数字`);
    }

    if (typeof layerInfo.width !== 'number' || layerInfo.width <= 0) {
      throw new Error(
        `${methodName}: layerInfo.width 字段必须存在且为大于0的数字`
      );
    }

    if (typeof layerInfo.height !== 'number' || layerInfo.height <= 0) {
      throw new Error(
        `${methodName}: layerInfo.height 字段必须存在且为大于0的数字`
      );
    }
  }
}

/**
 * 在 2D canvas 上绘制“透明网格底图”（使用 createPattern 平铺填满）
 * - 仅用于编辑 UI 展示，不参与合成导出
 * - 忽略 layerInfo，直接按 canvasWidth/canvasHeight 填满
 *
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {string} options.imageUrl - 透明网格图片地址
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.opacity=1] - 透明度（0-1）
 * @returns {Promise<void>}
 */
async function drawTransparentGridPatternToCanvas(options) {
  const { ctx, imageUrl, canvasWidth, canvasHeight, opacity } = options || {};

  // 通用参数校验（不校验 layerInfo）
  validateDrawCanvasOptions(
    {
      ctx,
      canvasWidth,
      canvasHeight
    },
    'drawTransparentGridPatternToCanvas'
  );

  if (!imageUrl || typeof imageUrl !== 'string') {
    throw new Error(
      'drawTransparentGridPatternToCanvas: imageUrl 不能为空且必须是字符串'
    );
  }

  const image = await loadImage(imageUrl);
  ctx.save();

  const finalOpacity = typeof opacity === 'number' ? opacity : 1;
  const oldAlpha = ctx.globalAlpha;
  ctx.globalAlpha = finalOpacity;

  const pattern = ctx.createPattern(image, 'repeat');
  if (pattern) {
    ctx.fillStyle = pattern;
    ctx.fillRect(0, 0, canvasWidth, canvasHeight);
  }

  ctx.globalAlpha = oldAlpha;
  ctx.restore();
}

/**
 * 绘制单个 layer 到 canvas
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {Object} options.layer - layer 对象（支持所有类型：userImage、skuTemplate、skuMaterial、background）
 * @param {number} [options.layer.opacity] - 图层透明度（0-1），从 layer.opacity 读取，默认为 1
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 中的坐标和尺寸缩放到目标画布
 * @returns {Promise<void>}
 */
export async function drawLayerToCanvas(options) {
  const { ctx, layer, canvasWidth, canvasHeight, scale = 1 } = options;

  // 验证 layer 特定参数
  if (!layer || typeof layer !== 'object') {
    throw new Error('drawLayerToCanvas: layer 参数必须是一个对象');
  }

  // 普通图层依赖 imageUrl，文字图层允许通过 imageData 还原主图。
  if (!hasLayerImageSource(layer)) {
    throw new Error('drawLayerToCanvas: layer 主图来源不能为空');
  }

  try {
    // 透明网格底图：平铺填满（忽略 layerInfo，避免因 layerInfo 不一致导致显示异常）
    if (layer.type === constant.layer_transparentGrid) {
      const opacity = layer.opacity || 1;
      await drawTransparentGridPatternToCanvas({
        ctx,
        imageUrl: layer.imageUrl,
        canvasWidth,
        canvasHeight,
        opacity
      });
      return;
    }

    // 验证通用参数（包含 layerInfo 验证）
    validateDrawCanvasOptions(
      {
        ctx,
        canvasWidth,
        canvasHeight,
        layerInfo: layer.layerInfo
      },
      'drawLayerToCanvas'
    );

    const layerInfo = layer.layerInfo;

    const imageUrlToDraw = await resolveLayerImageUrl(layer);
    // 加载图片
    const image = await loadImage(imageUrlToDraw);
    // || {
    //   x: 0,
    //   y: 0,
    //   width: canvasWidth,
    //   height: canvasHeight,
    //   rotate: 0,
    //   scale: 1
    // };

    // 保存 canvas 状态
    ctx.save();
    // 设置透明度（从 layer.opacity 读取，默认为 1）
    const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;
    const oldAlpha = ctx.globalAlpha;
    ctx.globalAlpha = opacity;

    // 计算绘制位置和尺寸（应用缩放参数）
    const {
      left,
      top,
      width,
      height,
      angle,
      scaleX,
      scaleY,
      originX = 'left',
      originY = 'top'
    } = layerInfo;

    // 应用缩放参数将实际尺寸转换为显示尺寸
    const scaledLeft = left * scale;
    const scaledTop = top * scale;
    const scaledWidth = width * (scaleX || 1) * scale;
    const scaledHeight = height * (scaleY || 1) * scale;

    // 如果有旋转，根据 originX/originY 确定旋转中心点（与 Fabric.js 保持一致）
    if (angle !== 0) {
      // 计算变换原点位置（与 Fabric.js 的 originX/originY 对应）
      let originPointX = scaledLeft;
      let originPointY = scaledTop;

      if (originX === 'center') {
        originPointX = scaledLeft + scaledWidth / 2;
      } else if (originX === 'right') {
        originPointX = scaledLeft + scaledWidth;
      }

      if (originY === 'center') {
        originPointY = scaledTop + scaledHeight / 2;
      } else if (originY === 'bottom') {
        originPointY = scaledTop + scaledHeight;
      }

      // 平移到变换原点，旋转，然后平移回去
      ctx.translate(originPointX, originPointY);
      ctx.rotate((angle * Math.PI) / 180);
      ctx.translate(-originPointX, -originPointY);
    }

    // 绘制图片
    ctx.drawImage(image, scaledLeft, scaledTop, scaledWidth, scaledHeight);

    // 恢复 canvas 状态（包括透明度）
    ctx.globalAlpha = oldAlpha;
    ctx.restore();
  } catch (error) {
    console.error('drawLayerToCanvas: 绘制 layer 失败', error);
    throw error;
  }
}

/**
 * 按照 zIndex 顺序绘制所有 layers 到 canvas
 * @param {Object} options - 参数对象
 * @param {CanvasRenderingContext2D} options.ctx - canvas 2D context
 * @param {Array} options.layers - layer 数组
 * @param {number} options.canvasWidth - 画布宽度
 * @param {number} options.canvasHeight - 画布高度
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 中的坐标和尺寸缩放到目标画布
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许绘制烫色专用图层
 * @returns {Promise<void>}
 */
export async function drawLayerArrayToCanvas(options) {
  // 验证通用参数
  validateDrawCanvasOptions(options, 'drawLayerArrayToCanvas');

  const {
    ctx,
    layers,
    canvasWidth,
    canvasHeight,
    scale,
    includeStampingLayers = false
  } = options;

  // 验证 layers 特定参数
  if (!layers || !Array.isArray(layers)) {
    throw new Error('drawLayerArrayToCanvas: layers 参数必须是一个数组');
  }

  // 如果没有 layers，直接返回
  if (layers.length === 0) {
    return;
  }

  // 按 zIndex 排序 layers（从小到大，zIndex 小的在下层）
  const sortedLayers = sortLayersByZIndex(layers);

  // 依次绘制每个 layer
  for (let i = 0; i < sortedLayers.length; i++) {
    const layer = sortedLayers[i];
    // 通用 2D 绘制默认过滤烫色图层。
    if (!shouldRenderLayerInCommonDesign(layer, { includeStampingLayers })) {
      continue;
    }
    // 缺少主图来源的图层无法参与 2D 合成绘制。
    if (!hasLayerImageSource(layer)) {
      continue;
    }

    try {
      await drawLayerToCanvas({
        ctx,
        layer,
        canvasWidth,
        canvasHeight,
        scale
      });
    } catch (error) {
      console.error(
        `drawLayerArrayToCanvas: 绘制 layer ${layer.id || i} 失败`,
        error
      );
      // 继续绘制其他 layer，不中断整个流程
    }
  }
}

/**
 * 添加 skuTemplate 类型的图层
 * @param {Object} options - 参数对象
 * @param {Object} options.layerData - 完整的 layerData 对象
 * @param {Object} options.template - 模板对象，包含 id、image、name 等字段
 * @param {Object} [options.layerInfo] - 图层画布信息（可选，如果不提供则根据 canvas 尺寸和模板图片尺寸计算）
 * @returns {Promise<Object>} 更新后的 layerData（不修改原对象）
 */
export async function addSkuTemplateLayer(options) {
  if (!options || typeof options !== 'object') {
    throw new Error('addSkuTemplateLayer: options 参数必须是一个对象');
  }

  const { layerData, template, layerInfo } = options;

  if (!layerData) {
    throw new Error('addSkuTemplateLayer: layerData 参数不能为空');
  }

  if (!template) {
    throw new Error('addSkuTemplateLayer: template 参数不能为空');
  }

  if (!template.image) {
    throw new Error('addSkuTemplateLayer: template.image 参数不能为空');
  }

  // 检查是否已存在 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (existingLayer) {
    console.error(
      'addSkuTemplateLayer: 已存在 skuTemplate layer，无法重复添加'
    );
    return layerData;
  }

  // 如果没有提供 layerInfo，根据 canvas 和模板图片尺寸计算
  let finalLayerInfo = layerInfo;
  if (!finalLayerInfo) {
    if (!layerData.canvas) {
      throw new Error(
        'addSkuTemplateLayer: 未提供 layerInfo 且 layerData.canvas 不存在，无法创建 layerInfo'
      );
    }

    const canvasWidth = layerData.canvas.displayWidth;
    const canvasHeight = layerData.canvas.displayHeight;

    // 加载模板图片获取原始尺寸
    const imageElement = await loadImage(template.image);
    const templateImageWidth = imageElement.naturalWidth || imageElement.width;
    const templateImageHeight =
      imageElement.naturalHeight || imageElement.height;

    // 使用 calcImageInCanvasPosition 计算模板图片在画布中的位置（fill 模式，撑满画布）

    // 创建 layerInfo：width/height 使用图片原始尺寸，scale 根据显示尺寸计算
    finalLayerInfo = createLayerInfo({
      left: 0,
      top: 0,
      width: templateImageWidth,
      height: templateImageHeight,
      angle: 0,
      scaleX: canvasWidth / templateImageWidth,
      scaleY: canvasHeight / templateImageHeight
    });
  }

  // 创建 skuTemplate layer
  // zIndex 由外部（调用方）按规则计算后传入；这里直接使用传入值
  // - 若传入非数字，则默认置顶
  const skuTemplateZIndex =
    template && typeof template.zIndex === 'number'
      ? template.zIndex
      : maxLayerZIndex;

  const skuTemplateLayer = createLayer({
    type: constant.layer_template,
    imageUrl: template.image,
    layerInfo: finalLayerInfo,
    whiteInk: template.whiteInk,
    maskUrl: template.maskUrl,
    backUrl: template.backUrl,
    spotUv: template.spotUv,
    frostUrl: template.frostUrl,
    canvasType: template.canvasType,
    imageData: template.imageData,
    zIndex: skuTemplateZIndex
  });

  // 添加到 layerData
  return addLayer(layerData, skuTemplateLayer);
}

/**
 * 更新 skuTemplate 类型的图层（合并对象）
 * @param {Object} layerData - 完整的 layerData 对象
 * @param {Object} updates - 要更新的字段对象，会与现有 layer 合并
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function updateSkuTemplateLayer(layerData, updates) {
  if (!layerData) {
    throw new Error('updateSkuTemplateLayer: layerData 参数不能为空');
  }

  if (!updates || typeof updates !== 'object') {
    throw new Error('updateSkuTemplateLayer: updates 参数必须是一个对象');
  }

  // 查找现有的 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (!existingLayer) {
    throw new Error('updateSkuTemplateLayer: 未找到 skuTemplate layer');
  }

  // 合并现有 layer 和 updates 对象
  const mergedUpdates = {
    ...existingLayer,
    ...updates
  };

  // 调用 updateLayer 更新
  return updateLayer(layerData, existingLayer.id, mergedUpdates);
}

/**
 * 删除 skuTemplate 类型的图层
 * @param {Object} layerData - 完整的 layerData 对象
 * @returns {Object} 更新后的 layerData（不修改原对象）
 */
export function removeSkuTemplateLayer(layerData) {
  if (!layerData) {
    throw new Error('removeSkuTemplateLayer: layerData 参数不能为空');
  }

  // 查找现有的 skuTemplate layer
  const existingLayer = getSkuTemplateLayer(layerData);
  if (!existingLayer) {
    // 如果不存在，直接返回原 layerData（允许幂等操作）
    return layerData;
  }

  // 调用 removeLayer 删除
  return removeLayer(layerData, existingLayer.id);
}

/**
 * 将 layerInfo 转换为 Fabric.js 对象属性
 * @param {Object} layerInfo - layerInfo 对象
 * @returns {Object} Fabric.js 对象属性
 */
export function layerInfoToFabricProperties(layerInfo) {
  if (!layerInfo || typeof layerInfo !== 'object') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo 参数必须是一个对象'
    );
  }

  // 验证必要字段
  if (typeof layerInfo.left !== 'number') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.left 字段必须存在且为数字'
    );
  }

  if (typeof layerInfo.top !== 'number') {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.top 字段必须存在且为数字'
    );
  }

  if (typeof layerInfo.width !== 'number' || layerInfo.width <= 0) {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.width 字段必须存在且为大于0的数字'
    );
  }

  if (typeof layerInfo.height !== 'number' || layerInfo.height <= 0) {
    throw new Error(
      'layerInfoToFabricProperties: layerInfo.height 字段必须存在且为大于0的数字'
    );
  }

  // 获取缩放比例（默认 1）
  const scale =
    typeof layerInfo.scale === 'number' && layerInfo.scale > 0
      ? layerInfo.scale
      : 1;

  // 获取旋转角度（默认 0），支持 rotate 和 angle（兼容旧数据）
  const angle =
    typeof layerInfo.angle === 'number'
      ? layerInfo.angle
      : typeof layerInfo.rotate === 'number'
      ? layerInfo.rotate
      : 0;

  return {
    left: layerInfo.left,
    top: layerInfo.top,
    angle: angle,
    scaleX: scale,
    scaleY: scale,
    width: layerInfo.width,
    height: layerInfo.height,
    originX: 'left',
    originY: 'top'
  };
}

/**
 * 将 Fabric.js 对象属性转换为 layerInfo
 * @param {Object} fabricObj - Fabric.js 对象或包含属性的对象
 * @returns {Object} layerInfo 对象
 */
export function fabricPropertiesToLayerInfo(fabricObj) {
  if (!fabricObj || typeof fabricObj !== 'object') {
    throw new Error(
      'fabricPropertiesToLayerInfo: fabricObj 参数必须是一个对象'
    );
  }

  // 获取 Fabric 对象的属性
  const left = typeof fabricObj.left === 'number' ? fabricObj.left : 0;
  const top = typeof fabricObj.top === 'number' ? fabricObj.top : 0;
  const angle = typeof fabricObj.angle === 'number' ? fabricObj.angle : 0;
  const scaleX =
    typeof fabricObj.scaleX === 'number' && fabricObj.scaleX > 0
      ? fabricObj.scaleX
      : 1;
  const scaleY =
    typeof fabricObj.scaleY === 'number' && fabricObj.scaleY > 0
      ? fabricObj.scaleY
      : 1;
  const width =
    typeof fabricObj.width === 'number' && fabricObj.width > 0
      ? fabricObj.width
      : 1;
  const height =
    typeof fabricObj.height === 'number' && fabricObj.height > 0
      ? fabricObj.height
      : 1;

  // layerInfo 的 width/height 是原始尺寸（Fabric 的 width/height）
  return createLayerInfo({
    left: left,
    top: top,
    width: width,
    height: height,
    angle: angle,
    scaleX: scaleX,
    scaleY: scaleY
  });
}

/**
 * 将图片转换为白墨
 * 调用 fillNonTransparentPixels 将图片的非透明像素填充为白色
 * @param {string} imageUrl - 图片URL
 * @returns {Promise<string>} 白墨图片URL（base64 data URL）
 */
async function imageToWhiteInk(imageUrl) {
  let r = await canvasUtil.fillNonTransparentPixels({
    imageSrc: imageUrl,
    rgbaString: constant.whiteInkRgba
  });
  // mwx.debugLog({
  //   imageToWhiteInk: r
  // });
  return r;
}

/**
 * 使用 Fabric.js 加载所有 layers 到 canvas
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} [options.fabricCanvas] - Fabric canvas 实例（offscreen=true 时可不传，内部会创建离屏 StaticCanvas）
 * @param {Array} options.layers - layer 数组
 * @param {Object} [options.canvasInfo] - 画布信息（用于注入透明网格底图 layer，至少包含 displayWidth/displayHeight；offscreen=true 时必传）
 * @param {boolean} [options.clearCanvas=true] - 是否清空 canvas
 * @param {boolean} [options.selectFirst=false] - 是否选中第一个可编辑的对象
 * @param {string} [options.selectedLayerId] - 指定要选中的 layerId，优先级高于 selectFirst
 * @param {boolean} [options.canEdit] - 全局禁用编辑模式，如果为 false 会覆盖局部的 canEdit
 * @param {boolean} [options.drawWhiteInkOnly=false] - 是否只绘制白墨图层，如果为 true，只绘制非清空白墨图层，并将 imageUrl 替换为 whiteInk（whiteInk='' 时使用 imageToWhiteInk 转换）
 * @param {number} [options.highResolutionMultiplier] - 高清模式倍数，用于导出高清图片（默认 1）
 * @param {number} [options.scale=1] - 缩放比例，用于将 layerInfo 坐标从 displayWidth 缩放到当前 canvas 尺寸（默认 1）
 * @param {number} [options.offsetX=0] - 绘制坐标 X 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.offsetY=0] - 绘制坐标 Y 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.renderWidth] - 透明网格渲染宽度，不传则使用 Fabric canvas 宽度
 * @param {number} [options.renderHeight] - 透明网格渲染高度，不传则使用 Fabric canvas 高度
 * @param {boolean} [options.injectTransparentGrid=true] - 是否自动注入透明网格底图（仅 UI 展示，不参与导出）
 * @param {boolean} [options.offscreen=false] - 离屏模式：内部自建 StaticCanvas 渲染并导出 PNG dataURL，完成后自动 dispose；为 true 时 fabricCanvas 参数被忽略，canvasInfo 必传
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许绘制烫色专用图层
 * @param {Object} [options.callbacks] - 自定义图层控件配置与回调
 * @param {Object} [options.callbacks.userImageLayerControls] - 用户图片图层控件配置
 * @returns {Promise<Object|string>} offscreen=false 返回 layerId 到 Fabric 对象的映射；offscreen=true 返回导出 PNG 的 dataURL 字符串
 */
export async function loadLayersToFabricCanvas(options) {
  const {
    layers,
    canvasInfo,
    clearCanvas = true,
    selectFirst = false,
    selectedLayerId,
    canEdit,
    drawWhiteInkOnly = false,
    highResolutionMultiplier = 1,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight,
    injectTransparentGrid = true,
    offscreen = false,
    callbacks,
    includeStampingLayers = false
  } = options;

  if (!layers || !Array.isArray(layers)) {
    throw new Error('loadLayersToFabricCanvas: layers 参数必须是一个数组');
  }

  // offscreen=true：内部自建离屏 StaticCanvas，渲染后导出 dataURL 并 dispose
  let fabricCanvas = options.fabricCanvas;
  let internalOffscreenCanvas = null;
  if (offscreen) {
    if (
      !canvasInfo ||
      typeof canvasInfo.displayWidth !== 'number' ||
      typeof canvasInfo.displayHeight !== 'number' ||
      !(canvasInfo.displayWidth > 0) ||
      !(canvasInfo.displayHeight > 0)
    ) {
      throw new Error(
        'loadLayersToFabricCanvas: offscreen=true 时 canvasInfo.displayWidth/displayHeight 必须是合法正数'
      );
    }
    internalOffscreenCanvas = createOffscreenStaticCanvas({
      width: canvasInfo.displayWidth,
      height: canvasInfo.displayHeight
    });
    fabricCanvas = internalOffscreenCanvas;
  }

  if (!fabricCanvas) {
    throw new Error('loadLayersToFabricCanvas: fabricCanvas 参数不能为空');
  }

  // 将回调注册到 fabricCanvas 上，供 deleteObjectHandler/copyObjectHandler 读取
  // 必须在加载图层之前注册，因为 handler 在用户点击控件时从 canvas._layerControlCallbacks 取回调
  if (callbacks) {
    fabricCanvas._layerControlCallbacks = callbacks;
  }

  const layerObjects = {};
  const layerObjectList = [];

  // 透明网格底图（仅 UI 展示）
  // - 只有在提供 canvasInfo 且注入开关开启时才会注入
  // - drawWhiteInkOnly（导出白墨）时不注入，避免影响导出
  let renderLayers = layers;
  if (injectTransparentGrid && drawWhiteInkOnly !== true) {
    renderLayers = ensureTransparentGridLayer(renderLayers, canvasInfo);
  }

  // 按 zIndex 排序 layers
  let sortedLayers = sortLayersByZIndex(renderLayers).filter((layer) =>
    shouldRenderLayerInCommonDesign(layer, { includeStampingLayers })
  );
  if (includeStampingLayers && drawWhiteInkOnly !== true) {
    const stampingOverlayLayers = sortedLayers.filter((layer) =>
      isStampingOverlayRenderLayer(layer)
    );
    const stampingEffectOverlayLayers = stampingOverlayLayers.filter(
      (layer) => !isUploadStampingOverlayRenderLayer(layer)
    );
    const uploadStampingOverlayLayers = stampingOverlayLayers.filter((layer) =>
      isUploadStampingOverlayRenderLayer(layer)
    );
    sortedLayers = sortedLayers
      .filter((layer) => !isStampingOverlayRenderLayer(layer))
      .concat(stampingEffectOverlayLayers)
      .concat(uploadStampingOverlayLayers);
  }

  // 如果只绘制白墨图层，筛选并替换 imageUrl
  if (drawWhiteInkOnly) {
    // 先收集需要转换的图层和对应的转换 Promise
    const layersToProcess = [];
    const whiteInkPromises = [];

    for (let i = 0; i < sortedLayers.length; i++) {
      const layer = sortedLayers[i];
      // 筛选出非清空白墨的图层（兼容历史清空值）
      // 默认白墨派生需要原图来源，已有独立白墨时可直接使用 whiteInk。
      if (
        layer &&
        !constant.isClearWhiteInkValue(layer.whiteInk) &&
        !noWhiteLayerType.includes(layer.type) &&
        (layer.whiteInk || hasLayerImageSource(layer))
      ) {
        layersToProcess.push(layer);
        // 如果 whiteInk=''，需要转换；否则直接使用
        if (!layer.whiteInk) {
          whiteInkPromises.push(
            resolveLayerImageUrl(layer).then((imageUrl) =>
              imageToWhiteInk(imageUrl)
            )
          );
        } else {
          whiteInkPromises.push(Promise.resolve(layer.whiteInk));
        }
      }
    }

    // 并发执行所有转换
    const whiteInkUrls = await Promise.all(whiteInkPromises);

    // 使用生成好的结果构建 whiteInkLayers
    const whiteInkLayers = [];
    for (let i = 0; i < layersToProcess.length; i++) {
      whiteInkLayers.push({
        ...layersToProcess[i],
        imageUrl: whiteInkUrls[i]
      });
    }

    sortedLayers = whiteInkLayers;
  }

  // 找到第一个可编辑的 layer
  let firstEditableLayer = null;

  // 预加载阶段：创建所有 Fabric 对象
  // - clearCanvas=true：保持旧画面不变，等全部对象 ready 后再一次性替换，避免空白帧
  // - clearCanvas=false：允许在现有画面基础上追加绘制
  const prevRenderOnAddRemove = fabricCanvas.renderOnAddRemove;
  const shouldRestoreRenderOnAddRemove =
    typeof prevRenderOnAddRemove === 'boolean' ||
    prevRenderOnAddRemove === undefined;
  try {
    if (prevRenderOnAddRemove !== false) {
      fabricCanvas.renderOnAddRemove = false;
    }

    // 依次加载每个 layer
    for (let i = 0; i < sortedLayers.length; i++) {
      const layer = sortedLayers[i];
      // 缺少主图来源的图层无法加载到 Fabric。
      if (!hasLayerImageSource(layer)) {
        continue;
      }

      try {
        // 如果传入了 selectedLayerId，只有该图层可编辑，其他图层强制不可编辑
        // 如果没有传入 selectedLayerId，使用原来的 canEdit 逻辑
        let layerCanEdit;
        if (selectedLayerId) {
          // 传入了 selectedLayerId：只有该图层可编辑，其他强制不可编辑
          layerCanEdit = layer.id === selectedLayerId ? true : false;
        } else {
          // 没有传入 selectedLayerId：使用原来的逻辑
          layerCanEdit = canEdit;
        }

        const fabricObj = await loadSingleLayerToFabricCanvas({
          fabricCanvas,
          layer,
          canEdit: layerCanEdit,
          highResolutionMultiplier,
          scale,
          offsetX,
          offsetY,
          renderWidth,
          renderHeight,
          skipImageDataResolve: drawWhiteInkOnly,
          callbacks
        });

        layerObjects[layer.id] = fabricObj;
        layerObjectList.push(fabricObj);

        // clearCanvas=false：追加绘制（保持原有行为：在现有 canvas 上叠加）
        if (clearCanvas !== true) {
          fabricCanvas.add(fabricObj);
        }

        // 记录第一个可编辑的 layer（如果全局 canEdit 为 false，则不记录）
        if (
          !firstEditableLayer &&
          canEdit !== false &&
          layer.canEditImage !== false
        ) {
          firstEditableLayer = layer;
        }
      } catch (error) {
        console.error(`加载 layer ${layer.id} 失败:`, error);
      }
    }

    // clearCanvas=true：原子替换（remove 旧对象 + add 新对象 + 最终一次 renderAll）
    if (clearCanvas === true) {
      try {
        if (typeof fabricCanvas.discardActiveObject === 'function') {
          fabricCanvas.discardActiveObject();
        }

        const oldObjects =
          typeof fabricCanvas.getObjects === 'function'
            ? fabricCanvas.getObjects()
            : [];
        const oldObjectList =
          oldObjects && typeof oldObjects.slice === 'function'
            ? oldObjects.slice()
            : [];
        for (let i = 0; i < oldObjectList.length; i++) {
          fabricCanvas.remove(oldObjectList[i]);
          disposeFabricObject(oldObjectList[i]);
        }

        for (let i = 0; i < layerObjectList.length; i++) {
          fabricCanvas.add(layerObjectList[i]);
        }
      } catch (error) {
        console.error('loadLayersToFabricCanvas: 原子替换失败', error);
        throw error;
      }
    }
  } finally {
    // 恢复 renderOnAddRemove（避免中途异常导致后续画布不再自动重绘）
    if (shouldRestoreRenderOnAddRemove) {
      if (typeof prevRenderOnAddRemove === 'boolean') {
        fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
      } else if (prevRenderOnAddRemove === undefined) {
        // Fabric 默认行为基本等同于 true
        fabricCanvas.renderOnAddRemove = true;
      } else {
        fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
      }
    }
  }

  // 选中对象：优先使用 selectedLayerId，如果没有则使用 selectFirst
  if (selectedLayerId && layerObjects[selectedLayerId]) {
    fabricCanvas.setActiveObject(layerObjects[selectedLayerId]);
  } else if (
    selectFirst &&
    firstEditableLayer &&
    layerObjects[firstEditableLayer.id]
  ) {
    fabricCanvas.setActiveObject(layerObjects[firstEditableLayer.id]);
  }

  // 最终渲染 canvas
  fabricCanvas.renderAll();

  // offscreen 模式：导出 PNG dataURL 后销毁内部离屏画布，返回 dataURL
  if (offscreen && internalOffscreenCanvas) {
    try {
      const dataURL = internalOffscreenCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: highResolutionMultiplier
      });
      return dataURL;
    } finally {
      try {
        internalOffscreenCanvas.dispose();
      } catch (e) {
        /* noop */
      }
    }
  }

  return layerObjects;
}

/**
 * 判断图片地址是否为 SVG。
 * @param {string} imageUrl - 图片地址。
 * @returns {boolean} 是否为 SVG 地址。
 */
function isSvgImageUrl(imageUrl) {
  const url = String(imageUrl || '')
    .trim()
    .toLowerCase();
  if (!url) {
    return false;
  }
  if (url.indexOf('data:image/svg+xml') === 0) {
    return true;
  }
  const cleanUrl = url.split('?')[0].split('#')[0];
  return cleanUrl.endsWith('.svg');
}

/**
 * 使用 Fabric SVG 解析能力加载 SVG 素材。
 * @param {string} imageUrl - SVG 图片地址。
 * @returns {Promise<fabric.Object>} SVG 对应的 Fabric 对象。
 */
async function loadSvgFabricObjectFromUrl(imageUrl) {
  const svgResult = await loadSVGFromURL(imageUrl, undefined, {
    crossOrigin: 'anonymous'
  });
  const objects = (svgResult && svgResult.objects) || [];
  if (!objects.length) {
    throw new Error('loadSvgFabricObjectFromUrl: SVG objects 为空');
  }
  const svgObject = util.groupSVGElements(objects, svgResult.options || {});
  svgObject.set('_fitLayerInfoSize', true);
  return svgObject;
}

/**
 * 根据图片地址类型加载 Fabric 对象。
 * @param {string} imageUrl - 图片地址。
 * @returns {Promise<fabric.Object>} Fabric 对象。
 */
async function loadFabricObjectFromImageUrl(imageUrl) {
  // SVG 地址使用 Fabric 的 SVG 解析流程，保留矢量绘制行为。
  if (isSvgImageUrl(imageUrl)) {
    try {
      return await loadSvgFabricObjectFromUrl(imageUrl);
    } catch (error) {
      console.error('loadFabricObjectFromImageUrl: SVG 加载失败', error);
      if (mwx.isDev) {
        mwx.Toast('SVG素材加载失败');
        throw error;
      }
    }
  }
  return await FabricImage.fromURL(imageUrl, {
    crossOrigin: 'anonymous'
  });
}

/**
 * 加载单个 layer 到 Fabric canvas
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Object} options.layer - layer 对象
 * @param {boolean} [options.canEdit] - 全局禁用编辑模式，如果为 false 会覆盖局部的 canEdit
 * @param {number} [options.highResolutionMultiplier] - 高清模式倍数，用于导出高清图片（默认 1，建议 2-3）
 * @param {number} [options.scale=1]
 * @param {number} [options.offsetX=0] - 绘制坐标 X 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.offsetY=0] - 绘制坐标 Y 偏移（仅 UI 外扩画布场景使用）
 * @param {number} [options.renderWidth] - 透明网格渲染宽度
 * @param {number} [options.renderHeight] - 透明网格渲染高度
 * @param {boolean} [options.skipImageDataResolve=false] - 是否跳过 imageData 合成
 * @param {Object} [options.callbacks] - 自定义图层控件配置与回调
 * @returns {Promise<fabric.Object>} Fabric 对象
 */
export async function loadSingleLayerToFabricCanvas(options) {
  const {
    fabricCanvas,
    layer,
    canEdit,
    highResolutionMultiplier,
    scale = 1,
    offsetX = 0,
    offsetY = 0,
    renderWidth,
    renderHeight,
    skipImageDataResolve = false,
    callbacks
  } = options;

  if (!fabricCanvas) {
    throw new Error('loadSingleLayerToFabricCanvas: fabricCanvas 参数不能为空');
  }

  const hasImageSource = skipImageDataResolve
    ? !!(layer && layer.imageUrl)
    : hasLayerImageSource(layer);
  // 跳过 imageData 解析时，调用方必须已经把 imageUrl 替换为可直接绘制的图片。
  if (!hasImageSource) {
    throw new Error('loadSingleLayerToFabricCanvas: layer 参数无效');
  }

  // 透明网格底图：使用 Pattern + Rect 平铺绘制（不参与导出）
  if (layer.type === constant.layer_transparentGrid) {
    return await loadTransparentGridLayerToFabricCanvas({
      fabricCanvas,
      layer,
      scale,
      offsetX,
      offsetY,
      renderWidth,
      renderHeight
    });
  }

  const resolvedImageUrl = skipImageDataResolve
    ? layer.imageUrl
    : await resolveLayerImageUrl(layer);
  let imageUrlToLoad = resolvedImageUrl;
  // 背景图层默认做灰度转 alpha（同色卡纸需要）；
  // layer.skipGrayToAlpha=true 时跳过（sticker 背景图片场景，保持图片原色不透明）
  const skuTemplate = mwx.Store.get(mwx.constant.skuTemplate) || {};
  const skipBackgroundGrayToAlpha = shouldSkipBackgroundGrayToAlpha(
    layer,
    skuTemplate
  );
  // 未命中跳过规则时，背景图层需要做灰度转 alpha。
  if (!skipBackgroundGrayToAlpha) {
    imageUrlToLoad = await convertGrayToAlphaForBackground(layer.imageUrl);
    // mwx.debugLog({
    //   title: 'convertGrayToAlphaForBackground',
    //   imageUrlToLoad,
    //   layerImage: layer.imageUrl
    // });
  }

  let timeout = null;
  try {
    const img = await loadFabricObjectFromImageUrl(imageUrlToLoad);

    const layerInfo = layer.layerInfo || {};
    // 处理 canEdit 参数：
    // - canEdit === true: 强制可编辑（覆盖 layer.canEditImage）
    // - canEdit === false: 强制不可编辑（覆盖 layer.canEditImage）
    // - canEdit === undefined: 使用 layer.canEditImage 的默认值
    let finalCanEdit;
    if (canEdit === true) {
      finalCanEdit = true; // 强制可编辑
    } else if (canEdit === false) {
      finalCanEdit = false; // 强制不可编辑
    } else {
      finalCanEdit = layer.canEditImage !== false; // 使用layer的默认值
    }
    const isLockedTemplateLayer =
      layer.type === constant.layer_template ||
      layer.type === constant.layer_stampingTemplate;

    const shouldFitTextImageDataLayerInfo =
      (layer.canvasType === 'textbox' && !!layer.imageData) ||
      layer.hasTextImageData === true;
    const fitLayerInfoSize =
      layer.fitLayerInfoSize === true ||
      img._fitLayerInfoSize === true ||
      shouldFitTextImageDataLayerInfo;
    let renderScaleX = layerInfo.scaleX;
    let renderScaleY = layerInfo.scaleY;
    // 临时预览图或文字 imageData 还原图尺寸与原图尺寸不一致时，按 layerInfo 目标尺寸适配显示。
    if (
      fitLayerInfoSize &&
      img.width &&
      img.height &&
      layerInfo.width &&
      layerInfo.height
    ) {
      renderScaleX = (layerInfo.width * (layerInfo.scaleX || 1)) / img.width;
      renderScaleY = (layerInfo.height * (layerInfo.scaleY || 1)) / img.height;
    }

    // 读取透明度（从 layer.opacity 读取，默认为 1）
    const opacity = typeof layer.opacity === 'number' ? layer.opacity : 1;
    // debugger;
    // 应用 scale 缩放坐标（layerInfo 基于 displayWidth，需要缩放到当前 canvas 尺寸）
    img.set({
      left: layerInfo.left + offsetX,
      top: layerInfo.top + offsetY,
      scaleX: renderScaleX,
      scaleY: renderScaleY,
      angle: layerInfo.angle || 0,
      originX: layerInfo.originX || 'left',
      originY: layerInfo.originY || 'top',
      opacity: opacity, // 设置透明度
      selectable: finalCanEdit,
      hasControls: finalCanEdit,
      hasBorders: finalCanEdit,
      evented: finalCanEdit,
      lockMovementX: !finalCanEdit || isLockedTemplateLayer,
      lockMovementY: !finalCanEdit || isLockedTemplateLayer,
      lockRotation: !finalCanEdit || isLockedTemplateLayer,
      lockScalingX: !finalCanEdit || isLockedTemplateLayer,
      lockScalingY: !finalCanEdit || isLockedTemplateLayer,
      lockUniScaling: true, // 锁定统一缩放，强制保持宽高比不变
      // editImageNew：缩放以对角点为锚点（类似 PS 默认缩放行为）
      centeredScaling: false
    });

    // editImageNew：可编辑对象定制 controls（左上删除、右上复制、右下缩放）
    // 注意：applyLayerCustomControls 在下方统一调用（带 callbacks），此处不再单独调用

    // 存储 layerId
    img.set('layerId', layer.id);
    img.set('strokeWidth', layer.strokeWidth || 0);
    // 存储高清倍数，用于导出时使用
    if (highResolutionMultiplier && highResolutionMultiplier > 1) {
      img.set('highResolutionMultiplier', highResolutionMultiplier);
    }

    // 更新对象的交互边界（必须在设置属性后调用，否则拖拽等操作无法正常工作）
    img.setCoords();

    // 可编辑对象定制 controls + 手势（在 layerId 设置之后调用，确保 handler 能拿到 layerId）
    if (finalCanEdit === true) {
      // 贴纸和素材都使用同一套图层编辑控件。
      if (
        layer.isSticker ||
        layer.type === constant.layer_skuMaterial ||
        layer.type === constant.layer_stampingMaterial ||
        layer.type === constant.layer_stampingTemplate ||
        layer.type === constant.layer_template
      ) {
        // 使用图层自定义操作手柄。
        applyLayerCustomControls(img, callbacks, {
          showCopyControl:
            layer.type !== constant.layer_template &&
            layer.type !== constant.layer_stampingTemplate,
          showScaleControl:
            layer.type !== constant.layer_template &&
            layer.type !== constant.layer_stampingTemplate,
          showEditTextControl:
            (layer.type === constant.layer_skuMaterial ||
              layer.type === constant.layer_template ||
              layer.type === constant.layer_stampingMaterial ||
              layer.type === constant.layer_stampingTemplate) &&
            (layer.canvasType === 'textbox' || layer.hasTextImageData === true)
        });
      } else if (layer.type === constant.layer_userImage) {
        // 原图使用素材同款编辑框，但不提供删除和 +1 控件。
        const userImageLayerControls =
          callbacks && callbacks.userImageLayerControls
            ? callbacks.userImageLayerControls
            : {};
        applyLayerCustomControls(img, callbacks, {
          showDeleteControl: userImageLayerControls.showDeleteControl === true,
          showCopyControl: userImageLayerControls.showCopyControl === true
        });
      } else {
        applyEditImageCornerControls(img); // 原逻辑
      }
      // 可编辑对象出现时，确保双指缩放/旋转/平移手势处于开启状态（默认即为 true）
      setActiveObjectPinchGestureEnabled(true);
    }

    // 注意：这里不再 add/render，由调用方统一处理，避免 clearCanvas 时出现空白帧与频繁重绘
    return img;
  } catch (error) {
    throw error;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

/**
 * 使用 Fabric.js 加载包含白墨的图层并合成
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {Array} options.layers - layer 数组
 * @param {Object} options.canvasInfo - 画布信息对象（包含 displayWidth 和 displayHeight）
 * @param {number} [options.opacity] - 非白墨区域的透明度（可选，默认从 config.imageWithWhiteInkDefaultOpacity 获取）
 * @param {boolean} [options.includeStampingLayers=false] - 是否允许绘制烫色专用图层
 * @param {number} options.whiteMode - SKU 默认白墨模式
 * @returns {Promise<void>}
 */
export async function loadLayersToFabricCanvasWithWhiteInk(options) {
  const {
    fabricCanvas,
    layers,
    canvasInfo,
    opacity,
    highResolutionMultiplier,
    injectTransparentGrid = true,
    imageOpacity,
    includeStampingLayers = false,
    whiteMode
  } = options;

  if (!fabricCanvas) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: fabricCanvas 参数不能为空'
    );
  }

  if (!layers || !Array.isArray(layers)) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: layers 参数必须是一个数组'
    );
  }

  if (
    !canvasInfo ||
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    throw new Error(
      'loadLayersToFabricCanvasWithWhiteInk: canvasInfo 参数必须包含 displayWidth 和 displayHeight'
    );
  }

  // whiteMode 仅保留为合法性校验，未设置 whiteInk 的图层统一按默认有白墨处理。
  if (
    ![
      constant.whiteModeMap.NO_BUTTON,
      constant.whiteModeMap.DEFAULT,
      constant.whiteModeMap.EMPTY
    ].includes(whiteMode)
  ) {
    throw new Error('loadLayersToFabricCanvasWithWhiteInk: whiteMode 参数无效');
  }

  // 使用传入的 opacity 或默认值
  const finalOpacity =
    typeof opacity === 'number' ? opacity : imageWithWhiteInkDefaultOpacity;
  let offscreenCanvas = null;

  try {
    // 透明网格底图（仅 UI 展示）
    // - 只有在注入开关开启时才会注入
    let layersWithTransparentGrid = layers;
    if (injectTransparentGrid) {
      layersWithTransparentGrid = ensureTransparentGridLayer(
        layers,
        canvasInfo
      );
    }

    // 检查是否存在用户自定义白墨图层
    const hasCustomWhiteInkLayers = layersWithTransparentGrid.some(
      (layer) =>
        layer &&
        !noWhiteLayerType.includes(layer.type) &&
        layer.whiteInk &&
        !constant.isClearWhiteInkValue(layer.whiteInk) &&
        !constant.isDefaultWhiteInkValue(layer.whiteInk)
    );

    // 检查是否存在默认或清空白墨图层，统一交给最终白墨导出器按覆盖关系处理。
    const hasDefaultWhiteInkLayers = layersWithTransparentGrid.some(
      (layer) =>
        layer &&
        !noWhiteLayerType.includes(layer.type) &&
        (constant.isDefaultWhiteInkValue(layer.whiteInk) ||
          constant.isClearWhiteInkValue(layer.whiteInk)) &&
        hasLayerImageSource(layer)
    );
    const hasWhiteInkLayers =
      hasCustomWhiteInkLayers || hasDefaultWhiteInkLayers;

    if (hasWhiteInkLayers) {
      const stampingOverlayLayers = includeStampingLayers
        ? layersWithTransparentGrid.filter((layer) =>
            isStampingOverlayRenderLayer(layer)
          )
        : [];
      const stampingEffectOverlayLayers = stampingOverlayLayers.filter(
        (layer) => !isUploadStampingOverlayRenderLayer(layer)
      );
      const uploadStampingOverlayLayers = stampingOverlayLayers.filter(
        (layer) => isUploadStampingOverlayRenderLayer(layer)
      );
      const whiteInkCompositeLayers = includeStampingLayers
        ? layersWithTransparentGrid.filter(
            (layer) => !isStampingOverlayRenderLayer(layer)
          )
        : layersWithTransparentGrid;
      // 离屏 StaticCanvas：用于导出 nonBackground / whiteInk / userImageWhiteInk
      // 说明：主画布属于 UI 展示，不应在中间导出步骤被临时替换成白墨图等中间态
      offscreenCanvas = createOffscreenStaticCanvas({
        width: canvasInfo.displayWidth,
        height: canvasInfo.displayHeight
      });

      // 第一步：绘制不包含背景图的图层
      const nonBackgroundLayers = whiteInkCompositeLayers.filter(
        (layer) => layer && layer.type !== constant.layer_transparentGrid
      );

      //
      await loadLayersToFabricCanvas({
        fabricCanvas: offscreenCanvas,
        layers: nonBackgroundLayers,
        canvasInfo: canvasInfo,
        clearCanvas: true,
        selectFirst: false,
        canEdit: false,
        highResolutionMultiplier,
        injectTransparentGrid: false,
        includeStampingLayers: false
      });

      // 导出不包含背景图的图层为图片
      const nonBackgroundImageDataURL = offscreenCanvas.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: highResolutionMultiplier
      });

      const whiteInkImageSrcFromComposite = await exportFinalWhiteInk({
        layers: whiteInkCompositeLayers,
        canvasInfo,
        highResolutionMultiplier,
        includeStampingLayers: false,
        whiteMode
      });
      let whiteInkImageSrc = whiteInkImageSrcFromComposite;

      // 没有实际白墨输出时生成同画布尺寸的纯透明图，避免后续合成逻辑消费空字符串。
      if (!whiteInkImageSrc) {
        whiteInkImageSrc = await getTransparentBlobUrlBySize({
          width: canvasInfo.displayWidth,
          height: canvasInfo.displayHeight
        });
      }

      // 第三步：合成两张图片
      let compositeImageDataURL = await canvasUtil.drawImageWithGrayAlpha({
        whiteInkImageSrc,
        outlineImageSrc: nonBackgroundImageDataURL,
        canvasWidth: canvasInfo.displayWidth,
        canvasHeight: canvasInfo.displayHeight,
        opacity: imageOpacity,
        exportImage: true
      });

      // if (imageOpacity !== undefined) {
      //   compositeImageDataURL = await canvasUtil.setImageOpacity({
      //     imageSrc: compositeImageDataURL,
      //     opacity: imageOpacity
      //   });
      // }

      mwx.debugLog({
        title: '白墨合成',
        whiteInkImageSrcFromComposite,
        compositeImageDataURL
      });

      // 第四步：预加载下一帧对象（透明网格 + 背景 + 合成图），全部 ready 后再原子替换
      // - 避免中途 clear() 造成空白帧闪屏
      // - 若合成图加载失败/超时：抛错并保持旧画面不变（由上层提示）

      // 最底层：透明网格底图（UI 展示用，不参与导出合成）
      const transparentGridLayers = layersWithTransparentGrid.filter((layer) =>
        isTransparentGridLayer(layer)
      );
      const transparentGridObjects = await Promise.all(
        transparentGridLayers.map((layer) => {
          return loadSingleLayerToFabricCanvas({
            fabricCanvas: fabricCanvas,
            layer: layer,
            canEdit: false,
            highResolutionMultiplier,
            scale: 1
          });
        })
      );

      // 背景图层
      // const backgroundLayers = layersWithTransparentGrid.filter(
      //   (layer) => layer && layer.type === constant.layer_background
      // );
      // const backgroundObjects = await Promise.all(
      //   backgroundLayers.map((layer) => {
      //     return loadSingleLayerToFabricCanvas({
      //       fabricCanvas: fabricCanvas,
      //       layer: layer,
      //       canEdit: false,
      //       highResolutionMultiplier,
      //       scale: 1
      //     });
      //   })
      // );

      // 合成图层（在背景之上）
      const compositeObject = await new Promise((resolve, reject) => {
        let timeout = setTimeout(() => {
          reject(new Error('合成图片加载超时'));
        }, 10000);

        FabricImage.fromURL(compositeImageDataURL, {
          crossOrigin: 'anonymous'
        })
          .then((img) => {
            if (timeout) {
              clearTimeout(timeout);
              timeout = null;
            }
            try {
              // 计算缩放比例，使图片填满画布（保持与原逻辑一致：按 displayWidth/displayHeight）
              const scaleX = canvasInfo.displayWidth / img.width;
              const scaleY = canvasInfo.displayHeight / img.height;

              img.set({
                left: 0,
                top: 0,
                scaleX: scaleX,
                scaleY: scaleY,
                angle: 0,
                originX: 'left',
                originY: 'top',
                selectable: false,
                hasControls: false,
                hasBorders: false,
                evented: false
              });
              resolve(img);
            } catch (error) {
              reject(error);
            }
          })
          .catch((error) => {
            if (timeout) {
              clearTimeout(timeout);
              timeout = null;
            }
            reject(error);
          });
      });

      const stampingEffectOverlayObjects = await Promise.all(
        stampingEffectOverlayLayers.map((layer) => {
          return loadSingleLayerToFabricCanvas({
            fabricCanvas: fabricCanvas,
            layer: layer,
            canEdit: false,
            highResolutionMultiplier,
            scale: 1
          });
        })
      );
      const uploadStampingOverlayObjects = await Promise.all(
        uploadStampingOverlayLayers.map((layer) => {
          return loadSingleLayerToFabricCanvas({
            fabricCanvas: fabricCanvas,
            layer: layer,
            canEdit: false,
            highResolutionMultiplier,
            scale: 1
          });
        })
      );

      const nextObjectList = []
        .concat(transparentGridObjects)
        // .concat(backgroundObjects)
        .concat([compositeObject])
        .concat(stampingEffectOverlayObjects)
        .concat(uploadStampingOverlayObjects);

      // 原子替换（remove 旧对象 + add 新对象 + 最终一次 renderAll）
      const prevRenderOnAddRemove = fabricCanvas.renderOnAddRemove;
      const shouldRestoreRenderOnAddRemove =
        typeof prevRenderOnAddRemove === 'boolean' ||
        prevRenderOnAddRemove === undefined;
      try {
        if (prevRenderOnAddRemove !== false) {
          fabricCanvas.renderOnAddRemove = false;
        }

        if (typeof fabricCanvas.discardActiveObject === 'function') {
          fabricCanvas.discardActiveObject();
        }

        const oldObjects =
          typeof fabricCanvas.getObjects === 'function'
            ? fabricCanvas.getObjects()
            : [];
        const oldObjectList =
          oldObjects && typeof oldObjects.slice === 'function'
            ? oldObjects.slice()
            : [];
        for (let i = 0; i < oldObjectList.length; i++) {
          fabricCanvas.remove(oldObjectList[i]);
          disposeFabricObject(oldObjectList[i]);
        }

        for (let i = 0; i < nextObjectList.length; i++) {
          fabricCanvas.add(nextObjectList[i]);
        }
      } finally {
        // 恢复 renderOnAddRemove（避免中途异常导致后续画布不再自动重绘）
        if (shouldRestoreRenderOnAddRemove) {
          if (typeof prevRenderOnAddRemove === 'boolean') {
            fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
          } else if (prevRenderOnAddRemove === undefined) {
            fabricCanvas.renderOnAddRemove = true;
          } else {
            fabricCanvas.renderOnAddRemove = prevRenderOnAddRemove;
          }
        }
      }

      // 最终渲染
      fabricCanvas.renderAll();
    } else {
      // 如果没有白墨图层，使用原有逻辑
      // 第一步：绘制所有图层
      await loadLayersToFabricCanvas({
        fabricCanvas: fabricCanvas,
        layers: layersWithTransparentGrid,
        canvasInfo: canvasInfo,
        clearCanvas: true,
        selectFirst: false,
        canEdit: false,
        highResolutionMultiplier,
        injectTransparentGrid: false,
        includeStampingLayers
      });

      // 此时 fabricCanvas 已经包含了所有图层，无需额外处理
    }
  } catch (error) {
    console.error('loadLayersToFabricCanvasWithWhiteInk: 处理失败', error);
    throw error;
  } finally {
    try {
      // 只释放本函数内部创建的离屏画布，不处理外部传入的 fabricCanvas。
      if (offscreenCanvas && typeof offscreenCanvas.dispose === 'function') {
        offscreenCanvas.dispose();
      }
    } catch (error) {
      console.error(
        'loadLayersToFabricCanvasWithWhiteInk: offscreenCanvas 释放失败',
        error
      );
    }
  }
}

/** 反面非透明但白墨侧透明：占反面非透明像素比例超过此值（0–1）则阻断 */
export const REVERSE_WHITEINK_MISMATCH_RATIO_THRESHOLD = 0.05;
/** 反面非透明但白墨侧透明：像素数超过此值则阻断 */
export const REVERSE_WHITEINK_MISMATCH_PIXEL_THRESHOLD = 50000; // 暂时不用

/**
 * 将 dataURL 缩放到指定尺寸并返回 ImageData；空字符串视为全透明
 * @param {string} dataURL
 * @param {number} expectW
 * @param {number} expectH
 * @returns {Promise<ImageData>}
 */
function dataURLToImageDataForCompare(dataURL, expectW, expectH) {
  return new Promise(function (resolve, reject) {
    if (typeof document === 'undefined') {
      reject(new Error('dataURLToImageDataForCompare: document 不存在'));
      return;
    }
    const w = Math.max(0, Math.round(expectW));
    const h = Math.max(0, Math.round(expectH));
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      canvas.width = 0;
      canvas.height = 0;
      reject(new Error('dataURLToImageDataForCompare: 无法获取 2d context'));
      return;
    }
    if (!dataURL || dataURL === '') {
      try {
        const imageData = ctx.getImageData(0, 0, w, h);
        canvas.width = 0;
        canvas.height = 0;
        resolve(imageData);
      } catch (e) {
        canvas.width = 0;
        canvas.height = 0;
        reject(e);
      }
      return;
    }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = function () {
      try {
        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        canvas.width = 0;
        canvas.height = 0;
        resolve(imageData);
      } catch (e) {
        canvas.width = 0;
        canvas.height = 0;
        reject(e);
      }
    };
    img.onerror = function (err) {
      canvas.width = 0;
      canvas.height = 0;
      reject(err || new Error('dataURLToImageDataForCompare: 图片加载失败'));
    };
    img.src = dataURL;
  });
}

/**
 * 反面合成图与白墨导出图同尺寸像素对比（alpha===0 为透明）。
 * 白墨导出使用 exportFinalWhiteInk + reverseCompareGate，未设置 whiteInk 时固定按默认有白墨处理。
 *
 * @param {Object} options
 * @param {Array} options.layers - 正面图层
 * @param {Array} [options.reverseLayer=[]] - 反面图层
 * @param {Object} options.canvasInfo - displayWidth/displayHeight
 * @param {number} [options.highResolutionMultiplier=1]
 * @param {boolean} [options.debug=false]
 * @param {number} [options.ratioThreshold]
 * @param {number} [options.pixelCountThreshold]
 * @param {string} [options.maskUrlFront] - 正面 mask（与 getMaskUrl 一致），有则对白墨导出图先裁剪再对比
 * @param {string} [options.maskUrlReverse] - 反面 mask（通常水平镜像），有则对反面导出图先裁剪再对比
 * @param {boolean} [options.excuteMaskClip=true]
 * @param {string} [options.bgColor] - 传给 applySkuTemplateMaskClipPath
 * @param {number} options.whiteMode - SKU 默认白墨模式
 * @returns {Promise<{mismatchCount:number,reverseOpaqueCount:number,ratio:number,blocked:boolean,reverseWithoutWhite:{mismatchCount:number,reverseOpaqueCount:number,ratio:number,blocked:boolean},whiteWithoutReverse:{mismatchCount:number,whiteOpaqueCount:number,ratio:number,blocked:boolean},error?:*,debugWhiteInkDataUrl?:string,debugReverseDataUrl?:string,debugReverseMismatchOverlayDataUrl?:string}>}
 */
export async function compareReverseLayerAgainstWhiteInk(options) {
  const opts = options || {};
  const layers = opts.layers;
  const reverseLayer = Array.isArray(opts.reverseLayer)
    ? opts.reverseLayer
    : [];
  const canvasInfo = opts.canvasInfo;
  const highResolutionMultiplier =
    typeof opts.highResolutionMultiplier === 'number'
      ? opts.highResolutionMultiplier
      : 1;
  const debug = opts.debug === true;
  const whiteMode = opts.whiteMode;
  const ratioThreshold =
    typeof opts.ratioThreshold === 'number'
      ? opts.ratioThreshold
      : REVERSE_WHITEINK_MISMATCH_RATIO_THRESHOLD;
  const pixelCountThreshold =
    typeof opts.pixelCountThreshold === 'number'
      ? opts.pixelCountThreshold
      : REVERSE_WHITEINK_MISMATCH_PIXEL_THRESHOLD;

  const createEmptyCheckResult = function (opaqueCountField) {
    const result = {
      mismatchCount: 0,
      ratio: 0,
      blocked: false
    };
    result[opaqueCountField] = 0;
    return result;
  };
  const createEmptyResult = function () {
    const reverseWithoutWhite = createEmptyCheckResult('reverseOpaqueCount');
    const whiteWithoutReverse = createEmptyCheckResult('whiteOpaqueCount');
    return {
      mismatchCount: 0,
      reverseOpaqueCount: 0,
      ratio: 0,
      blocked: false,
      reverseWithoutWhite: reverseWithoutWhite,
      whiteWithoutReverse: whiteWithoutReverse
    };
  };

  try {
    if (!layers || !Array.isArray(layers)) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: layers 参数必须是一个数组'
      );
    }
    if (
      !canvasInfo ||
      typeof canvasInfo.displayWidth !== 'number' ||
      typeof canvasInfo.displayHeight !== 'number'
    ) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: canvasInfo 须含 displayWidth/displayHeight'
      );
    }

    const whiteDataURL = await exportFinalWhiteInk({
      layers: layers,
      canvasInfo: canvasInfo,
      highResolutionMultiplier: highResolutionMultiplier,
      reverseCompareGate: true,
      whiteMode
    });

    let reverseDataURL = '';
    const offReverse = createOffscreenStaticCanvas({
      width: canvasInfo.displayWidth,
      height: canvasInfo.displayHeight
    });
    try {
      await loadLayersToFabricCanvas({
        fabricCanvas: offReverse,
        layers: reverseLayer,
        canvasInfo: canvasInfo,
        clearCanvas: true,
        selectFirst: false,
        canEdit: false,
        drawWhiteInkOnly: false,
        highResolutionMultiplier: highResolutionMultiplier,
        injectTransparentGrid: false
      });
      reverseDataURL = offReverse.toDataURL({
        format: 'png',
        quality: 1,
        multiplier: highResolutionMultiplier
      });
    } finally {
      try {
        // 只释放本函数内部创建的反面离屏画布。
        if (offReverse && typeof offReverse.dispose === 'function') {
          offReverse.dispose();
        }
      } catch (error) {
        console.error(
          'compareReverseLayerAgainstWhiteInk: offReverse 释放失败',
          error
        );
      }
    }

    const maskUrlFront =
      opts.maskUrlFront && typeof opts.maskUrlFront === 'string'
        ? opts.maskUrlFront
        : '';
    const maskUrlReverse =
      opts.maskUrlReverse && typeof opts.maskUrlReverse === 'string'
        ? opts.maskUrlReverse
        : '';
    const excuteMaskClip = opts.excuteMaskClip !== false;

    const clipCommon = {
      canvasInfo: canvasInfo,
      layers: layers,
      bgColor: opts.bgColor,
      highResolutionMultiplier: highResolutionMultiplier,
      excuteMaskClip: excuteMaskClip
    };

    let whiteForCompare = whiteDataURL;
    let reverseForCompare = reverseDataURL;
    if (maskUrlFront) {
      whiteForCompare = await applySkuMaskClipToDataURL(
        Object.assign({}, clipCommon, {
          dataURL: whiteDataURL,
          maskUrl: maskUrlFront
        })
      );
    }
    if (maskUrlReverse) {
      reverseForCompare = await applySkuMaskClipToDataURL(
        Object.assign({}, clipCommon, {
          dataURL: reverseDataURL,
          maskUrl: maskUrlReverse
        })
      );
    }

    reverseForCompare = await mirrorImageHorizontal(reverseForCompare);

    const wPx = Math.max(
      1,
      Math.round(canvasInfo.displayWidth * highResolutionMultiplier)
    );
    const hPx = Math.max(
      1,
      Math.round(canvasInfo.displayHeight * highResolutionMultiplier)
    );

    const whiteImageData = await dataURLToImageDataForCompare(
      whiteForCompare,
      wPx,
      hPx
    );
    const reverseImageData = await dataURLToImageDataForCompare(
      reverseForCompare,
      wPx,
      hPx
    );

    const dataW = whiteImageData.data;
    const dataR = reverseImageData.data;
    if (dataW.length !== dataR.length) {
      throw new Error(
        'compareReverseLayerAgainstWhiteInk: ImageData 长度不一致'
      );
    }

    const buildCheckResult = function (
      mismatchCount,
      baseOpaqueCount,
      opaqueCountField
    ) {
      let ratio = 0;
      if (baseOpaqueCount > 0) {
        ratio = mismatchCount / baseOpaqueCount;
      }

      let blocked = false;
      if (mismatchCount > pixelCountThreshold) {
        blocked = true;
      }
      if (baseOpaqueCount > 0 && ratio > ratioThreshold) {
        blocked = true;
      }

      const result = {
        mismatchCount: mismatchCount,
        ratio: ratio,
        blocked: blocked
      };
      result[opaqueCountField] = baseOpaqueCount;
      return result;
    };

    let reverseMismatchCount = 0;
    let reverseOpaqueCount = 0;
    let whiteMismatchCount = 0;
    let whiteOpaqueCount = 0;
    // 单次扫描同时统计两个方向，避免重复遍历 ImageData。
    for (let i = 0; i < dataR.length; i += 4) {
      const aw = dataW[i + 3];
      const ar = dataR[i + 3];

      if (ar !== 0) {
        reverseOpaqueCount++;
        if (aw === 0) {
          reverseMismatchCount++;
        }
      }
      if (aw !== 0) {
        whiteOpaqueCount++;
        if (ar === 0) {
          whiteMismatchCount++;
        }
      }
    }

    // 以反面非透明像素为基准，检查对应位置是否缺少白墨覆盖。
    const reverseWithoutWhite = buildCheckResult(
      reverseMismatchCount,
      reverseOpaqueCount,
      'reverseOpaqueCount'
    );
    // 以白墨非透明像素为基准，检查对应位置是否缺少反面图内容。
    const whiteWithoutReverse = buildCheckResult(
      whiteMismatchCount,
      whiteOpaqueCount,
      'whiteOpaqueCount'
    );

    const result = {
      blocked: reverseWithoutWhite.blocked || whiteWithoutReverse.blocked,
      reverseWithoutWhite: reverseWithoutWhite,
      whiteWithoutReverse: whiteWithoutReverse
    };
    if (debug) {
      result.debugWhiteInkDataUrl = whiteForCompare || '';
      result.debugReverseDataUrl = reverseForCompare;
    }
    return result;
  } catch (error) {
    console.error('compareReverseLayerAgainstWhiteInk: 处理失败', error);
    const out = createEmptyResult();
    out.error = error;
    out.blocked = false;
    return out;
  }
}

/**
 * 应用 skuTemplate 的 maskUrl 蒙版裁剪到 Fabric canvas
 * - 若存在 type=skuTemplate 且 maskUrl 非空：黑色区域裁剪为透明
 * - 若不存在或 maskUrl 为空：清空 fabricCanvas.clipPath，避免残留影响后续绘制
 *
 * 说明：
 * - 蒙版图片按画布尺寸直接拉伸铺满（0,0,canvasW,canvasH）
 * - 黑色阈值：亮度 < threshold 且 alpha > 0 时判定为“需要裁剪掉”
 * - 使用 fabricCanvas.clipPath 保留原有对象（不做扁平化）
 *
 * @param {Object} options
 * @param {fabric.Canvas} options.fabricCanvas
 * @param {Array} options.layers
 * @param {number} [options.threshold=16] 黑色亮度阈值（0-255）
 */
export async function applySkuTemplateMaskClipPath(options) {
  const { fabricCanvas, layers, threshold = 10 } = options || {};

  // fabricCanvas 是裁剪挂载目标，缺失时无法继续。
  if (!fabricCanvas) {
    throw new Error('applySkuTemplateMaskClipPath: fabricCanvas 参数不能为空');
  }
  // 空图层场景允许调用方清理裁剪后直接返回。
  if (!layers || !Array.isArray(layers)) {
    // 允许调用方在某些场景传空，直接清理即可
    clearFabricCanvasClipPath(fabricCanvas);
    fabricCanvas.renderAll();
    return;
  }

  // 找到 skuTemplate layer
  let skuTemplateLayer = null;
  for (let i = 0; i < layers.length; i++) {
    const layer = layers[i];
    if (layer && layer.type === constant.layer_template) {
      skuTemplateLayer = layer;
      break;
    }
  }

  // maskUrl: 裁剪区域为透明的,其余部分为黑色
  const maskUrl =
    skuTemplateLayer && typeof skuTemplateLayer.maskUrl === 'string'
      ? skuTemplateLayer.maskUrl
      : options.maskUrl || '';

  // 无 mask：清理裁剪
  if (!maskUrl) {
    // 画布存在旧裁剪时需要先释放旧 clipPath。
    if (fabricCanvas.clipPath) {
      clearFabricCanvasClipPath(fabricCanvas);
      fabricCanvas.renderAll();
    }
    return;
  }

  try {
    const canvasWidth =
      typeof fabricCanvas.getWidth === 'function'
        ? fabricCanvas.getWidth()
        : fabricCanvas.width;
    const canvasHeight =
      typeof fabricCanvas.getHeight === 'function'
        ? fabricCanvas.getHeight()
        : fabricCanvas.height;

    // canvas 尺寸无效时无法生成裁剪图。
    if (
      typeof canvasWidth !== 'number' ||
      canvasWidth <= 0 ||
      typeof canvasHeight !== 'number' ||
      canvasHeight <= 0
    ) {
      throw new Error(
        'applySkuTemplateMaskClipPath: canvasWidth/canvasHeight 无效'
      );
    }

    const dpr = getMaskClipDpr(canvasWidth, canvasHeight);
    const t = typeof threshold === 'number' ? threshold : 16;
    const maskClipCacheKey = createSkuTemplateMaskClipCacheKey({
      maskUrl,
      canvasWidth,
      canvasHeight,
      dpr,
      threshold: t
    });
    let maskBlobUrl = await getValidSkuTemplateMaskClipBlobUrlCache(
      maskClipCacheKey
    );

    // 无有效缓存时才重新生成 mask 裁剪图，避免重复像素处理。
    if (!maskBlobUrl) {
      // 生成 alpha 蒙版（黑色区域 alpha=0，其它 alpha=255）
      const maskImg = await canvasUtil.loadImage(maskUrl);

      canvasUtil.checkMaskProportion(
        maskImg.width,
        maskImg.height,
        canvasWidth,
        canvasHeight,
        'applySkuTemplateMaskClipPath'
      );

      const offscreenCanvas = document.createElement('canvas');
      const initRes = await canvasUtil.initCanvas({
        dom: offscreenCanvas,
        canvasWidth,
        canvasHeight,
        customDpr: dpr
      });

      const ctx = initRes && initRes.ctx;
      const canvasNode = initRes && initRes.canvasNode;

      // 离屏 canvas 初始化失败时停止裁剪，避免后续空指针。
      if (!ctx || !canvasNode) {
        throw new Error('applySkuTemplateMaskClipPath: 离屏 canvas 初始化失败');
      }

      // 按画布尺寸拉伸铺满
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      ctx.drawImage(maskImg, 0, 0, canvasWidth, canvasHeight);

      // initCanvas 会按 mask 专用 dpr 扩大真实像素尺寸。
      const actualWidth = canvasNode.width;
      const actualHeight = canvasNode.height;
      const actualPixelCount = actualWidth * actualHeight;

      // 降采样后真实像素宽高仍需有效。
      if (actualWidth <= 0 || actualHeight <= 0) {
        throw new Error('applySkuTemplateMaskClipPath: mask 真实像素尺寸无效');
      }

      // 像素保护兜底，避免异常尺寸绕过 DPR 限制。
      if (actualPixelCount > maskClipMaxPixels) {
        throw new Error('applySkuTemplateMaskClipPath: mask 像素尺寸超过限制');
      }

      const imageData = ctx.getImageData(0, 0, actualWidth, actualHeight);
      const data = imageData.data;

      for (let i = 0; i < data.length; i += 4) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];

        const brightness = Math.floor((r + g + b) / 3);
        // 规则：
        // - mask 透明像素(alpha=0)：视为保留（不裁剪）
        // - mask 非透明像素且接近黑色：裁剪（透明）
        const isBlack = a > 10 && brightness < t;

        // clipPath 使用 alpha 判断；白色不透明、黑色透明
        data[i] = 255;
        data[i + 1] = 255;
        data[i + 2] = 255;
        data[i + 3] = isBlack ? 0 : 255;
      }

      // putImageData 不受 transform 影响，直接写入实际像素
      ctx.putImageData(imageData, 0, 0);

      maskBlobUrl = await canvasUtil.canvasToBlobUrl(canvasNode, 'image/png');
      setSkuTemplateMaskClipBlobUrlCache(maskClipCacheKey, maskBlobUrl);

      // 释放离屏资源
      canvasNode.width = 0;
      canvasNode.height = 0;
    }

    const clipImg = await FabricImage.fromURL(maskBlobUrl, {
      crossOrigin: 'anonymous'
    });

    clipImg.set({
      left: 0,
      top: 0,
      originX: 'left',
      originY: 'top',
      scaleX: canvasWidth / clipImg.width,
      scaleY: canvasHeight / clipImg.height,
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false
    });
    clipImg.absolutePositioned = true;

    clearFabricCanvasClipPath(fabricCanvas);
    fabricCanvas.clipPath = clipImg;
    fabricCanvas.renderAll();
  } catch (error) {
    console.error('applySkuTemplateMaskClipPath: 处理失败', error);
    throw error;
  }
}

/**
 * 将 dataURL 铺满 fabricCanvas（与 replaceCanvasWithImage 同缩放口径）
 * @param {fabric.StaticCanvas|fabric.Canvas} fabricCanvas
 * @param {string} dataURL
 * @returns {Promise<void>}
 */
async function replaceFabricCanvasWithDataURLImage(fabricCanvas, dataURL) {
  if (!fabricCanvas || !dataURL) {
    return;
  }
  const canvasW = fabricCanvas.width;
  const canvasH = fabricCanvas.height;
  const img = await FabricImage.fromURL(dataURL, {
    crossOrigin: 'anonymous'
  });
  clearFabricCanvasClipPath(fabricCanvas);
  fabricCanvas.clear();
  img.set({
    left: 0,
    top: 0,
    originX: 'left',
    originY: 'top',
    selectable: false,
    evented: false,
    hasControls: false,
    hasBorders: false
  });
  if (img.width && img.height) {
    img.scaleX = canvasW / img.width;
    img.scaleY = canvasH / img.height;
  }
  fabricCanvas.add(img);
  fabricCanvas.renderAll();
}

/**
 * 对已导出的 PNG dataURL 按画布逻辑尺寸铺回离屏 Fabric，再套用与编辑页一致的 sku mask 裁剪（applySkuTemplateMaskClipPath），重新导出。
 * 无 maskUrl 或 excuteMaskClip 为 false 时原样返回 dataURL。
 *
 * @param {Object} options
 * @param {string} options.dataURL
 * @param {Object} options.canvasInfo - displayWidth / displayHeight
 * @param {string} [options.maskUrl]
 * @param {Array} [options.layers] - 解析 mask 的兜底（通常与模板层一致）
 * @param {string} [options.bgColor]
 * @param {number} [options.highResolutionMultiplier=1]
 * @param {boolean} [options.excuteMaskClip=true]
 * @returns {Promise<string>}
 */
export async function applySkuMaskClipToDataURL(options) {
  const opts = options || {};
  const dataURL = opts.dataURL;
  const canvasInfo = opts.canvasInfo;
  const maskUrl =
    opts.maskUrl && typeof opts.maskUrl === 'string' ? opts.maskUrl : '';
  const layers = opts.layers;
  const highResolutionMultiplier =
    typeof opts.highResolutionMultiplier === 'number'
      ? opts.highResolutionMultiplier
      : 1;
  const excuteMaskClip = opts.excuteMaskClip !== false;

  if (!excuteMaskClip || !maskUrl) {
    return dataURL || '';
  }
  if (!dataURL || !canvasInfo) {
    return dataURL || '';
  }
  if (
    typeof canvasInfo.displayWidth !== 'number' ||
    typeof canvasInfo.displayHeight !== 'number'
  ) {
    return dataURL;
  }

  const fabricCanvas = createOffscreenStaticCanvas({
    width: canvasInfo.displayWidth,
    height: canvasInfo.displayHeight
  });
  try {
    await replaceFabricCanvasWithDataURLImage(fabricCanvas, dataURL);
    await applySkuTemplateMaskClipPath({
      fabricCanvas: fabricCanvas,
      layers: Array.isArray(layers) ? layers : [],
      maskUrl: maskUrl,
      bgColor: opts.bgColor
    });
    const out = fabricCanvas.toDataURL({
      format: 'png',
      quality: 1,
      multiplier: highResolutionMultiplier
    });
    return out;
  } finally {
    try {
      // 只释放本函数内部创建的裁剪离屏画布。
      if (fabricCanvas && typeof fabricCanvas.dispose === 'function') {
        fabricCanvas.dispose();
      }
    } catch (error) {
      console.error('applySkuMaskClipToDataURL: fabricCanvas 释放失败', error);
    }
  }
}

/**
 * 使用 globalCompositeOperation 对导出的 fabricCanvas 图片进行反向蒙版裁剪
 *
 * 适用场景：
 * - 需要“扁平化”导出一张已裁剪后的 PNG（不依赖 Fabric 的 clipPath 实时裁剪）
 * - 直接使用 maskUrl 的 alpha 通道作为裁剪蒙版
 *
 * 规则：
 * - maskUrl 透明区域：保留 fabricCanvas 对应位置的内容
 * - maskUrl 不透明区域：擦除 fabricCanvas 对应位置的内容
 *
 * @param {Object} options - 配置选项
 * @param {fabric.Canvas} options.fabricCanvas - Fabric Canvas 对象
 * @param {string} options.maskUrl - 蒙版图片 URL
 * @returns {Promise<string>} 返回裁剪后的 PNG DataURL（无 maskUrl 时直接返回导出结果）
 *
 * @example
 * const outPng = await applySkuTemplateMaskByComposite({
 *   fabricCanvas,
 *   maskUrl: getMaskUrl()
 * });
 */
export async function applySkuTemplateMaskByComposite(options) {
  const { fabricCanvas, maskUrl } = options || {};

  if (!fabricCanvas) {
    throw new Error(
      'applySkuTemplateMaskByComposite: fabricCanvas 参数不能为空'
    );
  }

  // 先导出 Fabric canvas
  const exportDataUrl = fabricCanvas.toDataURL({
    format: 'png',
    quality: 1
  });

  // 无 maskUrl：直接返回导出结果
  if (!maskUrl || typeof maskUrl !== 'string') {
    return exportDataUrl;
  }

  try {
    // 1) 加载导出的 Fabric 图片（图片 A）
    const srcImg = await canvasUtil.loadImage(exportDataUrl);
    const srcW =
      (srcImg && srcImg.naturalWidth) || (srcImg && srcImg.width) || 0;
    const srcH =
      (srcImg && srcImg.naturalHeight) || (srcImg && srcImg.height) || 0;
    if (!srcW || !srcH) {
      throw new Error('applySkuTemplateMaskByComposite: 导出图片尺寸获取失败');
    }

    // 2) 加载 maskUrl
    const maskImg = await canvasUtil.loadImage(maskUrl);

    canvasUtil.checkMaskProportion(
      maskImg.width,
      maskImg.height,
      srcW,
      srcH,
      'applySkuTemplateMaskByComposite'
    );

    // 3) 创建输出 canvas 并使用 destination-out 合成
    const outCanvas = document.createElement('canvas');
    outCanvas.width = srcW;
    outCanvas.height = srcH;
    const outCtx = outCanvas.getContext('2d');
    if (!outCtx) {
      throw new Error('applySkuTemplateMaskByComposite: outCanvas 初始化失败');
    }

    // 先绘制图片 A
    outCtx.clearRect(0, 0, srcW, srcH);
    outCtx.drawImage(srcImg, 0, 0, srcW, srcH);

    // 使用 destination-out：maskUrl 不透明区域会擦除图片 A
    outCtx.globalCompositeOperation = 'destination-out';
    outCtx.drawImage(maskImg, 0, 0, srcW, srcH);

    // 恢复默认合成模式
    outCtx.globalCompositeOperation = 'source-over';

    const outDataUrl = outCanvas.toDataURL('image/png');

    // 释放离屏资源
    outCanvas.width = 0;
    outCanvas.height = 0;

    return outDataUrl;
  } catch (error) {
    console.error('applySkuTemplateMaskByComposite: 处理失败', error);
    throw error;
  }
}

/**
 * 从 Fabric 对象提取 layerInfo
 * @param {fabric.Object} fabricObj - Fabric 对象
 * @returns {Object} layerInfo 对象
 */
export function extractLayerInfoFromFabricObject(fabricObj) {
  if (!fabricObj) {
    throw new Error('extractLayerInfoFromFabricObject: fabricObj 参数不能为空');
  }

  // layerInfo.width/height 就是图片原始宽高（fabricObj.width/height）
  // 直接提取 Fabric 对象的属性
  const layerInfo = {
    left: fabricObj.left || 0,
    top: fabricObj.top || 0,
    width: fabricObj.width || 0,
    height: fabricObj.height || 0,
    scaleX: fabricObj.scaleX || 1,
    scaleY: fabricObj.scaleY || 1,
    angle: fabricObj.angle || 0,
    originX: fabricObj.originX || 'left',
    originY: fabricObj.originY || 'top'
  };

  return layerInfo;
}

/**
 * 扫描图片四周的透明像素边距（像素级）
 * @param {string} imageUrl - 图片 URL
 * @param {number} imageWidth - 图片宽度
 * @param {number} imageHeight - 图片高度
 * @returns {Promise<{leftWidth: number, rightWidth: number, topWidth: number, bottomWidth: number}>}
 */
export async function scanImageTransparentBorders(
  imageUrl,
  imageWidth,
  imageHeight
) {
  var canvas = document.createElement('canvas');
  var ctx = canvas.getContext('2d');
  canvas.width = imageWidth;
  canvas.height = imageHeight;

  try {
    var img = await new Promise(function (resolve, reject) {
      var image = new Image();
      if (/^http/.test(imageUrl)) {
        image.crossOrigin = 'anonymous';
      }
      image.onload = function () {
        resolve(image);
      };
      image.onerror = function (err) {
        reject(err);
      };
      image.src = imageUrl;
    });

    ctx.drawImage(img, 0, 0, imageWidth, imageHeight);
    var data = ctx.getImageData(0, 0, imageWidth, imageHeight);
    var w = data.width;
    var h = data.height;
    var d = data.data;

    function isColTransparent(col) {
      for (var i = 0; i < h; i++) {
        if (d[(i * w + col) * 4 + 3] !== 0) return false;
      }
      return true;
    }
    function isRowTransparent(row) {
      for (var j = 0; j < w; j++) {
        if (d[(row * w + j) * 4 + 3] !== 0) return false;
      }
      return true;
    }

    var leftWidth = 0;
    while (leftWidth < w && isColTransparent(leftWidth)) leftWidth++;

    var rightWidth = 0;
    while (rightWidth < w && isColTransparent(w - 1 - rightWidth)) rightWidth++;

    var topWidth = 0;
    while (topWidth < h && isRowTransparent(topWidth)) topWidth++;

    var bottomWidth = 0;
    while (bottomWidth < h && isRowTransparent(h - 1 - bottomWidth))
      bottomWidth++;

    return {
      leftWidth: leftWidth,
      rightWidth: rightWidth,
      topWidth: topWidth,
      bottomWidth: bottomWidth
    };
  } finally {
    canvas.width = 0;
    canvas.height = 0;
  }
}

/**
 * 扫描图片的"非透明像素最小包围盒 bbox"（基于图片像素坐标系，左上为原点）
 *
 * 与 scanImageTransparentBorders 相比：直接返回主体在图片内的 {left, top, width, height}，
 * 调用方常用于"导出图片 → 像素扫描定位主体覆盖范围"的兜底检测场景。
 *
 * @param {string} imageUrl - 图片 URL（支持 PNG/base64/blob，跨域图片需服务端开 CORS）
 * @param {number} imageWidth - 图片宽度（像素）
 * @param {number} imageHeight - 图片高度（像素）
 * @returns {Promise<{left:number, top:number, width:number, height:number}>}
 *   全透明时返回 { left: 0, top: 0, width: 0, height: 0 }
 */
export async function scanImageNonTransparentBBox(
  imageUrl,
  imageWidth,
  imageHeight
) {
  var borders = await scanImageTransparentBorders(
    imageUrl,
    imageWidth,
    imageHeight
  );
  var width = imageWidth - borders.leftWidth - borders.rightWidth;
  var height = imageHeight - borders.topWidth - borders.bottomWidth;
  if (width <= 0 || height <= 0) {
    return { left: 0, top: 0, width: 0, height: 0 };
  }
  return {
    left: borders.leftWidth,
    top: borders.topWidth,
    width: width,
    height: height
  };
}

/**
 * 根据图片 URL 创建 layerInfo
 * @param {Object} options - 配置选项
 * @param {string} options.imageUrl - 图片 URL
 * @param {Object} options.canvasInfo - 画布信息对象，包含 displayWidth 和 displayHeight
 * @param {string} [options.type] - 布局类型：ImageLayoutTypeMap.fit | fill | fitRemoveTransparent
 * @returns {Promise<Object>} layerInfo 对象
 */
export async function createLayerInfoFromImageUrl(options) {
  if (!options) {
    throw new Error('createLayerInfoFromImageUrl: options 参数不能为空');
  }

  const {
    imageUrl,
    canvasInfo,
    type = constant.ImageLayoutTypeMap.fit
  } = options;

  // 使用验证函数验证参数
  validateImageUrl(imageUrl, 'createLayerInfoFromImageUrl: imageUrl');
  validateCanvasInfo(canvasInfo, 'createLayerInfoFromImageUrl: canvasInfo');
  validateLayoutType(type, 'createLayerInfoFromImageUrl: type');

  // 获取图片信息
  const imageInfo = await mwx.getImageInfo(imageUrl);

  if (type === constant.ImageLayoutTypeMap.fitRemoveTransparent) {
    // 扫描图片四周透明边距，以有效内容尺寸做 fit 布局，再修正偏移
    const borders = await scanImageTransparentBorders(
      imageUrl,
      imageInfo.width,
      imageInfo.height
    );
    const cropWidth = imageInfo.width - borders.leftWidth - borders.rightWidth;
    const cropHeight =
      imageInfo.height - borders.topWidth - borders.bottomWidth;

    const fitDrawInfo = calcImageInCanvasPosition({
      imageWidth: cropWidth > 0 ? cropWidth : imageInfo.width,
      imageHeight: cropHeight > 0 ? cropHeight : imageInfo.height,
      canvasWidth: canvasInfo.displayWidth,
      canvasHeight: canvasInfo.displayHeight,
      type: constant.ImageLayoutTypeMap.fit
    });

    const scaleX =
      fitDrawInfo.inCanvasImageWidth /
      (cropWidth > 0 ? cropWidth : imageInfo.width);
    const scaleY =
      fitDrawInfo.inCanvasImageHeight /
      (cropHeight > 0 ? cropHeight : imageInfo.height);

    return createLayerInfo({
      left: fitDrawInfo.currStatus.x - borders.leftWidth * scaleX,
      top: fitDrawInfo.currStatus.y - borders.topWidth * scaleY,
      width: imageInfo.width,
      height: imageInfo.height,
      angle: 0,
      scaleX: scaleX,
      scaleY: scaleY
    });
  }

  // 计算图片在画布中的位置
  const drawCanvasInfo = calcImageInCanvasPosition({
    imageWidth: imageInfo.width,
    imageHeight: imageInfo.height,
    canvasWidth: canvasInfo.displayWidth,
    canvasHeight: canvasInfo.displayHeight,
    type: type
  });

  // 创建 layerInfo 对象
  return createLayerInfo({
    left: drawCanvasInfo.currStatus.x,
    top: drawCanvasInfo.currStatus.y,
    width: imageInfo.width,
    height: imageInfo.height,
    angle: 0,
    scaleX: drawCanvasInfo.inCanvasImageWidth / imageInfo.width,
    scaleY: drawCanvasInfo.inCanvasImageHeight / imageInfo.height
  });
}

/**
 * 计算图片"非透明主体矩形"经过对象 transform(left/top/scaleX/scaleY/angle) 后
 * 在所属画布坐标系下的轴对齐包围盒 AABB
 *
 * 约定：对象 originX='left' / originY='top'（与项目现有编辑页一致）
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 具备 left/top/scaleX/scaleY/angle/width/height 字段的对象（fabric 对象或等价的 layerInfo）
 * @param {{leftWidth:number,rightWidth:number,topWidth:number,bottomWidth:number}} params.borders - 由 scanImageTransparentBorders 得到的四周透明边距（基于图片原始像素坐标系）
 * @returns {{left:number, top:number, width:number, height:number}} AABB，单位与 fabricObj.left/top 同坐标系
 */
export function calcSubjectAABB(params) {
  if (!params || !params.fabricObj || !params.borders) {
    throw new Error('calcSubjectAABB: 缺少必要参数 fabricObj / borders');
  }
  var obj = params.fabricObj;
  var borders = params.borders;

  var imgW = typeof obj.width === 'number' ? obj.width : 0;
  var imgH = typeof obj.height === 'number' ? obj.height : 0;
  if (imgW <= 0 || imgH <= 0) {
    throw new Error('calcSubjectAABB: fabricObj.width/height 必须为正数');
  }

  var left = typeof obj.left === 'number' ? obj.left : 0;
  var top = typeof obj.top === 'number' ? obj.top : 0;
  var scaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var scaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;
  var angle = typeof obj.angle === 'number' ? obj.angle : 0;

  var leftWidth = borders.leftWidth || 0;
  var rightWidth = borders.rightWidth || 0;
  var topWidth = borders.topWidth || 0;
  var bottomWidth = borders.bottomWidth || 0;

  var x1 = leftWidth;
  var x2 = imgW - rightWidth;
  var y1 = topWidth;
  var y2 = imgH - bottomWidth;

  var theta = (angle * Math.PI) / 180;
  var cos = Math.cos(theta);
  var sin = Math.sin(theta);

  function toWorld(lx, ly) {
    var sx = lx * scaleX;
    var sy = ly * scaleY;
    return {
      x: left + sx * cos - sy * sin,
      y: top + sx * sin + sy * cos
    };
  }

  var p1 = toWorld(x1, y1);
  var p2 = toWorld(x2, y1);
  var p3 = toWorld(x2, y2);
  var p4 = toWorld(x1, y2);

  var minX = Math.min(p1.x, p2.x, p3.x, p4.x);
  var maxX = Math.max(p1.x, p2.x, p3.x, p4.x);
  var minY = Math.min(p1.y, p2.y, p3.y, p4.y);
  var maxY = Math.max(p1.y, p2.y, p3.y, p4.y);

  return {
    left: minX,
    top: minY,
    width: maxX - minX,
    height: maxY - minY
  };
}

/**
 * 计算"图片非透明主体矩形的 AABB 至少一边 >= 显示区对应边，且该维度完全覆盖显示区"的修正
 *
 * 修正顺序：
 *   1) 若两维 AABB 都 < display 对应边，则以显示区中心为锚点，按 ratio=max(displayW/AABB.w, displayH/AABB.h) 同比放大 scaleX/scaleY；
 *   2) 基于（可能已缩放后的）AABB，按维度独立做最小平移补偿，让满足 >= 条件的维度完全覆盖 display。
 *
 * 注意：
 *   - scaleX/scaleY 放大时同乘一个 ratio，保持二者原比例关系（兼容 pinch 产生的非均匀）；
 *   - 浮点容差 EPS=0.5，避免亚像素抖动导致反复修正；
 *   - 原对象属性不会被修改，仅返回建议的新 left/top/scaleX/scaleY 和 needUpdate 标志。
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 具备 left/top/scaleX/scaleY/angle/width/height 字段的对象（fabric 对象或等价的 layerInfo）
 * @param {{leftWidth:number,rightWidth:number,topWidth:number,bottomWidth:number}} params.borders - 由 scanImageTransparentBorders 得到的四周透明边距
 * @param {{left:number, top:number, width:number, height:number}} params.displayRect - 显示区矩形（与 fabricObj.left/top 同坐标系）
 * @returns {{needUpdate:boolean, left:number, top:number, scaleX:number, scaleY:number}}
 */
export function calcFitRemoveTransparentCorrection(params) {
  if (!params || !params.fabricObj || !params.borders || !params.displayRect) {
    throw new Error(
      'calcFitRemoveTransparentCorrection: 缺少必要参数 fabricObj / borders / displayRect'
    );
  }
  var obj = params.fabricObj;
  var borders = params.borders;
  var display = params.displayRect;
  var EPS = 0.5;

  var origLeft = typeof obj.left === 'number' ? obj.left : 0;
  var origTop = typeof obj.top === 'number' ? obj.top : 0;
  var origScaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var origScaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;

  var newLeft = origLeft;
  var newTop = origTop;
  var newScaleX = origScaleX;
  var newScaleY = origScaleY;

  var aabb = calcSubjectAABB({ fabricObj: obj, borders: borders });

  if (aabb.width <= 0 || aabb.height <= 0) {
    return {
      needUpdate: false,
      left: origLeft,
      top: origTop,
      scaleX: origScaleX,
      scaleY: origScaleY
    };
  }

  if (aabb.width + EPS < display.width && aabb.height + EPS < display.height) {
    var ratioW = display.width / aabb.width;
    var ratioH = display.height / aabb.height;
    var bindingAabbSide = ratioW <= ratioH ? aabb.width : aabb.height;
    var ratio = Math.min(ratioW, ratioH) + EPS / bindingAabbSide;
    if (ratio > 1) {
      // 以「主体 AABB 中心」为锚点同比放大，避免主体被强行拉向显示区中心
      // （pinch / 工具按钮缩放后的体感更自然，最终覆盖语义由下方平移补偿保证）
      var acx = aabb.left + aabb.width / 2;
      var acy = aabb.top + aabb.height / 2;
      newScaleX = origScaleX * ratio;
      newScaleY = origScaleY * ratio;
      newLeft = acx - (acx - origLeft) * ratio;
      newTop = acy - (acy - origTop) * ratio;
      aabb = calcSubjectAABB({
        fabricObj: {
          left: newLeft,
          top: newTop,
          scaleX: newScaleX,
          scaleY: newScaleY,
          angle: obj.angle,
          width: obj.width,
          height: obj.height
        },
        borders: borders
      });
    }
  }

  var dx = 0;
  var dy = 0;

  if (aabb.width + EPS >= display.width) {
    if (aabb.left > display.left) {
      dx = display.left - aabb.left;
    } else if (aabb.left + aabb.width < display.left + display.width) {
      dx = display.left + display.width - (aabb.left + aabb.width);
    }
  }
  if (aabb.height + EPS >= display.height) {
    if (aabb.top > display.top) {
      dy = display.top - aabb.top;
    } else if (aabb.top + aabb.height < display.top + display.height) {
      dy = display.top + display.height - (aabb.top + aabb.height);
    }
  }

  newLeft = newLeft + dx;
  newTop = newTop + dy;

  var needUpdate =
    Math.abs(newLeft - origLeft) > 1e-6 ||
    Math.abs(newTop - origTop) > 1e-6 ||
    Math.abs(newScaleX - origScaleX) > 1e-9 ||
    Math.abs(newScaleY - origScaleY) > 1e-9;

  return {
    needUpdate: needUpdate,
    left: newLeft,
    top: newTop,
    scaleX: newScaleX,
    scaleY: newScaleY
  };
}

/**
 * 基于"已知主体 AABB"计算 fabric 对象的覆盖修正（同比放大 + 平移补偿）
 *
 * 与 calcFitRemoveTransparentCorrection 的区别：
 *   - calcFitRemoveTransparentCorrection 内部用 borders + transform 推导 AABB（适合无旋转或矩形主体）
 *   - 本函数 AABB 由调用方提供（例如像素扫描得到的真实主体 bbox），适合有旋转 + 异形主体的兜底场景
 *
 * 算法（与 calcFitRemoveTransparentCorrection 一致）：
 *   1) 若 aabb 两维都 < display 对应边，则以「主体 AABB 中心」为锚点，按 ratio = min(displayW/aabbW, displayH/aabbH) + EPS/binding 同比放大 scaleX/scaleY；
 *   2) 按等比推算放大后的 aabb（中心不变、宽高 × ratio），再按维度独立做最小平移补偿，让满足 >= 条件的维度完全覆盖 display。
 *
 * 注意：
 *   - 不修改入参对象，仅返回建议的新 left/top/scaleX/scaleY 与 needUpdate；
 *   - 浮点容差 EPS=0.5，避免亚像素抖动反复修正。
 *
 * @param {Object} params
 * @param {Object} params.fabricObj - 读取 left/top/scaleX/scaleY（与 displayRect 同坐标系）
 * @param {{left:number,top:number,width:number,height:number}} params.aabb - 主体 AABB（与 displayRect 同坐标系）
 * @param {{left:number,top:number,width:number,height:number}} params.displayRect - 显示区矩形
 * @returns {{needUpdate:boolean,left:number,top:number,scaleX:number,scaleY:number}}
 */
export function calcAabbCoverageCorrection(params) {
  if (!params || !params.fabricObj || !params.aabb || !params.displayRect) {
    throw new Error(
      'calcAabbCoverageCorrection: 缺少必要参数 fabricObj / aabb / displayRect'
    );
  }
  var obj = params.fabricObj;
  var aabb = {
    left: params.aabb.left,
    top: params.aabb.top,
    width: params.aabb.width,
    height: params.aabb.height
  };
  var display = params.displayRect;
  var EPS = 0.5;

  var origLeft = typeof obj.left === 'number' ? obj.left : 0;
  var origTop = typeof obj.top === 'number' ? obj.top : 0;
  var origScaleX = typeof obj.scaleX === 'number' ? obj.scaleX : 1;
  var origScaleY = typeof obj.scaleY === 'number' ? obj.scaleY : 1;

  var newLeft = origLeft;
  var newTop = origTop;
  var newScaleX = origScaleX;
  var newScaleY = origScaleY;

  if (aabb.width <= 0 || aabb.height <= 0) {
    return {
      needUpdate: false,
      left: origLeft,
      top: origTop,
      scaleX: origScaleX,
      scaleY: origScaleY
    };
  }

  if (aabb.width + EPS < display.width && aabb.height + EPS < display.height) {
    var ratioW = display.width / aabb.width;
    var ratioH = display.height / aabb.height;
    var bindingAabbSide = ratioW <= ratioH ? aabb.width : aabb.height;
    var ratio = Math.min(ratioW, ratioH) + EPS / bindingAabbSide;
    if (ratio > 1) {
      // 以「主体 AABB 中心」为锚点同比放大，主体视觉上原地放大，
      // 最终覆盖语义由下方平移补偿保证
      var acx = aabb.left + aabb.width / 2;
      var acy = aabb.top + aabb.height / 2;
      newScaleX = origScaleX * ratio;
      newScaleY = origScaleY * ratio;
      newLeft = acx - (acx - origLeft) * ratio;
      newTop = acy - (acy - origTop) * ratio;
      // 等比推算放大后的 aabb：中心不变，宽高 × ratio
      var newAabbW = aabb.width * ratio;
      var newAabbH = aabb.height * ratio;
      aabb = {
        left: acx - newAabbW / 2,
        top: acy - newAabbH / 2,
        width: newAabbW,
        height: newAabbH
      };
    }
  }

  var dx = 0;
  var dy = 0;

  if (aabb.width + EPS >= display.width) {
    if (aabb.left > display.left) {
      dx = display.left - aabb.left;
    } else if (aabb.left + aabb.width < display.left + display.width) {
      dx = display.left + display.width - (aabb.left + aabb.width);
    }
  }
  if (aabb.height + EPS >= display.height) {
    if (aabb.top > display.top) {
      dy = display.top - aabb.top;
    } else if (aabb.top + aabb.height < display.top + display.height) {
      dy = display.top + display.height - (aabb.top + aabb.height);
    }
  }

  newLeft = newLeft + dx;
  newTop = newTop + dy;

  var needUpdate =
    Math.abs(newLeft - origLeft) > 1e-6 ||
    Math.abs(newTop - origTop) > 1e-6 ||
    Math.abs(newScaleX - origScaleX) > 1e-9 ||
    Math.abs(newScaleY - origScaleY) > 1e-9;

  return {
    needUpdate: needUpdate,
    left: newLeft,
    top: newTop,
    scaleX: newScaleX,
    scaleY: newScaleY
  };
}

/**
 * 获取当前的合成图片（目前返回 previewCompositeImage）
 * 后续可以根据功能返回 outlineImage（包含描边）或 accessoryAndOutlineImage（包含配件）
 *
 * @param {Object} layerData - 完整的图层数据
 * @param {Array} layerData.layers - 图层数组
 * @param {Object} layerData.canvas - 画布信息
 * @returns {Promise<Object>} 返回合成图片对象 { imageSrc, fileName, layerInfo }
 */
export async function getCurrentCompositeImage(layerData) {
  // 目前直接返回 previewCompositeImage
  // 后续可以根据配置返回其他合成图片
  return await generatePreviewCompositeImage(layerData);
}

/**
 * 生成预览合成原始图（previewCompositeImage）
 * 合成所有 type=userImage 和 type=material 的图层
 * 不包含 type=skuTemplate、描边、配件
 *
 * @param {Object} layerData - 完整的图层数据
 * @param {Array} layerData.layers - 图层数组
 * @param {Object} layerData.canvas - 画布信息
 * @returns {Promise<Object>} 返回 previewCompositeImage 对象
 *
 * @example
 * const previewCompositeImage = await generatePreviewCompositeImage(layerData);
 * // 返回:
 * // {
 * //   imageSrc: 'data:image/png;base64,...',
 * //   fileName: 'preview_composite_xxx.png',
 * //   layerInfo: { left: 0, top: 0, width: 750, height: 1000, ... }
 * // }
 */
export async function generatePreviewCompositeImage(layerData) {
  if (!layerData || !layerData.layers || !layerData.canvas) {
    throw new Error('generatePreviewCompositeImage: layerData 参数不完整');
  }

  const { layers, canvas } = layerData;

  // 1. 筛选需要合成的图层（userImage + material + skuTemplate ）
  let layerTypeList = [
    constant.layer_userImage,
    constant.layer_skuMaterial,
    constant.layer_template
  ];
  const compositeLayers = layers.filter(
    (layer) =>
      shouldRenderLayerInCommonDesign(layer) &&
      layerTypeList.includes(layer.type)
  );

  // 2. 按 zIndex 排序
  compositeLayers.sort((a, b) => a.zIndex - b.zIndex);

  // 3. 创建临时 canvas
  const tempCanvas = document.createElement('canvas');

  let scale = canvas.productionWidth / canvas.displayWidth;
  tempCanvas.width = canvas.productionWidth;
  tempCanvas.height = canvas.productionHeight;
  const ctx = tempCanvas.getContext('2d');

  try {
    // 4. 逐层绘制（使用已有的 drawLayerToCanvas 方法）
    for (const layer of compositeLayers) {
      try {
        await drawLayerToCanvas({
          ctx,
          layer,
          canvasWidth: canvas.displayWidth,
          canvasHeight: canvas.displayHeight,
          scale
        });
      } catch (error) {
        console.error(
          'generatePreviewCompositeImage: 绘制图层失败',
          layer.id,
          error
        );
        // 继续绘制其他图层
      }
    }

    // 5. 导出为 DataURL
    const imageSrc = tempCanvas.toDataURL('image/png');
    const fileName = `preview_composite_${Date.now()}.png`;

    let imageInfo = await loadImage(imageSrc);
    let layerScale = canvas.displayWidth / imageInfo.naturalWidth;
    // 6. 创建 layerInfo（填满画布）
    const layerInfo = createLayerInfo({
      left: 0,
      top: 0,
      width: imageInfo.naturalWidth,
      height: imageInfo.naturalHeight,
      scaleX: layerScale,
      scaleY: layerScale,
      angle: 0,
      originX: 'left',
      originY: 'top'
    });

    return {
      imageSrc,
      fileName,
      layerInfo
    };
  } finally {
    tempCanvas.width = 0;
    tempCanvas.height = 0;
  }
}

/**
 * 创建默认的背景图层（填满画布）
 * 与旧方案的 getBgInfo 不同，新方案直接填满画布，不做居中处理
 *
 * @param {string} imageUrl - 背景图片 URL（previewCompositeImage.imageSrc）
 * @param {Object} canvasInfo - 画布信息 { displayWidth, displayHeight }
 * @returns {Object} 返回背景图层对象
 *
 * @example
 * const backgroundLayer = createDefaultBackgroundLayer(
 *   previewCompositeImage.imageSrc,
 *   { displayWidth: 750, displayHeight: 1000 }
 * );
 */
export async function createDefaultBackgroundLayer(imageUrl, canvasInfo) {
  if (!imageUrl) {
    throw new Error('createDefaultBackgroundLayer: imageUrl 参数不能为空');
  }

  if (!canvasInfo || !canvasInfo.displayWidth || !canvasInfo.displayHeight) {
    throw new Error('createDefaultBackgroundLayer: canvasInfo 参数不完整');
  }

  let imageInfo = await loadImage(imageUrl);
  let scale = canvasInfo.displayWidth / imageInfo.naturalWidth;

  return createLayer({
    type: constant.layer_background,
    imageUrl: imageUrl,
    layerInfo: createLayerInfo({
      left: 0,
      top: 0,
      width: imageInfo.naturalWidth,
      height: imageInfo.naturalHeight,
      scaleX: scale,
      scaleY: scale,
      angle: 0,
      originX: 'left',
      originY: 'top'
    }),
    opacity: mwx.getBackgroundDefaultOpacity(), // 默认透明度
    backgroundUIState: {
      activeTab: 'gradient',
      selectedBg: null
    },
    canEditImage: false, // 背景图层不可编辑
    canAdjustLayer: false, // 背景图层不可调整层级
    zIndex: constant.backgroundMaxZIndex
  });
}

/**
 * 生成纯色或渐变背景图片
 * 创建一个填充了指定颜色的 canvas 并导出为 PNG DataURL
 *
 * @param {string} background - CSS 颜色值（纯色或渐变）
 * @param {number} width - 画布宽度
 * @param {number} height - 画布高度
 * @returns {string} PNG DataURL
 *
 * @example
 * const bgDataUrl = createSolidColorImage('#ff0000', 750, 1000);
 * const gradientDataUrl = createSolidColorImage('linear-gradient(to bottom, #ff0000, #0000ff)', 750, 1000);
 */
export function createSolidColorImage(background, width, height) {
  if (!background) {
    throw new Error('createSolidColorImage: background 参数不能为空');
  }

  if (!width || !height) {
    throw new Error('createSolidColorImage: width 和 height 参数不能为空');
  }

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = width;
  tempCanvas.height = height;
  const ctx = tempCanvas.getContext('2d');

  try {
    // 处理渐变背景
    if (background.includes('gradient')) {
      // 解析渐变字符串并创建渐变对象
      // 例如: 'linear-gradient(to bottom, #ff0000, #0000ff)'
      const gradientMatch = background.match(/linear-gradient\(([^)]+)\)/);
      if (gradientMatch) {
        const parts = gradientMatch[1].split(',').map((s) => s.trim());
        // 简单处理：to bottom 或默认从上到下
        const gradient = ctx.createLinearGradient(0, 0, 0, height);

        // 提取颜色值
        const colors = parts.filter(
          (p) => p.startsWith('#') || p.startsWith('rgb')
        );
        if (colors.length >= 2) {
          gradient.addColorStop(0, colors[0]);
          gradient.addColorStop(1, colors[1]);
          // 支持多个颜色
          if (colors.length > 2) {
            const step = 1 / (colors.length - 1);
            colors.forEach((color, index) => {
              gradient.addColorStop(index * step, color);
            });
          }
        }

        ctx.fillStyle = gradient;
      } else {
        // 如果解析失败，使用纯色
        ctx.fillStyle = background;
      }
    } else {
      // 纯色背景
      ctx.fillStyle = background;
    }

    ctx.fillRect(0, 0, width, height);

    return tempCanvas.toDataURL('image/png');
  } finally {
    tempCanvas.width = 0;
    tempCanvas.height = 0;
  }
}

export function isDefaultWhiteInk(layer) {
  return constant.isDefaultWhiteInkValue(layer.whiteInk);
}

/**
 * 根据 retouchAction 和 retouchType 获取对应的 userSubType
 * @param {string} retouchAction - 修图动作类型
 * @param {string} retouchType - 修图类型
 * @returns {string} 返回对应的 userSubType 值：'ORIGIN' | 'HEAD' | 'BODY'
 */
function getRetouchType(retouchAction, retouchType) {
  if (retouchAction == constant.tool_noRetouch) {
    return constant.subType_origin;
  } else {
    if (retouchType == constant.tool_headRetouch) {
      return constant.subType_head;
    } else {
      return constant.subType_body;
    }
  }
}

/**
 * 将前端数据结构转换为后端数据结构
 * 前端数据结构：{ canvas: {...}, layers: [...], reverseLayer: [], ... }
 * 后端数据结构：{ canvasInfo: {...}, layers: [...] }
 * @param {Object} frontendData - 前端数据结构
 * @returns {Object} 后端数据结构
 */
export function convertFrontendToBackend(frontendData, template) {
  console.log('convertFrontendToBackend', JSON.stringify(frontendData));

  if (!frontendData || typeof frontendData !== 'object') {
    throw new Error('数据格式有误,请删除后重做');
  }
  const sanitizedFrontendData = removeImageDataRenderRuntimeFields(
    frontendData
  );

  const frontendCanvas = sanitizedFrontendData.canvas || {};
  const filterFrontendLayers = function (layers) {
    return (layers || []).filter((layer) => {
      if (!shouldIncludeLayerInBackendPayload(layer)) {
        return false;
      }
      // 透明网格底图仅用于前端编辑 UI，不应传给后端
      if (
        layer &&
        (layer.type === constant.layer_transparentGrid ||
          (layer.type == constant.layer_template && layer.isClearTemplate))
      ) {
        return false;
      }
      return true;
    });
  };

  const fontendKeyToBackendMap = {
    canAdjustLayer: 'sortable',
    canEditImage: 'editable'
  };

  // 可以直接从 layer 读取透传的字段
  const directLayerKeys = [
    'id',
    'type',
    'zIndex',
    'imageUrl',
    'canvasType',
    'imageData',
    'backUrl',
    'foilUrl',
    'canvasType'
  ];

  /**
   * 将提交给后台的 imageData 统一转换为字符串。
   * @param {*} imageData - 图层上的 imageData 数据。
   * @returns {string|undefined} 可提交给后台的 imageData 字符串。
   */
  const normalizeBackendImageData = function (imageData) {
    return imageData === undefined || imageData === null
      ? undefined
      : typeof imageData === 'string'
      ? imageData
      : JSON.stringify(imageData);
  };

  const convertLayerCollection = function (layers) {
    const frontendLayers = filterFrontendLayers(layers);
    const convertedLayers = frontendLayers.map((layer) => {
      if (!layer || typeof layer !== 'object') {
        return layer;
      }

      const layerInfo = layer.layerInfo || {};
      const directFields = directLayerKeys.reduce(function (acc, key) {
        acc[key] = layer[key];
        return acc;
      }, {});
      let convertedLayer = Object.assign(directFields, {
        imageData: normalizeBackendImageData(layer.imageData),
        canEditImage: layer.canEditImage ? 1 : 0,
        canAdjustLayer: layer.canAdjustLayer ? 1 : 0,
        layerInfo: {
          left: layerInfo.left,
          top: layerInfo.top,
          scale: layerInfo.scaleX,
          angle: layerInfo.angle,
          width: layerInfo.width,
          height: layerInfo.height
        },
        whiteAction: layer.whiteAction || 'DEFAULT',
        // 白墨收缩
        whiteShrink:
          layer.whiteShrink !== undefined
            ? layer.whiteShrink || layer.whiteShrink === undefined
              ? 1
              : 0
            : 1,
        // 逆向层
        frostUrl:
          layer.frostUrl ||
          layer.reversePrimerLayerImageUrl ||
          layer.reversePrimerLayerAction,
        // 局部 UV
        spotUv: layer.spotUv || layer.scopeUv || layer.scopeUvAction,
        strokeWidth: layer.strokeWidth, // 描边宽度
        strokeColor: layer.strokeColor, // 描边颜色
        whiteInk: constant.isClearWhiteInkValue(layer.whiteInk)
          ? constant.WhiteActionMap.EMPTY
          : layer.whiteInk
      });

      switch (layer.type) {
        case constant.layer_background:
          var backgroundOpacity =
            typeof layer.opacity === 'number'
              ? layer.opacity
              : mwx.getBackgroundDefaultOpacity();
          convertedLayer.opacity = backgroundOpacity;
          convertedLayer.opacitySlider =
            backgroundOpacity / mwx.getBackgroundDefaultOpacity();
          convertedLayer.layerExtra = null;
          break;
        case constant.layer_template:
          if (layer.layerExtra) {
            convertedLayer.layerExtra = layer.layerExtra;
          } else {
            convertedLayer.layerExtra = null;
          }
          break;
        case constant.layer_userImage:
          var originImage = {
            ...(layer.originImageInfo || {})
          };
          delete originImage.originImageFileName;
          convertedLayer.layerExtra = {
            ...convertedLayer.layerExtra,
            originImage: originImage,
            userSubType:
              getRetouchType(layer.retouchAction, layer.retouchType) || 'BODY'
          };
          break;
        default:
          convertedLayer.layerExtra = null;
          break;
      }
      Object.keys(fontendKeyToBackendMap).forEach((key) => {
        if (convertedLayer[key]) {
          convertedLayer[fontendKeyToBackendMap[key]] = convertedLayer[key];
          delete convertedLayer[key];
        }
      });
      // 默认白墨不传给后台，由后台按缺省有白墨处理。
      if (constant.isDefaultWhiteInkValue(convertedLayer.whiteInk)) {
        delete convertedLayer.whiteInk;
      }

      return convertedLayer;
    });

    let backgroundConvertedLayer = null;
    const backendLayers = [];
    convertedLayers.forEach((convertedLayer) => {
      if (convertedLayer && convertedLayer.type === constant.layer_background) {
        backgroundConvertedLayer = convertedLayer;
      } else {
        backendLayers.push(convertedLayer);
      }
    });

    let background = null;
    if (
      backgroundConvertedLayer &&
      (backgroundConvertedLayer.imageUrl || '').indexOf('blob') == -1
    ) {
      const bgLayerInfo = backgroundConvertedLayer.layerInfo || {};
      background = {
        canEditImage: backgroundConvertedLayer.canEditImage,
        canAdjustLayer: backgroundConvertedLayer.canAdjustLayer,
        imageUrl: backgroundConvertedLayer.imageUrl,
        opacity: backgroundConvertedLayer.opacity,
        opacitySlider: backgroundConvertedLayer.opacitySlider,
        layerInfo: bgLayerInfo
      };
    }
    if (backgroundConvertedLayer && backgroundConvertedLayer.imageData) {
      // 背景 有可编辑文字 也需要保存imageData
      background.imageData = backgroundConvertedLayer.imageData;
    }

    return {
      background: background,
      layers: sortLayersByZIndex(backendLayers)
    };
  };

  const frontCollection = convertLayerCollection(sanitizedFrontendData.layers);
  const reverseCollection = convertLayerCollection(
    sanitizedFrontendData.reverseLayer
  );
  let isReverseLayerEmpty =
    sanitizedFrontendData.reverseLayer &&
    sanitizedFrontendData.reverseLayer.filter(
      (v) => v.type == constant.layer_userImage
    ).length == 0;

  // 正反不同图
  if (template.isFrontBackLayer) {
    const reverseUserImageLayer =
      reverseCollection && reverseCollection.layers
        ? reverseCollection.layers.find((layer) => {
            return layer && layer.type == constant.layer_userImage;
          })
        : null;
    let backImg =
      reverseUserImageLayer && reverseUserImageLayer.imageUrl
        ? reverseUserImageLayer.imageUrl
        : sanitizedFrontendData.reverseLayerIsMirror
        ? ''
        : 'empty';

    // 不是正反不同图, backImg不能为'empty',改为null
    if (backImg === 'empty') {
      let template = mwx.Store.get(mwx.constant.skuTemplate);
      if (!template.isFrontBackLayer) {
        backImg = null;
      }
    }

    //  先默认只有一张反面图
    frontCollection.layers.some((v) => {
      if (v.type == constant.layer_userImage && backImg !== null) {
        v.backImg = backImg;
        return true;
      }
    });
  }

  return {
    front: {
      displayWidth: frontendCanvas.displayWidth || 0,
      displayHeight: frontendCanvas.displayHeight || 0,
      background: frontCollection.background,
      layers: frontCollection.layers
    },
    back: {
      displayWidth: frontendCanvas.displayWidth || 0,
      displayHeight: frontendCanvas.displayHeight || 0,
      background: reverseCollection?.background,
      layers: reverseCollection?.layers
    }
  };
}

/**
 * 判断值是否为有效数字。
 * @param {*} value - 待判断的值
 * @returns {boolean} 是否为有效数字
 */
function isValidBackendNumber(value) {
  return typeof value === 'number' && isFinite(value);
}

/**
 * 将后端图层的缩放字段还原为前端 scaleX/scaleY。
 * @param {Object} layerInfo - 后端 layerInfo
 * @returns {{scaleX:number,scaleY:number}} 前端缩放字段
 */
function getFrontendLayerScale(layerInfo) {
  var scale =
    isValidBackendNumber(layerInfo && layerInfo.scale) && layerInfo.scale > 0
      ? layerInfo.scale
      : 1;
  return {
    scaleX:
      isValidBackendNumber(layerInfo && layerInfo.scaleX) &&
      layerInfo.scaleX > 0
        ? layerInfo.scaleX
        : scale,
    scaleY:
      isValidBackendNumber(layerInfo && layerInfo.scaleY) &&
      layerInfo.scaleY > 0
        ? layerInfo.scaleY
        : scale
  };
}

/**
 * 将后端 layerInfo 还原为前端图层位置信息。
 * @param {Object} layerInfo - 后端 layerInfo
 * @returns {Object} 前端 layerInfo
 */
function normalizeBackendLayerInfo(layerInfo) {
  var info = layerInfo || {};
  var scale = getFrontendLayerScale(info);
  return {
    left: isValidBackendNumber(info.left) ? info.left : 0,
    top: isValidBackendNumber(info.top) ? info.top : 0,
    width: isValidBackendNumber(info.width) ? info.width : 0,
    height: isValidBackendNumber(info.height) ? info.height : 0,
    scaleX: scale.scaleX,
    scaleY: scale.scaleY,
    angle: isValidBackendNumber(info.angle) ? info.angle : 0,
    originX: info.originX || 'left',
    originY: info.originY || 'top'
  };
}

/**
 * 将后端 0/1 字段还原为前端布尔字段。
 * @param {Object} layer - 后端图层
 * @param {string} backendKey - 后端字段名
 * @param {string} frontendKey - 前端字段名
 * @param {boolean} defaultValue - 默认值
 * @returns {boolean} 前端布尔值
 */
function normalizeBackendEditableValue(
  layer,
  backendKey,
  frontendKey,
  defaultValue
) {
  // 前端字段已存在时优先保留。
  if (typeof layer[frontendKey] === 'boolean') {
    return layer[frontendKey];
  }
  // 后端 0/1 字段存在时按后端字段还原。
  if (layer[backendKey] !== undefined && layer[backendKey] !== null) {
    return !!layer[backendKey];
  }
  return defaultValue;
}

/**
 * 尝试把后端 imageData 字符串还原为对象。
 * @param {*} imageData - 后端 imageData
 * @returns {*} 前端可消费的 imageData
 */
function normalizeBackendImageData(imageData) {
  // 空值保持空，避免额外制造 imageData。
  if (imageData === undefined || imageData === null || imageData === '') {
    return imageData;
  }
  // 非字符串说明已经是前端结构。
  if (typeof imageData !== 'string') {
    return imageData;
  }
  try {
    return JSON.parse(imageData);
  } catch (error) {
    return imageData;
  }
}

/**
 * 根据后端 userSubType 还原前端修图状态。
 * @param {string} userSubType - 后端 userSubType
 * @returns {{retouchAction:string,retouchType:string,excuteRetouch:boolean}} 修图状态
 */
function getFrontendRetouchConfig(userSubType) {
  // 原图类型按“不抠图”恢复。
  if (userSubType === constant.subType_origin) {
    return {
      retouchAction: constant.tool_noRetouch,
      retouchType: constant.tool_noRetouch,
      excuteRetouch: false
    };
  }
  // 头像类型按头部抠图恢复。
  if (userSubType === constant.subType_head) {
    return {
      retouchAction: constant.tool_retouch,
      retouchType: constant.tool_headRetouch,
      excuteRetouch: true
    };
  }
  return {
    retouchAction: constant.tool_retouch,
    retouchType: constant.tool_fullBodyRetouch,
    excuteRetouch: true
  };
}

/**
 * 从后端原图信息中取出可用图片地址。
 * @param {Object} originImage - 后端 layerExtra.originImage
 * @returns {string} 图片地址
 */
function getOriginImageUrl(originImage) {
  return (originImage && (originImage.imageUrl || originImage.url)) || '';
}

/**
 * 从图片 URL 中提取后端图片 fileName。
 * @param {string} imageUrl - 图片地址
 * @returns {string} 去掉扩展名后的后端 fileName，无法解析时返回空字符串
 */
function getImageFileNameFromUrl(imageUrl) {
  // 图片地址为空或非字符串时无法从 URL 解析 fileName。
  if (!imageUrl || typeof imageUrl !== 'string') {
    return '';
  }
  var urlWithoutQuery = imageUrl.split('?')[0].split('#')[0];
  var pathSegments = urlWithoutQuery.split('/');
  var fullFilename = pathSegments[pathSegments.length - 1] || '';
  // 路径末段为空时无法从 URL 解析 fileName。
  if (!fullFilename) {
    return '';
  }
  try {
    fullFilename = decodeURIComponent(fullFilename);
  } catch (error) {}
  var dotIndex = fullFilename.lastIndexOf('.');
  // 存在扩展名时，后端 fileName 使用不含扩展名的文件名。
  if (dotIndex > 0) {
    return fullFilename.slice(0, dotIndex);
  }
  return fullFilename;
}

/**
 * 将后端 originImage 还原为前端 originImageInfo。
 * @param {Object} originImage - 后端 layerExtra.originImage
 * @returns {Object|null} 前端 originImageInfo
 */
function normalizeBackendOriginImageInfo(originImage) {
  var imageUrl = getOriginImageUrl(originImage);
  // 二次编辑 userImage 必须从 layerExtra.originImage 还原原图，不能用当前图兜底。
  if (!imageUrl) {
    throw new Error('二次编辑 userImage 原图数据缺失');
  }
  var imageWidth =
    (originImage &&
      (originImage.imageWidth ||
        originImage.width ||
        originImage.naturalWidth)) ||
    0;
  var imageHeight =
    (originImage &&
      (originImage.imageHeight ||
        originImage.height ||
        originImage.naturalHeight)) ||
    0;
  return {
    ...(originImage || {}),
    imageUrl: imageUrl,
    imageWidth: imageWidth,
    imageHeight: imageHeight,
    originImageWidth:
      (originImage && originImage.originImageWidth) || imageWidth,
    originImageHeight:
      (originImage && originImage.originImageHeight) || imageHeight,
    originImageFileName: ''
  };
}

/**
 * 根据原图信息构造前端 imageStatus.uploadRes。
 * @param {Object|null} originImageInfo - 前端 originImageInfo
 * @returns {Object|null} 上传结果
 */
function buildBackendUploadRes(originImageInfo) {
  var imageUrl = getOriginImageUrl(originImageInfo);
  // uploadRes 必须指向明确原图，缺失时阻断二次编辑链路。
  if (!imageUrl) {
    throw new Error('二次编辑 userImage 上传原图数据缺失');
  }
  return {
    url: imageUrl,
    fileName: '',
    naturalWidth:
      (originImageInfo &&
        (originImageInfo.originImageWidth || originImageInfo.imageWidth)) ||
      0,
    naturalHeight:
      (originImageInfo &&
        (originImageInfo.originImageHeight || originImageInfo.imageHeight)) ||
      0
  };
}

/**
 * 将后端白墨字段还原为前端字段。
 * @param {Object} layer - 后端图层
 * @returns {{whiteAction:string,whiteInk:*}} 白墨字段
 */
function normalizeBackendWhiteInk(layer) {
  var whiteAction = layer.whiteAction || '';
  var whiteInk = layer.whiteInk;
  // 后台历史 -1 清空白墨值统一还原为当前清空白墨值。
  if (constant.isClearWhiteInkValue(whiteInk)) {
    whiteInk = emptyWhiteInk;
  }
  // 后端 EMPTY 动作对应前端清空白墨占位值。
  if (whiteAction === constant.WhiteActionMap.EMPTY) {
    whiteInk = emptyWhiteInk;
  }
  // 后端 DEFAULT 与前端空动作等价。
  if (whiteAction === constant.WhiteActionMap.DEFAULT) {
    whiteAction = '';
  }
  return {
    whiteAction: whiteAction,
    whiteInk: whiteInk
  };
}

/**
 * 判断值是否为逆向层动作枚举。
 * @param {string} value - 待判断字段值
 * @returns {boolean} 是否为逆向层动作
 */
function isReversePrimerLayerAction(value) {
  return Object.keys(constant.ReversePrimerLayerActionMap).some(function (key) {
    return constant.ReversePrimerLayerActionMap[key] === value;
  });
}

/**
 * 判断值是否为局部 UV 动作枚举。
 * @param {string} value - 待判断字段值
 * @returns {boolean} 是否为局部 UV 动作
 */
function isScopeUvAction(value) {
  return Object.keys(constant.ScopeUvActionMap).some(function (key) {
    return constant.ScopeUvActionMap[key] === value;
  });
}

/**
 * 从后端 frostUrl 中恢复逆向层前端字段。
 * @param {Object} layer - 后端图层
 * @returns {{reversePrimerLayerImageUrl:string,reversePrimerLayerAction:string}} 逆向层前端字段
 */
function normalizeBackendReversePrimerLayer(layer) {
  var frostUrl = layer && layer.frostUrl;
  var isLegacyAction = isReversePrimerLayerAction(frostUrl);
  return {
    reversePrimerLayerImageUrl: isLegacyAction ? '' : frostUrl || '',
    reversePrimerLayerAction: isLegacyAction
      ? frostUrl
      : frostUrl
      ? constant.ReversePrimerLayerActionMap.CUSTOMIZE
      : constant.ReversePrimerLayerActionMap.AUTO
  };
}

/**
 * 从后端 spotUv 中恢复局部 UV 前端字段。
 * @param {Object} layer - 后端图层
 * @returns {{scopeUv:string,scopeUvAction:string}} 局部 UV 前端字段
 */
function normalizeBackendScopeUv(layer) {
  var spotUv = layer && layer.spotUv;
  var isLegacyAction = isScopeUvAction(spotUv);
  return {
    scopeUv: isLegacyAction ? '' : spotUv || '',
    scopeUvAction: isLegacyAction
      ? spotUv
      : spotUv
      ? constant.ScopeUvActionMap.CUSTOMIZE
      : constant.ScopeUvActionMap.EMPTY
  };
}

/**
 * 根据 originImageInfo 生成上传刀线源图尺寸 key。
 * @param {Object|null} originImageInfo - 原图信息
 * @returns {string} 尺寸 key，格式为 widthxheight
 */
function getUploadOutlineSizeKeyFromOriginImageInfo(originImageInfo) {
  // 原图尺寸不完整时，无法恢复上传刀线历史尺寸 key。
  if (
    !originImageInfo ||
    !originImageInfo.originImageWidth ||
    !originImageInfo.originImageHeight
  ) {
    return '';
  }
  return (
    originImageInfo.originImageWidth + 'x' + originImageInfo.originImageHeight
  );
}

/**
 * 根据后端数据推断形状面板选中 key。
 * @param {Object} options - 推断参数
 * @param {Object} options.data - 后端照片项
 * @param {Object} options.userImageLayer - 当前 userImage 图层
 * @returns {string} 形状面板选中 key
 */
function inferSelectedTemplateKeyFromBackend(options) {
  var opts = options || {};
  var data = opts.data || {};
  var extra = data.extra || {};
  var userImageLayer = opts.userImageLayer || {};
  // 自定义刀线由新增的永久源图字段识别。
  if (extra.originMask) {
    return constant.tool_uploadOutline;
  }
  // 系统形状选中态需要等待形状列表加载后，用 extra.mask 匹配恢复。
  // 未抠图按默认状态恢复。
  if (userImageLayer.retouchAction === constant.tool_noRetouch) {
    return constant.useDefaultRetouch;
  }
  // 大头抠图按大头工具恢复选中态。
  if (userImageLayer.retouchType === constant.tool_headRetouch) {
    return constant.tool_headRetouch;
  }
  // 全身抠图按全身工具恢复选中态。
  if (userImageLayer.retouchType === constant.tool_fullBodyRetouch) {
    return constant.tool_fullBodyRetouch;
  }
  return constant.useDefaultRetouch;
}

/**
 * 将后端单个图层还原为 edit.vue 使用的前端图层。
 * @param {Object} layer - 后端图层
 * @returns {Object|null} 前端图层
 */
function normalizeBackendLayer(layer) {
  // 空图层不参与还原。
  if (!layer || typeof layer !== 'object') {
    return null;
  }
  var type = layer.type || constant.layer_template;
  var whiteInkInfo = normalizeBackendWhiteInk(layer);
  var frontendLayer = {
    ...layer,
    id: layer.id || generateLayerId(),
    type: type,
    zIndex: isValidBackendNumber(layer.zIndex) ? layer.zIndex : 0,
    imageData: normalizeBackendImageData(layer.imageData),
    layerInfo: normalizeBackendLayerInfo(layer.layerInfo),
    canEditImage: normalizeBackendEditableValue(
      layer,
      'editable',
      'canEditImage',
      type === constant.layer_userImage
    ),
    canAdjustLayer: normalizeBackendEditableValue(
      layer,
      'sortable',
      'canAdjustLayer',
      type !== constant.layer_background
    ),
    whiteAction: whiteInkInfo.whiteAction,
    whiteShrink:
      layer.whiteShrink === undefined || layer.whiteShrink === null
        ? true
        : !!layer.whiteShrink,
    whiteInk: whiteInkInfo.whiteInk,
    strokeWidth: layer.strokeWidth,
    strokeColor: layer.strokeColor,
    frostUrl: layer.frostUrl,
    spotUv: layer.spotUv,
    foilUrl: layer.foilUrl,
    canvasType: layer.canvasType
  };
  delete frontendLayer.editable;
  delete frontendLayer.sortable;

  // 用户图层需要补齐图片状态和修图状态，供编辑页继续换图、修图、保存。
  if (type === constant.layer_userImage) {
    var layerExtra = layer.layerExtra || {};
    var reversePrimerLayerInfo = normalizeBackendReversePrimerLayer(layer);
    var scopeUvInfo = normalizeBackendScopeUv(layer);
    var originImageInfo = normalizeBackendOriginImageInfo(
      layerExtra.originImage
    );
    var uploadRes = buildBackendUploadRes(originImageInfo);
    var retouchConfig = getFrontendRetouchConfig(layerExtra.userSubType);
    var originImageFileName = '';
    var retouchFileName =
      layer.fileName || getImageFileNameFromUrl(layer.imageUrl) || '';
    frontendLayer.originImageInfo = originImageInfo;
    frontendLayer.originImageUrl = getOriginImageUrl(originImageInfo);
    frontendLayer.originImageFileName = originImageFileName;
    frontendLayer.imageStatus = {
      uploadRes: uploadRes,
      afterClipUploadRes: null,
      beforeClipRetouchRes: null,
      retouchRes:
        retouchConfig.excuteRetouch && layer.imageUrl
          ? {
              url: layer.imageUrl,
              fileName: retouchFileName
            }
          : null,
      shapeClipRes: null,
      customOutlineClipOriginImageRes: null
    };
    frontendLayer.retouchAction = retouchConfig.retouchAction;
    frontendLayer.retouchType = retouchConfig.retouchType;
    frontendLayer.excuteRetouch = retouchConfig.excuteRetouch;
    // 后台自定义白墨只有 whiteInk 时，二次编辑白墨页需要用它作为已保存来源图回显。
    if (
      frontendLayer.whiteAction === constant.WhiteActionMap.CUSTOMIZE &&
      frontendLayer.whiteInk &&
      !frontendLayer.whiteInkOriginImageSrc
    ) {
      frontendLayer.whiteInkOriginImageSrc = frontendLayer.whiteInk;
    }
    frontendLayer.reversePrimerLayerImageUrl =
      reversePrimerLayerInfo.reversePrimerLayerImageUrl;
    frontendLayer.reversePrimerLayerAction =
      reversePrimerLayerInfo.reversePrimerLayerAction;
    frontendLayer.scopeUv = scopeUvInfo.scopeUv;
    frontendLayer.scopeUvAction = scopeUvInfo.scopeUvAction;
  }

  return frontendLayer;
}

/**
 * 还原后端背景图层透明度。
 * @param {Object} background - 后端 imageInfo.background
 * @returns {number} 前端背景透明度
 */
function normalizeBackendBackgroundOpacity(background) {
  var defaultOpacity = mwx.getBackgroundDefaultOpacity();
  var opacitySlider = background && background.opacitySlider;
  // 旧后端数据可能把未提交的 opacity 补成 0，此时用 opacitySlider 还原。
  if (
    background &&
    background.opacity === 0 &&
    typeof opacitySlider === 'number' &&
    opacitySlider > 0
  ) {
    return opacitySlider * defaultOpacity;
  }
  // 新数据或真实透明度优先使用后端 opacity。
  if (background && typeof background.opacity === 'number') {
    return background.opacity;
  }
  // 缺少 opacity 时使用 opacitySlider 做兼容兜底。
  if (typeof opacitySlider === 'number') {
    return opacitySlider * defaultOpacity;
  }
  return defaultOpacity;
}

/**
 * 将后端 background 字段还原为前端背景图层。
 * @param {Object} background - 后端 imageInfo.background
 * @returns {Object|null} 前端背景图层
 */
function normalizeBackendBackgroundLayer(background) {
  // 没有背景图时不写入背景图层。
  if (!background || !background.imageUrl) {
    return null;
  }
  var whiteInkInfo = normalizeBackendWhiteInk(background);
  var backgroundLayer = {
    ...background,
    id: background.id || generateLayerId(),
    type: constant.layer_background,
    zIndex: backgroundMaxZIndex,
    imageData: normalizeBackendImageData(background.imageData),
    layerInfo: normalizeBackendLayerInfo(background.layerInfo),
    canEditImage: normalizeBackendEditableValue(
      background,
      'editable',
      'canEditImage',
      false
    ),
    canAdjustLayer: normalizeBackendEditableValue(
      background,
      'sortable',
      'canAdjustLayer',
      false
    ),
    whiteAction: whiteInkInfo.whiteAction,
    whiteInk: whiteInkInfo.whiteInk,
    opacity: normalizeBackendBackgroundOpacity(background),
    opacitySlider:
      background.opacitySlider !== undefined ? background.opacitySlider : 1
  };
  delete backgroundLayer.editable;
  delete backgroundLayer.sortable;
  return backgroundLayer;
}

/**
 * 将后端 imageInfo/duplex 转换成前端 layerData。
 * @param {Object} data - 后端照片项
 * @param {Object} [options] - 转换配置
 * @param {Object} [options.template] - SKU 模板，用于判断是否恢复完整多组件状态
 * @returns {Object|null} 前端 layerData
 */
function buildLayerDataFromBackend(data, options) {
  var opts = options || {};
  var template = opts.template || {};
  var imageInfo = data && data.imageInfo;
  // 后端没有 imageInfo 时无法还原 layerData。
  if (!imageInfo || typeof imageInfo !== 'object') {
    return null;
  }
  var canvas = {
    displayWidth: imageInfo.displayWidth || 0,
    displayHeight: imageInfo.displayHeight || 0
  };
  var layers = (imageInfo.layers || [])
    .map(function (layer) {
      return normalizeBackendLayer(layer);
    })
    .filter(Boolean);
  var backgroundLayer = normalizeBackendBackgroundLayer(imageInfo.background);
  // 背景层在前端统一放回 layers，保证 edit.vue 可按现有链路渲染。
  if (backgroundLayer) {
    layers.push(backgroundLayer);
  }

  var reverseLayer = [];
  // 反向分层 SKU 使用 duplex 协议恢复独立反面图层。
  if (
    template &&
    template.isReverseLayerSku &&
    data.duplex &&
    typeof data.duplex === 'object'
  ) {
    reverseLayer = (data.duplex.layers || [])
      .map(function (layer) {
        return normalizeBackendLayer(layer);
      })
      .filter(Boolean);
    var reverseBackgroundLayer = normalizeBackendBackgroundLayer(
      data.duplex.background
    );
    // 反面背景层也按前端 layerData 结构放回 reverseLayer。
    if (reverseBackgroundLayer) {
      reverseLayer.push(reverseBackgroundLayer);
    }
  }

  var layerData = initLayerData({
    canvas: canvas,
    layers: layers,
    reverseLayer: reverseLayer,
    reverseLayerIsMirror: !reverseLayer.some(function (layer) {
      return layer && layer.type === constant.layer_userImage;
    }),
    strokeWidth: data.border || 0
  });
  layerData.maskUrl = '';
  layerData.maskId = imageInfo.maskId || 0;
  layerData.backendMaskUrl = (data.extra && data.extra.mask) || '';
  layerData.previewUrl =
    data.previewUrl ||
    ((data.imageUrlList || []).length ? data.imageUrlList[0] : '');
  // 旧钥匙扣字段仍写回 keyPoint，兼容非 componentData 链路。
  if (data.acrylic && data.acrylic.holes) {
    layerData.keyPoint = data.acrylic.holes;
  }
  // 旧立牌字段仍写回 socketInfo，兼容非 componentData 链路。
  if (data.acrylic && data.acrylic.slotRect) {
    layerData.socketInfo = {
      slotRect: data.acrylic.slotRect
    };
  }
  // 只有完整多组件 SKU 才从 acrylic 反推 componentData，避免旧钥匙扣等链路被误判。
  if (template.isCustomYakeliSku) {
    var componentData = convertAcrylicToComponentData(data.acrylic, {
      canvas: layerData.canvas
    });
    // 后端 acrylic 含多组件字段时，需要恢复到 componentData。
    if (Object.keys(componentData).length) {
      layerData.componentData = componentData;
    }
  }
  return layerData;
}

/**
 * 将后端图层数据反向转换为 edit.vue 使用的前端结构。
 * @param {Object} data - 后端照片项
 * @param {Object} [options] - 转换配置
 * @param {Object} [options.template] - SKU 模板
 * @returns {Object} 前端照片项增量数据
 */
export function convertBackendToFrontEnd(data, options) {
  data = data || {};
  options = options || {};
  var layerData = buildLayerDataFromBackend(data, {
    template: options.template || {}
  });
  // 没有 imageInfo 时保留旧的浅转换兜底。
  if (!layerData) {
    return {
      previewUrl: data.previewUrl,
      imageInfo: data.imageInfo
    };
  }
  var userImageLayer = (layerData.layers || []).find(function (layer) {
    return layer && layer.type === constant.layer_userImage;
  });
  var originImageSrc =
    (userImageLayer && userImageLayer.originImageUrl) ||
    ((data.imageUrlList || []).length ? data.imageUrlList[0] : '') ||
    data.previewUrl;
  var extra = data.extra || {};
  var frontendExtra = {
    ...extra
  };
  delete frontendExtra.shapeMaskUrl;
  var originMask = extra.originMask || '';
  var originImageInfo = userImageLayer && userImageLayer.originImageInfo;
  return {
    previewUrl: data.previewUrl,
    imageInfo: data.imageInfo,
    originImageSrc: originImageSrc,
    uploadRes:
      userImageLayer && userImageLayer.imageStatus
        ? userImageLayer.imageStatus.uploadRes
        : null,
    status: 'resolve_upload',
    selectedTemplateKey: inferSelectedTemplateKeyFromBackend({
      layerData: layerData,
      data: data,
      userImageLayer: userImageLayer
    }),
    isCustomOutline: !!originMask,
    customOutlineClipOriginImageRes: null,
    uploadOutlineSourceBlobUrl: originMask,
    uploadOutlineSourceSizeKey: getUploadOutlineSizeKeyFromOriginImageInfo(
      originImageInfo
    ),
    extra: frontendExtra,
    shapeTemplateInfo: null,
    layerData: layerData
  };
}

/**
 * 将图片铺满 canvas（等比例时等比例缩放，不等比例时拉伸）
 * @param {Object} options - 参数对象
 * @param {fabric.Canvas} options.fabricCanvas - Fabric canvas 实例
 * @param {string} options.imageUrl - 图片 URL
 * @returns {Promise<void>}
 */
export async function drawImageFillCanvas(options) {
  const { fabricCanvas, imageUrl } = options || {};

  if (!fabricCanvas) {
    throw new Error('drawImageFillCanvas: fabricCanvas 参数不能为空');
  }

  if (!imageUrl) {
    return;
  }

  let timeout = null;
  try {
    // 加载图片，添加超时处理
    const loadPromise = FabricImage.fromURL(imageUrl, {
      crossOrigin: 'anonymous'
    });

    const img = await loadPromise;

    // 获取 canvas 尺寸
    const canvasWidth = fabricCanvas.width;
    const canvasHeight = fabricCanvas.height;

    // 获取图片原始尺寸
    const imageWidth = img.width;
    const imageHeight = img.height;

    // 计算宽高比
    const canvasRatio = canvasWidth / canvasHeight;
    const imageRatio = imageWidth / imageHeight;

    // 判断是否等比例（允许小的误差）
    const ratioDiff = Math.abs(canvasRatio - imageRatio);
    const isEqualRatio = ratioDiff < 0.001;

    let scaleX;
    let scaleY;

    if (isEqualRatio) {
      // 等比例：使用统一的缩放比例
      const scale = canvasWidth / imageWidth;
      scaleX = scale;
      scaleY = scale;
    } else {
      // 不等比例：分别计算 scaleX 和 scaleY，允许变形
      scaleX = canvasWidth / imageWidth;
      scaleY = canvasHeight / imageHeight;
    }

    // 设置图片属性
    img.set({
      left: 0,
      top: 0,
      scaleX: scaleX,
      scaleY: scaleY,
      originX: 'left',
      originY: 'top',
      selectable: false,
      hasControls: false,
      hasBorders: false,
      evented: false,
      lockMovementX: true,
      lockMovementY: true,
      lockRotation: true,
      lockScalingX: true,
      lockScalingY: true
    });

    // 更新对象的交互边界
    img.setCoords();

    // 添加到 canvas 并渲染
    fabricCanvas.add(img);
    fabricCanvas.renderAll();
  } catch (error) {
    console.error('drawImageFillCanvas: 图片加载或绘制失败', error);
    throw error;
  } finally {
    if (timeout) {
      clearTimeout(timeout);
    }
  }
}

// 获取maskUrl
export function getLayerMaskUrl(layerData) {
  let maskUrl = '';
  if (layerData) {
    const skuTemplateLayer = getSkuTemplateLayer(layerData);
    if (
      skuTemplateLayer &&
      typeof skuTemplateLayer.maskUrl === 'string' &&
      skuTemplateLayer.maskUrl
    ) {
      maskUrl = skuTemplateLayer.maskUrl;
    }
  }
  if (!maskUrl) {
    maskUrl = layerData.maskUrl || '';
  }
  return maskUrl;
}

export const EDIT_CACHE_TYPE = 'edit';

export const retouchMap = {
  [constant.tool_fullBodyRetouch]: {
    val: 1,
    text: '抠全身'
  },
  [constant.tool_headRetouch]: {
    val: 5,
    text: '抠大头'
  }
};

export let anotherShapeOptions = [
  {
    shapeName: '默认',
    icon: require('@/assets/image/origin_retouch.png'),
    useDefaultRetouch: true,
    selectedTemplateKey: constant.useDefaultRetouch
  }
];

export let shapeRetouchOptions = [
  {
    shapeName: '抠全身',
    icon: require('@/assets/image/shape_fullbody.png'),
    selectedTemplateKey: constant.tool_fullBodyRetouch,
    toolType: constant.tool_fullBodyRetouch
  },
  {
    shapeName: '抠大头',
    icon: require('@/assets/image/shape_head.png'),
    selectedTemplateKey: constant.tool_headRetouch,
    toolType: constant.tool_headRetouch
  }
];

/** 正反面 layerData 字段：键为正面 canonical，值为该侧真实属性名 */
export const VIEW_LAYER_DATA_FIELDS_FRONT = {
  layers: constant.LayerKeyMap.LAYERS,
  outlineImageSrc: 'outlineImageSrc',
  outlineOriginImageSrc: 'outlineOriginImageSrc',
  outlineOriginImageFileName: 'outlineOriginImageFileName',
  pureOutlineLineImageSrc: 'pureOutlineLineImageSrc',
  pureOutlineLineCropBounds: 'pureOutlineLineCropBounds',
  outlineSourceCanvasInfo: 'outlineSourceCanvasInfo'
};

export const VIEW_LAYER_DATA_FIELDS_REVERSE = {
  layers: constant.LayerKeyMap.REVERSE_LAYER,
  outlineImageSrc: 'reverseOutlineImageSrc',
  outlineOriginImageSrc: 'reverseOutlineOriginImageSrc',
  outlineOriginImageFileName: 'reverseOutlineOriginImageFileName',
  pureOutlineLineImageSrc: 'reversePureOutlineLineImageSrc',
  pureOutlineLineCropBounds: 'reversePureOutlineLineCropBounds',
  outlineSourceCanvasInfo: 'reverseOutlineSourceCanvasInfo'
};

/**
 * 按正/反视图解析 layerData 上的字段名（键名始终以正面为准）。
 * @param {boolean} isReverseView
 * @param {string} [key] - 与 VIEW_LAYER_DATA_FIELDS_FRONT 的键一致
 * @returns {Object|string|undefined} 无 key 时返回浅拷贝映射对象；有 key 时返回对应字符串
 */
export function getViewLayerDataFields(isReverseView, key) {
  const map = isReverseView
    ? VIEW_LAYER_DATA_FIELDS_REVERSE
    : VIEW_LAYER_DATA_FIELDS_FRONT;
  if (key === undefined) {
    return Object.assign({}, map);
  }
  if (map[key] === undefined) {
    console.error(`getViewLayerDataFields: key ${key} not found in map`, map);
    if (process.env.NODE_ENV !== 'production') {
      throw new Error(`getViewLayerDataFields: key ${key} not found in map`);
    }
    return undefined;
  }
  return map[key];
}
